import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const UPLOADS_BASE = API_BASE.startsWith("/") ? "" : API_BASE;

/* ─── Biomarker range helpers ───────────────────────────────────────────────── */
const BIO_OP_SYM = { gt: ">", gte: ">", lt: "<", lte: "<" };
const fmtBioRange = (min, max, unit = "", minOp = "none", maxOp = "none") => {
  const u = unit ? ` ${unit}` : "";
  const parts = [];
  if (min != null) { const s = BIO_OP_SYM[minOp]; parts.push(s ? `${s} ${min}` : `${min}`); }
  if (max != null) { const s = BIO_OP_SYM[maxOp]; parts.push(s ? `${s} ${max}` : `${max}`); }
  return parts.length ? parts.join(" – ") + u : "—";
};

/* ─── Display maps ─────────────────────────────────────────────────────────── */
const GENDER_LABELS = {
  male: "Male", female: "Female",
  non_binary: "Non-binary", prefer_not_to_say: "Prefer not to say",
};
const PRACTICE_LABELS = {
  solo: "Solo Practice", clinic: "Clinic",
  hospital: "Hospital", telehealth_only: "Telehealth Only",
};
const DOC_TYPE_LABELS = {
  medical_license: "Medical License",
  board_certificate: "Board Certificate",
  functional_medicine_cert: "Functional Medicine Certificate",
  malpractice_insurance: "Malpractice Insurance",
  id_proof: "ID Proof",
  cv_resume: "CV / Resume",
  other: "Other",
};
const ACCOUNT_STATUS_MAP = {
  pending:      { label: "Pending Review", color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"   },
  under_review: { label: "Under Review",   color: "#1D4ED8", bg: "rgba(29,78,216,0.1)",   border: "rgba(29,78,216,0.25)"  },
  approved:     { label: "Approved",       color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"  },
  paused:       { label: "Paused",         color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"   },
  suspended:    { label: "Suspended",      color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"  },
  rejected:     { label: "Rejected",       color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"  },
  offboarded:   { label: "Offboarded",     color: "#6B7280", bg: "rgba(107,114,128,0.1)", border: "rgba(107,114,128,0.2)" },
};
const VERIFY_STYLE = {
  verified: { color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"   },
  rejected: { color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"   },
  pending:  { color: "#5A90A8", bg: "rgba(90,144,168,0.08)", border: "rgba(90,144,168,0.2)"   },
  expired:  { color: "#B45309", bg: "rgba(180,83,9,0.08)",   border: "rgba(180,83,9,0.2)"     },
};

/* ─── Shared style helpers ──────────────────────────────────────────────────── */
const card = {
  background: "#FFFFFF",
  border: "1px solid rgba(90,144,168,0.18)",
  borderRadius: 20,
  padding: "26px 28px",
  boxShadow: "0 2px 16px rgba(90,144,168,0.1)",
};
const sectionHead = {
  fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
  textTransform: "uppercase", color: "#5A90A8",
  fontFamily: "Inter, sans-serif", marginBottom: 18,
  paddingBottom: 14, borderBottom: "1px solid rgba(90,144,168,0.15)",
};
const rowLabel = {
  fontSize: 12, color: "#7AAABB",
  fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: "min(130px, 35vw)",
};
const rowValue = {
  fontSize: 13, color: "#1C3545",
  fontFamily: "Inter, sans-serif", textAlign: "right", lineHeight: 1.55,
  wordBreak: "break-word",
};

/* ─── Small reusable components ─────────────────────────────────────────────── */
function Tag({ children }) {
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", margin: "2px 3px 2px 0" }}>
      {children}
    </span>
  );
}

function InfoRow({ label, value, tags }) {
  const empty = tags ? !tags?.length : (value === null || value === undefined || value === "");
  if (empty) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: tags ? "flex-start" : "center", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
      <span style={rowLabel}>{label}</span>
      {tags
        ? <div style={{ textAlign: "right" }}>{tags.map((t, i) => <Tag key={i}>{t}</Tag>)}</div>
        : <span style={rowValue}>{value}</span>
      }
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div style={card}>
      <p style={sectionHead}>{icon && <span style={{ marginRight: 7 }}>{icon}</span>}{title}</p>
      {children}
    </div>
  );
}

/* All configurable cluster fields for Doctor — resolved against fieldSettings at render time */
const DOCTOR_CLUSTER_FIELD_MAP = [
  { key: "cluster_id",               label: "Cluster ID",             get: (c) => c.cluster_id },
  { key: "score_code",               label: "Score Code",             get: (c) => c.score_code },
  { key: "score_full_name",          label: "Score Full Name",        get: (c) => c.score_full_name },
  { key: "user_facing_name",         label: "User-Facing Name",       get: (c) => c.user_facing_name },
  { key: "user_subtitle",            label: "User Subtitle",          get: (c) => c.user_subtitle },
  { key: "tile_name",                label: "Tile Name",              get: (c) => c.tile_name },
  { key: "clinical_scope",           label: "Clinical Scope",         get: (c) => c.clinical_scope,           multiline: true },
  { key: "what_moves_the_score",     label: "What Moves The Score",   get: (c) => c.what_moves_the_score,     multiline: true },
  { key: "specialist_type",          label: "Specialist Type",        get: (c) => c.specialist_type,          multiline: true },
  { key: "user_persona",             label: "User Persona",           get: (c) => c.user_persona,             multiline: true },
  { key: "biomarkers_in_cluster",    label: "Biomarkers",             get: (c) => c.biomarkers_in_cluster != null ? `${c.biomarkers_in_cluster} tracked` : null },
  { key: "composite_formula",        label: "Composite Formula",      get: (c) => c.composite_formula,        multiline: true },
  { key: "objective_weights",        label: "Objective Weights",      get: (c) => c.objective_weights },
  { key: "score_bands",              label: "Score Bands",            get: (c) => c.score_bands,              multiline: true },
  { key: "score_intent",             label: "Score Intent",           get: (c) => c.score_intent },
  { key: "physician_oversight",      label: "Physician Oversight",    get: (c) => c.physician_oversight || null },
  { key: "status",                   label: "Status",                 get: (c) => c.status },
  { key: "subjective_slot",          label: "Subjective Slot",        get: (c) => c.subjective_slot },
  { key: "lab_or_vital_markers",     label: "Lab / Vital Markers",    get: (c) => c.lab_or_vital_markers },
  { key: "required_lab_markers",     label: "Required Lab Markers",   get: (c) => c.required_lab_markers },
];

/* Default fallback settings (used until the API response arrives) */
const DOCTOR_FIELD_DEFAULTS = {
  cluster_id: "L2", score_code: "L1", score_full_name: "L1", user_facing_name: "L2",
  user_subtitle: "L2", tile_name: "hidden", clinical_scope: "L1", what_moves_the_score: "L1",
  specialist_type: "L1", user_persona: "L2", biomarkers_in_cluster: "L1",
  composite_formula: "L2", objective_weights: "L2", score_bands: "L1",
  score_intent: "L1", physician_oversight: "L1", status: "L1",
  subjective_slot: "L2", lab_or_vital_markers: "L1", required_lab_markers: "L1",
};

/* Default biomarker field visibility for Doctor (from image spec) */
const DOCTOR_BIO_FIELD_DEFAULTS = {
  backend_field: "hidden", data_type: "L1", score_direction: "L2",
  scoring_type: "L2", provenance: "hidden", clinical_rationale: "L1",
};

/* ClusterL2Fields — shows L2 fields (Doctor) behind a "Show More" toggle */
function ClusterL2Fields({ cluster, fieldSettings }) {
  const [open, setOpen] = React.useState(false);
  if (!cluster) return null;
  const settings = Object.keys(fieldSettings).length > 0 ? fieldSettings : DOCTOR_FIELD_DEFAULTS;
  const l2 = DOCTOR_CLUSTER_FIELD_MAP
    .filter(({ key }) => settings[key] === "L2")
    .map(({ label, get, multiline }) => ({ label, val: get(cluster), multiline }))
    .filter(({ val }) => val != null && val !== "");
  if (l2.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <button onClick={() => setOpen((p) => !p)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.18)", borderRadius: 8, color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", width: "100%" }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms" }}><polyline points="6 9 12 15 18 9"/></svg>
        {open ? "Show Less" : `Show More Details (${l2.length} fields)`}
      </button>
      {open && (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
          {l2.map(({ label, val, multiline }) => {
            const strVal = String(val);
            const items = multiline && strVal.includes("\n") ? strVal.split("\n").filter(Boolean) : null;
            if (items) {
              return (
                <div key={label} style={{ background: "#FFF", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "12px 14px" }}>
                  <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{label}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {items.map((item) => (
                      <span key={item} style={{ display: "inline-block", padding: "3px 9px", borderRadius: 999, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)", color: "#5A90A8", fontSize: 11, fontFamily: "Inter, sans-serif" }}>{item}</span>
                    ))}
                  </div>
                </div>
              );
            }
            if (multiline) {
              return (
                <div key={label} style={{ background: "#FFF", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 10, padding: "12px 14px" }}>
                  <p style={{ margin: "0 0 6px", fontSize: 9, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{label}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{strVal}</p>
                </div>
              );
            }
            return (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: "#FFF", border: "1px solid rgba(90,144,168,0.12)", borderRadius: 8 }}>
                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: 11, color: "#1C3545", fontFamily: "Inter, sans-serif", fontWeight: 500, textAlign: "right", wordBreak: "break-word" }}>{strVal}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Spinner({ label = "Loading your dashboard…" }) {
  return (
    <div style={{ minHeight: "100vh", background: "#EDF4F7", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <p style={{ color: "#7AAABB", fontSize: 14, fontFamily: "Inter, sans-serif", margin: 0 }}>{label}</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
const CLUSTER_APPROVAL_STATUS = {
  approved: { label: "Approved", color: "#16A34A", bg: "rgba(22,163,74,0.1)",  border: "rgba(22,163,74,0.25)"  },
  pending:  { label: "Pending",  color: "#B45309", bg: "rgba(180,83,9,0.1)",   border: "rgba(180,83,9,0.25)"   },
  rejected: { label: "Rejected", color: "#DC2626", bg: "rgba(220,38,38,0.1)",  border: "rgba(220,38,38,0.25)"  },
};

export default function DoctorDashboard({ onLogout }) {
  const { logout } = useAuth();
  const [doctor,    setDoctor]    = useState(null);
  const [profile,   setProfile]   = useState(null);
  const [documents, setDocuments] = useState([]);
  const [clusters,  setClusters]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [dashView,       setDashView]       = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* ── cluster field visibility settings ── */
  const [clusterFieldSettings, setClusterFieldSettings] = useState({});

  /* ── biomarker field visibility settings ── */
  const [biomarkerFieldSettings, setBiomarkerFieldSettings] = useState({});
  const [expandedBiomarkerId, setExpandedBiomarkerId] = useState(null);
  const [doctorFeatureFlags, setDoctorFeatureFlags] = useState({ biomarker_customization: false, custom_biomarker_proposals: false });

  /* ── clusters view state ── */
  const [customisations,    setCustomisations]    = useState([]);
  const [selClusterId,      setSelClusterId]      = useState(null);
  const [clusterBiomarkers, setClusterBiomarkers] = useState([]);
  const [clusterScoring,    setClusterScoring]    = useState([]);
  const [bioLoading,        setBioLoading]        = useState(false);
  const [activeBio,         setActiveBio]         = useState(null);
  const [customForm,        setCustomForm]        = useState({ action: "adjust_weight", proposed_weight: "", proposed_optimal_min: "", proposed_optimal_max: "", reason: "", supporting_reference: "" });
  const [customSaving,      setCustomSaving]      = useState(false);
  const [customErr,         setCustomErr]         = useState("");

  /* ── custom biomarker proposals state ── */
  const [myCustomBiomarkers,  setMyCustomBiomarkers]  = useState([]);
  const [cbFormOpen,          setCbFormOpen]          = useState(false);
  const [cbForm,              setCbForm]              = useState({ biomarker_name: "", biomarker_code: "", category: "lab", unit: "", reference_range_min: "", reference_range_min_op: "none", reference_range_max: "", reference_range_max_op: "none", optimal_range_min: "", optimal_range_min_op: "none", optimal_range_max: "", optimal_range_max_op: "none", proposed_weight: "", reason: "", supporting_reference: "" });
  const [cbSaving,            setCbSaving]            = useState(false);
  const [cbErr,               setCbErr]               = useState("");

  /* ── assessment questions state ── */
  const [clusterMasterQuestions, setClusterMasterQuestions] = useState([]);
  const [myCustomQuestions,      setMyCustomQuestions]      = useState([]);
  const [qFormOpen,              setQFormOpen]              = useState(false);
  const [qForm,                  setQForm]                  = useState({ question_text: "", question_type: "free_text", options: "", maps_to_score_dimension: "", proposed_weight: "", display_order: "" });
  const [qSaving,                setQSaving]                = useState(false);
  const [qErr,                   setQErr]                   = useState("");

  /* ── practice view state ── */
  const [practiceTab,       setPracticeTab]       = useState("schedule");
  const [availability,      setAvailability]      = useState([]);
  const [capacity,          setCapacity]          = useState(null);
  const [teamMembers,       setTeamMembers]       = useState([]);
  const [practiceLoaded,    setPracticeLoaded]    = useState(false);
  const [practiceSaving,    setPracticeSaving]    = useState(false);
  const [practiceErr,       setPracticeErr]       = useState("");
  const [practiceOk,        setPracticeOk]        = useState("");

  /* availability form */
  const [avForm, setAvForm] = useState({ day_of_week: "monday", start_time: "09:00", end_time: "17:00", timezone: doctor?.timezone || "UTC" });

  /* capacity form */
  const [capForm, setCapForm] = useState({ max_active_patients_total: 30, max_new_patients_per_month: 5, accepting_new_patients: true, lead_time_days: 3, consultation_duration_mins: 45, buffer_between_slots_mins: 15, auto_pause_at_capacity: true });

  /* clinic form (mirrors profile fields) */
  const [clinicForm, setClinicForm]     = useState({ clinic_name: "", clinic_address: "", practice_type: "solo", telehealth_enabled: true, consultation_mode: [] });
  const [clinicDirty, setClinicDirty]   = useState(false);

  /* team form */
  const [tmForm, setTmForm]       = useState({ full_name: "", role: "coordinator", email: "", phone: "", is_primary_contact: false, notes: "" });
  const [showTmForm, setShowTmForm] = useState(false);
  const [editMemberId, setEditMemberId] = useState(null);

  /* ── programs view state ── */
  const [programs,        setPrograms]        = useState([]);
  const [programsLoaded,  setProgramsLoaded]  = useState(false);
  const [progScreen,      setProgScreen]      = useState("list"); // "list" | "builder"
  const [editProg,        setEditProg]        = useState(null);   // program being edited
  const [progInclusions,  setProgInclusions]  = useState([]);
  const [progSaving,      setProgSaving]      = useState(false);
  const [progErr,         setProgErr]         = useState("");
  const [progOk,          setProgOk]          = useState("");
  const [statusFilter,    setStatusFilter]    = useState("all");
  const [inclLoading,     setInclLoading]     = useState(false);

  /* inclusion form */
  const EMPTY_INCL = { inclusion_type: "consultation", inclusion_label: "", description: "", is_included_in_price: true, additional_cost: "", quantity: 1, notes: "" };
  const [inclForm,     setInclForm]     = useState(EMPTY_INCL);
  const [showInclForm, setShowInclForm] = useState(false);

  /* outcome tag input */
  const [outcomeInput, setOutcomeInput] = useState("");

  const EMPTY_PROG_FORM = {
    program_name: "", program_tagline: "", program_description: "",
    clusters_covered: [], duration_weeks: "", total_sessions: "",
    session_frequency: "", max_patients: "", mode: [],
    target_profile: "", expected_outcomes: [],
    pricing_model: "", price_inr: "", price_usd: "",
  };
  const [progForm, setProgForm] = useState(EMPTY_PROG_FORM);

  useEffect(() => {
    const load = async () => {
      const [docRes, profRes, docsRes, clusterRes, custRes, myQRes, cbRes, cfvRes, bfvRes, ffRes] = await Promise.allSettled([
        api.get("/doctor/onboarding/me"),
        api.get("/doctor/profile"),
        api.get("/doctor/documents"),
        api.get("/doctor/clusters/my"),
        api.get("/doctor/clusters/biomarkers/customisations"),
        api.get("/doctor/clusters/questions/my"),
        api.get("/doctor/clusters/biomarkers/custom"),
        api.get("/doctor/clusters/field-settings"),
        api.get("/doctor/clusters/biomarker-field-settings"),
        api.get("/doctor/clusters/feature-flags"),
      ]);
      if (docRes.status     === "fulfilled") setDoctor(docRes.value.data);
      if (profRes.status    === "fulfilled") setProfile(profRes.value.data);
      if (docsRes.status    === "fulfilled") setDocuments(Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
      if (clusterRes.status === "fulfilled") setClusters(Array.isArray(clusterRes.value.data) ? clusterRes.value.data : []);
      if (custRes.status    === "fulfilled") setCustomisations(Array.isArray(custRes.value.data) ? custRes.value.data : []);
      if (myQRes.status     === "fulfilled") setMyCustomQuestions(Array.isArray(myQRes.value.data) ? myQRes.value.data : []);
      if (cbRes.status      === "fulfilled") setMyCustomBiomarkers(Array.isArray(cbRes.value.data) ? cbRes.value.data : []);
      if (cfvRes.status     === "fulfilled" && cfvRes.value.data && typeof cfvRes.value.data === "object") setClusterFieldSettings(cfvRes.value.data);
      if (bfvRes.status     === "fulfilled" && bfvRes.value.data && typeof bfvRes.value.data === "object") setBiomarkerFieldSettings(bfvRes.value.data);
      if (ffRes.status      === "fulfilled" && ffRes.value.data && typeof ffRes.value.data === "object") setDoctorFeatureFlags(ffRes.value.data);
      setLoading(false);
    };
    load();
  }, []);

  /* ── Inbox polling — fetch unread count every 45 s, full inbox on first open ── */
  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await api.get("/doctor/notifications/unread-count");
        setUnreadCount(data.unread_count ?? 0);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 45000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  /* ── Practice data loader (lazy: only when tab first opened) ── */
  const loadPractice = async () => {
    if (practiceLoaded) return;
    const [avRes, capRes, tmRes] = await Promise.allSettled([
      api.get("/doctor/availability"),
      api.get("/doctor/capacity").catch(() => ({ data: null })),
      api.get("/doctor/team"),
    ]);
    if (avRes.status  === "fulfilled") setAvailability(Array.isArray(avRes.value.data)  ? avRes.value.data  : []);
    if (capRes.status === "fulfilled" && capRes.value.data) {
      const c = capRes.value.data;
      setCapacity(c);
      setCapForm({ max_active_patients_total: c.max_active_patients_total, max_new_patients_per_month: c.max_new_patients_per_month, accepting_new_patients: c.accepting_new_patients, lead_time_days: c.lead_time_days, consultation_duration_mins: c.consultation_duration_mins, buffer_between_slots_mins: c.buffer_between_slots_mins, auto_pause_at_capacity: c.auto_pause_at_capacity });
    }
    if (tmRes.status  === "fulfilled") setTeamMembers(Array.isArray(tmRes.value.data)   ? tmRes.value.data  : []);
    setPracticeLoaded(true);
  };

  const openPractice = () => { setDashView("practice"); loadPractice(); };

  const flash = (msg, isErr = false) => { isErr ? setPracticeErr(msg) : setPracticeOk(msg); setTimeout(() => isErr ? setPracticeErr("") : setPracticeOk(""), 3500); };

  /* ── Availability CRUD ── */
  const addSlot = async () => {
    setPracticeSaving(true);
    try {
      const { data } = await api.post("/doctor/availability", { ...avForm, timezone: doctor?.timezone || avForm.timezone });
      setAvailability((p) => [...p, data]);
      flash("Slot added.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to add slot.", true); }
    setPracticeSaving(false);
  };

  const deleteSlot = async (id) => {
    try {
      await api.delete(`/doctor/availability/${id}`);
      setAvailability((p) => p.filter((s) => s.id !== id));
      flash("Slot removed.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to remove slot.", true); }
  };

  /* ── Capacity settings ── */
  const saveCapacity = async () => {
    setPracticeSaving(true);
    try {
      const { data } = await api.patch("/doctor/capacity", capForm);
      setCapacity(data);
      flash("Capacity settings saved.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to save capacity.", true); }
    setPracticeSaving(false);
  };

  /* ── Clinic details ── */
  // Sync clinic form whenever profile data arrives
  useEffect(() => {
    if (profile) {
      setClinicForm({ clinic_name: profile.clinic_name || "", clinic_address: profile.clinic_address || "", practice_type: profile.practice_type || "solo", telehealth_enabled: profile.telehealth_enabled ?? true, consultation_mode: profile.consultation_mode || [] });
      setClinicDirty(false);
    }
  }, [profile]); // eslint-disable-line

  const saveClinic = async () => {
    setPracticeSaving(true);
    try {
      const { data } = await api.patch("/doctor/profile", clinicForm);
      setProfile(data);
      setClinicDirty(false);
      flash("Clinic details saved.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to save clinic details.", true); }
    setPracticeSaving(false);
  };

  const toggleMode = (mode) => {
    const modes = clinicForm.consultation_mode || [];
    setClinicForm((p) => ({ ...p, consultation_mode: modes.includes(mode) ? modes.filter((m) => m !== mode) : [...modes, mode] }));
    setClinicDirty(true);
  };

  /* ── Team CRUD ── */
  const resetTmForm = () => { setTmForm({ full_name: "", role: "coordinator", email: "", phone: "", is_primary_contact: false, notes: "" }); setEditMemberId(null); setShowTmForm(false); };

  const saveMember = async () => {
    setPracticeSaving(true);
    try {
      if (editMemberId) {
        const { data } = await api.patch(`/doctor/team/${editMemberId}`, tmForm);
        setTeamMembers((p) => p.map((m) => m.id === editMemberId ? data : m));
      } else {
        const { data } = await api.post("/doctor/team", tmForm);
        setTeamMembers((p) => [...p, data]);
      }
      resetTmForm();
      flash("Team member saved.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to save team member.", true); }
    setPracticeSaving(false);
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/doctor/team/${id}`);
      setTeamMembers((p) => p.filter((m) => m.id !== id));
      flash("Team member removed.");
    } catch (e) { flash(e?.response?.data?.detail || "Failed to remove.", true); }
  };

  const editMember = (m) => { setTmForm({ full_name: m.full_name, role: m.role, email: m.email || "", phone: m.phone || "", is_primary_contact: m.is_primary_contact, notes: m.notes || "" }); setEditMemberId(m.id); setShowTmForm(true); };

  /* ── Programs helpers ── */
  const progFlash = (msg, isErr = false) => {
    isErr ? setProgErr(msg) : setProgOk(msg);
    setTimeout(() => isErr ? setProgErr("") : setProgOk(""), 3800);
  };

  const loadPrograms = async () => {
    if (programsLoaded) return;
    try {
      const { data } = await api.get("/doctor/programs");
      setPrograms(Array.isArray(data) ? data : []);
    } catch (_) {}
    setProgramsLoaded(true);
  };

  const openPrograms = () => { setDashView("programs"); loadPrograms(); };

  const formFromProg = (p) => ({
    program_name: p.program_name || "",
    program_tagline: p.program_tagline || "",
    program_description: p.program_description || "",
    clusters_covered: p.clusters_covered || [],
    duration_weeks: p.duration_weeks != null ? String(p.duration_weeks) : "",
    total_sessions: p.total_sessions  != null ? String(p.total_sessions)  : "",
    session_frequency: p.session_frequency || "",
    max_patients: p.max_patients != null ? String(p.max_patients) : "",
    mode: p.mode || [],
    target_profile: p.target_profile || "",
    expected_outcomes: p.expected_outcomes || [],
    pricing_model: p.pricing_model || "",
    price_inr: p.price_inr != null ? String(p.price_inr) : "",
    price_usd: p.price_usd != null ? String(p.price_usd) : "",
  });

  const openBuilder = async (prog = null) => {
    setProgErr(""); setProgOk(""); setProgInclusions([]); setShowInclForm(false); setOutcomeInput("");
    if (prog) {
      setEditProg(prog);
      setProgForm(formFromProg(prog));
      setInclLoading(true);
      try {
        const { data } = await api.get(`/doctor/programs/${prog.id}/inclusions`);
        setProgInclusions(Array.isArray(data) ? data : []);
      } catch (_) {}
      setInclLoading(false);
    } else {
      setEditProg(null);
      setProgForm(EMPTY_PROG_FORM);
    }
    setProgScreen("builder");
  };

  const buildPayload = () => {
    const f = progForm;
    return {
      program_name: f.program_name.trim(),
      program_tagline: f.program_tagline.trim() || null,
      program_description: f.program_description.trim() || null,
      clusters_covered: f.clusters_covered,
      duration_weeks:  f.duration_weeks  !== "" ? Number(f.duration_weeks)  : null,
      total_sessions:  f.total_sessions  !== "" ? Number(f.total_sessions)  : null,
      session_frequency: f.session_frequency || null,
      max_patients:    f.max_patients    !== "" ? Number(f.max_patients)    : null,
      mode: f.mode,
      target_profile: f.target_profile.trim() || null,
      expected_outcomes: f.expected_outcomes,
      pricing_model: f.pricing_model || null,
      price_inr: f.price_inr !== "" ? Number(f.price_inr) : null,
      price_usd: f.price_usd !== "" ? Number(f.price_usd) : null,
    };
  };

  const saveDraft = async () => {
    if (!progForm.program_name.trim()) { progFlash("Program name is required.", true); return; }
    setProgSaving(true);
    try {
      if (editProg) {
        const { data } = await api.patch(`/doctor/programs/${editProg.id}`, buildPayload());
        setPrograms((prev) => prev.map((p) => p.id === data.id ? data : p));
        setEditProg(data);
        progFlash("Draft saved.");
      } else {
        const { data } = await api.post("/doctor/programs", buildPayload());
        setPrograms((prev) => [data, ...prev]);
        setEditProg(data);
        progFlash("Program created.");
      }
    } catch (e) { progFlash(e?.response?.data?.detail || "Failed to save.", true); }
    setProgSaving(false);
  };

  const submitForReview = async () => {
    if (!editProg) { progFlash("Save the draft first.", true); return; }
    setProgSaving(true);
    try {
      const { data } = await api.post(`/doctor/programs/${editProg.id}/submit`);
      setPrograms((prev) => prev.map((p) => p.id === data.id ? data : p));
      setEditProg(data);
      progFlash("Program submitted for admin review.");
    } catch (e) { progFlash(e?.response?.data?.detail || "Failed to submit.", true); }
    setProgSaving(false);
  };

  const addInclusion = async () => {
    if (!editProg) { progFlash("Save the draft first.", true); return; }
    if (!inclForm.inclusion_label.trim()) { progFlash("Inclusion label is required.", true); return; }
    setProgSaving(true);
    try {
      const payload = {
        ...inclForm,
        quantity: Number(inclForm.quantity) || 1,
        additional_cost: inclForm.additional_cost !== "" ? Number(inclForm.additional_cost) : null,
      };
      const { data } = await api.post(`/doctor/programs/${editProg.id}/inclusions`, payload);
      setProgInclusions((prev) => [...prev, data]);
      setInclForm(EMPTY_INCL);
      setShowInclForm(false);
    } catch (e) { progFlash(e?.response?.data?.detail || "Failed to add inclusion.", true); }
    setProgSaving(false);
  };

  const removeInclusion = async (inclId) => {
    if (!editProg) return;
    try {
      await api.delete(`/doctor/programs/${editProg.id}/inclusions/${inclId}`);
      setProgInclusions((prev) => prev.filter((i) => i.id !== inclId));
    } catch (e) { progFlash(e?.response?.data?.detail || "Failed to remove.", true); }
  };

  /* ── payment view state ── */
  const [paymentLoaded,   setPaymentLoaded]   = useState(false);
  const [paymentDetails,  setPaymentDetails]  = useState(null);   // masked PaymentDetailsResponse
  const [feeAgreements,   setFeeAgreements]   = useState([]);
  const [paymentSaving,   setPaymentSaving]   = useState(false);
  const [paymentErr,      setPaymentErr]      = useState("");
  const [paymentOk,       setPaymentOk]       = useState("");
  const EMPTY_PAY_FORM = { account_holder_name: "", bank_name: "", account_number: "", ifsc_code: "", upi_id: "", pan_number: "", gst_number: "" };
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);

  /* ── resource library state ── */
  const [resources,       setResources]       = useState([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [resScreen,       setResScreen]       = useState("list"); // "list" | "form"
  const [editRes,         setEditRes]         = useState(null);   // resource being edited
  const [resSaving,       setResSaving]       = useState(false);
  const [resUploading,    setResUploading]     = useState(false);
  const [resErr,          setResErr]           = useState("");
  const [resOk,           setResOk]            = useState("");
  const [resTypeFilter,   setResTypeFilter]    = useState("all");
  const [resStatusFilter, setResStatusFilter]  = useState("all");
  const [resSearch,       setResSearch]        = useState("");
  const [tagInput,        setTagInput]         = useState("");
  /* source toggle: "upload" | "link" */
  const [resSource,       setResSource]        = useState("upload");
  const EMPTY_RES_FORM = {
    title: "", description: "", resource_type: "pdf",
    file_url: "", file_format: "", file_size_bytes: null,
    external_url: "", tags: [], cluster_ids: [], program_ids: [],
    status: "draft", is_featured: false,
  };
  const [resForm, setResForm] = useState(EMPTY_RES_FORM);

  /* ── notification preferences state ── */
  const [notifLoaded,  setNotifLoaded]  = useState(false);
  const [notifSaving,  setNotifSaving]  = useState(false);
  const [notifErr,     setNotifErr]     = useState("");
  const [notifOk,      setNotifOk]      = useState("");
  const NOTIF_DEFAULTS = {
    notify_new_patient_enrolled: true, notify_patient_assessment_done: true,
    notify_patient_missed_session: true, notify_patient_message: true,
    notify_patient_care_plan_deviation: true, notify_patient_goal_achieved: true,
    notify_program_status_changed: true, notify_program_new_enrollment: true,
    notify_program_paused: false,
    notify_lab_results_ready: true, notify_resource_engagement: false,
    notify_follow_up_due: true, notify_document_expiring: true, notify_payout_processed: true,
    channel_email: true, channel_in_app: true, channel_sms: false,
    quiet_hours_enabled: false, quiet_start: "22:00", quiet_end: "07:00",
    daily_digest_enabled: false, weekly_digest_enabled: false,
  };
  const [notifPrefs, setNotifPrefs] = useState(NOTIF_DEFAULTS);

  /* ── inbox state ── */
  const [inbox,        setInbox]        = useState([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [bellOpen,     setBellOpen]     = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);

  /* ── hub (practice overview) state ── */
  const [hubTab,           setHubTab]           = useState("patients");
  const [hubPatients,      setHubPatients]      = useState([]);
  const [hubPatientsLoaded,setHubPatientsLoaded]= useState(false);
  const [hubPatientSearch, setHubPatientSearch] = useState("");
  const [selEnrollment,    setSelEnrollment]    = useState(null);   // enrollment_id of detail view
  const [hubDetail,        setHubDetail]        = useState(null);
  const [hubDetailLoading, setHubDetailLoading] = useState(false);
  const [hubAnalytics,     setHubAnalytics]     = useState(null);
  const [hubAnalyticsLoaded,setHubAnalyticsLoaded] = useState(false);
  const [hubEarnings,      setHubEarnings]      = useState(null);
  const [hubEarningsLoaded,setHubEarningsLoaded]= useState(false);
  const [myPayouts,        setMyPayouts]        = useState([]);

  /* ── care plan builder state (patient detail tab) ── */
  const EMPTY_CP_GOAL = { goal_type: "short_term", goal_title: "", goal_description: "", target_value: "", due_date: "" };
  const EMPTY_CP_FORM = { plan_overview: "", tailored_for_personality: "", tailored_for_beliefs: "", tailored_for_readiness: "", goals: [] };
  const [cpFormOpen, setCpFormOpen] = useState(false);
  const [cpForm,     setCpForm]     = useState(EMPTY_CP_FORM);
  const [cpSaving,   setCpSaving]   = useState(false);
  const [cpMsg,      setCpMsg]      = useState(null);

  const setCpGoal = (idx, field, value) =>
    setCpForm((f) => ({ ...f, goals: f.goals.map((g, i) => (i === idx ? { ...g, [field]: value } : g)) }));
  const addCpGoal = () => setCpForm((f) => ({ ...f, goals: [...f.goals, { ...EMPTY_CP_GOAL }] }));
  const removeCpGoal = (idx) => setCpForm((f) => ({ ...f, goals: f.goals.filter((_, i) => i !== idx) }));

  const openCpForm = () => {
    setCpForm({ ...EMPTY_CP_FORM, goals: [{ ...EMPTY_CP_GOAL }] });
    setCpMsg(null);
    setCpFormOpen(true);
  };

  const submitCarePlan = async () => {
    if (!selEnrollment) return;
    const goals = cpForm.goals.filter((g) => g.goal_title.trim());
    if (!cpForm.plan_overview.trim() && goals.length === 0) {
      setCpMsg({ type: "error", text: "Add a plan overview or at least one goal." });
      return;
    }
    setCpSaving(true);
    setCpMsg(null);
    try {
      await api.post(`/doctor/patients/${selEnrollment}/care-plan`, {
        plan_overview: cpForm.plan_overview.trim() || null,
        tailored_for_personality: cpForm.tailored_for_personality.trim() || null,
        tailored_for_beliefs: cpForm.tailored_for_beliefs.trim() || null,
        tailored_for_readiness: cpForm.tailored_for_readiness.trim() || null,
        goals: goals.map((g) => ({
          goal_type: g.goal_type,
          goal_title: g.goal_title.trim(),
          goal_description: g.goal_description.trim() || null,
          target_value: g.target_value.trim() || null,
          due_date: g.due_date || null,
        })),
      });
      setCpFormOpen(false);
      setCpForm(EMPTY_CP_FORM);
      setCpMsg({ type: "success", text: "Care plan created. The patient has been notified." });
      try {
        const { data } = await api.get(`/doctor/patients/${selEnrollment}`);
        setHubDetail(data);
      } catch (_) {}
    } catch (err) {
      setCpMsg({ type: "error", text: err.response?.data?.detail || "Could not create the care plan. Please try again." });
    }
    setCpSaving(false);
  };

  const toggleProgMode = (m) => setProgForm((p) => ({ ...p, mode: p.mode.includes(m) ? p.mode.filter((x) => x !== m) : [...p.mode, m] }));
  const toggleCluster = (id) => setProgForm((p) => ({ ...p, clusters_covered: p.clusters_covered.includes(id) ? p.clusters_covered.filter((x) => x !== id) : [...p.clusters_covered, id] }));
  const addOutcome = () => { const v = outcomeInput.trim(); if (v && !progForm.expected_outcomes.includes(v)) { setProgForm((p) => ({ ...p, expected_outcomes: [...p.expected_outcomes, v] })); } setOutcomeInput(""); };
  const removeOutcome = (idx) => setProgForm((p) => ({ ...p, expected_outcomes: p.expected_outcomes.filter((_, i) => i !== idx) }));

  const duplicateProgram = async () => {
    if (!editProg) return;
    setProgSaving(true);
    try {
      // 1. Create new draft with same data, name suffixed with " (Copy)"
      // Strip any existing " (Copy)" suffixes first so repeated duplicates never stack them.
      const base = buildPayload();
      const baseName = base.program_name.replace(/( \(Copy\))+$/i, "").trim();
      const { data: newProg } = await api.post("/doctor/programs", { ...base, program_name: `${baseName} (Copy)` });

      // 2. Copy inclusions — use already-loaded list or fetch from server first
      let sourceInclusions = progInclusions;
      if (sourceInclusions.length === 0) {
        try {
          const { data } = await api.get(`/doctor/programs/${editProg.id}/inclusions`);
          sourceInclusions = Array.isArray(data) ? data : [];
        } catch (_) {}
      }
      for (const inc of sourceInclusions) {
        try {
          await api.post(`/doctor/programs/${newProg.id}/inclusions`, {
            inclusion_type: inc.inclusion_type,
            inclusion_label: inc.inclusion_label,
            description: inc.description || null,
            is_included_in_price: inc.is_included_in_price,
            additional_cost: inc.additional_cost ?? null,
            quantity: inc.quantity,
            notes: inc.notes || null,
          });
        } catch (_) {}
      }

      // 3. Fetch the copied inclusions for the new program
      let copiedInclusions = [];
      try {
        const { data } = await api.get(`/doctor/programs/${newProg.id}/inclusions`);
        copiedInclusions = Array.isArray(data) ? data : [];
      } catch (_) {}

      // 4. Open the new draft in the builder
      setPrograms((prev) => [newProg, ...prev]);
      setEditProg(newProg);
      setProgForm(formFromProg(newProg));
      setProgInclusions(copiedInclusions);
      setShowInclForm(false);
      setOutcomeInput("");
      progFlash("Program duplicated as a new draft. You can now edit it.");
    } catch (e) {
      progFlash(e?.response?.data?.detail || "Failed to duplicate program.", true);
    }
    setProgSaving(false);
  };

  /* ── payment helpers ── */
  const payFlash = (msg, isErr = false) => {
    isErr ? setPaymentErr(msg) : setPaymentOk(msg);
    setTimeout(() => isErr ? setPaymentErr("") : setPaymentOk(""), 4000);
  };

  const loadPayment = async () => {
    if (paymentLoaded) return;
    const [payRes, feeRes] = await Promise.allSettled([
      api.get("/doctor/payment").catch(() => ({ data: null })),
      api.get("/doctor/fee-agreements"),
    ]);
    if (payRes.status === "fulfilled" && payRes.value.data) {
      const pd = payRes.value.data;
      setPaymentDetails(pd);
      // Pre-fill non-sensitive fields; leave account_number + pan_number blank (must re-enter to update)
      setPayForm((p) => ({
        ...p,
        account_holder_name: pd.account_holder_name || "",
        bank_name: pd.bank_name || "",
        ifsc_code: pd.ifsc_code || "",
        upi_id: pd.upi_id || "",
        gst_number: pd.gst_number || "",
      }));
    }
    if (feeRes.status === "fulfilled") setFeeAgreements(Array.isArray(feeRes.value.data) ? feeRes.value.data : []);
    setPaymentLoaded(true);
  };

  const openPayment = () => { setDashView("payment"); loadPayment(); };

  const savePayment = async () => {
    const f = payForm;
    if (!f.account_holder_name.trim()) { payFlash("Account holder name is required.", true); return; }
    if (!f.bank_name.trim())           { payFlash("Bank name is required.", true); return; }
    if (!f.ifsc_code.trim())           { payFlash("IFSC code is required.", true); return; }
    if (f.ifsc_code.trim().length !== 11) { payFlash("IFSC code must be exactly 11 characters.", true); return; }
    // If editing existing details, account_number and pan_number can be left blank to keep current values
    // But if this is a new submission, both are required
    if (!paymentDetails) {
      if (!f.account_number.trim()) { payFlash("Account number is required.", true); return; }
      if (!f.pan_number.trim())     { payFlash("PAN number is required.", true); return; }
    }
    if (f.account_number.trim() && (f.account_number.trim().length < 8 || f.account_number.trim().length > 20)) {
      payFlash("Account number must be 8–20 digits.", true); return;
    }
    if (f.pan_number.trim() && f.pan_number.trim().length !== 10) {
      payFlash("PAN must be exactly 10 characters.", true); return;
    }
    setPaymentSaving(true);
    try {
      const payload = {
        account_holder_name: f.account_holder_name.trim(),
        bank_name: f.bank_name.trim(),
        account_number: f.account_number.trim() || undefined,
        ifsc_code: f.ifsc_code.trim().toUpperCase(),
        upi_id: f.upi_id.trim() || null,
        pan_number: f.pan_number.trim().toUpperCase() || undefined,
        gst_number: f.gst_number.trim() || null,
      };
      // Remove undefined keys so backend doesn't reject them
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const { data } = await api.post("/doctor/payment", payload);
      setPaymentDetails(data);
      setPayForm((p) => ({ ...p, account_number: "", pan_number: "" })); // clear sensitive fields
      payFlash("Payment details saved successfully.");
    } catch (e) {
      payFlash(e?.response?.data?.detail || "Failed to save payment details.", true);
    }
    setPaymentSaving(false);
  };

  const signAgreement = async (agreementId) => {
    setPaymentSaving(true);
    try {
      const { data } = await api.post(`/doctor/fee-agreements/${agreementId}/sign`, { agree: true });
      setFeeAgreements((prev) => prev.map((a) => a.id === agreementId ? data : a));
      payFlash("Fee agreement signed successfully.");
    } catch (e) {
      payFlash(e?.response?.data?.detail || "Failed to sign agreement.", true);
    }
    setPaymentSaving(false);
  };

  /* ── resource library helpers ── */
  const resFlash = (msg, isErr = false) => {
    isErr ? setResErr(msg) : setResOk(msg);
    setTimeout(() => isErr ? setResErr("") : setResOk(""), 4000);
  };

  const loadResources = async () => {
    if (resourcesLoaded) return;
    try {
      const { data } = await api.get("/doctor/resources");
      setResources(Array.isArray(data) ? data : []);
    } catch (_) {}
    setResourcesLoaded(true);
  };

  const openResources = () => { setDashView("resources"); loadResources(); loadPrograms(); };

  /* ── notification helpers ── */
  const notifFlash = (msg, isErr = false) => {
    isErr ? setNotifErr(msg) : setNotifOk(msg);
    setTimeout(() => isErr ? setNotifErr("") : setNotifOk(""), 4000);
  };

  const loadNotifPrefs = async () => {
    if (notifLoaded) return;
    try {
      const { data } = await api.get("/doctor/notifications/preferences");
      setNotifPrefs((p) => ({ ...p, ...data }));
    } catch (_) {}
    setNotifLoaded(true);
  };

  const openNotifications = () => { setDashView("notifications"); loadNotifPrefs(); loadInbox(); };

  const saveNotifPrefs = async () => {
    setNotifSaving(true);
    try {
      const { data } = await api.put("/doctor/notifications/preferences", notifPrefs);
      setNotifPrefs((p) => ({ ...p, ...data }));
      notifFlash("Notification preferences saved.");
    } catch (e) {
      notifFlash(e?.response?.data?.detail || "Failed to save preferences.", true);
    }
    setNotifSaving(false);
  };

  const toggleNotif = (key) => setNotifPrefs((p) => ({ ...p, [key]: !p[key] }));

  /* ── inbox helpers ── */
  const loadInbox = async () => {
    setInboxLoading(true);
    try {
      const { data } = await api.get("/doctor/notifications/?limit=30");
      setInbox(Array.isArray(data.items) ? data.items : []);
      setUnreadCount(data.unread_count ?? 0);
    } catch (_) {}
    setInboxLoading(false);
  };

  const openBell = () => {
    const next = !bellOpen;
    setBellOpen(next);
    if (next && inbox.length === 0) loadInbox();
  };

  const markRead = async (id) => {
    try {
      await api.put(`/doctor/notifications/${id}/read`);
      setInbox((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/doctor/notifications/read-all");
      setInbox((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const dismissNotif = async (id) => {
    try {
      await api.delete(`/doctor/notifications/${id}`);
      const dismissed = inbox.find((n) => n.id === id);
      setInbox((prev) => prev.filter((n) => n.id !== id));
      if (dismissed && !dismissed.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  };

  const fmtNotifTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const NOTIF_ICONS = {
    program_status_changed: "📋", new_patient_enrolled: "👤",
    patient_assessment_done: "📝", patient_missed_session: "⚠️",
    patient_message: "💬", patient_care_plan_deviation: "🔴",
    patient_goal_achieved: "🏆", lab_results_ready: "🔬",
    follow_up_due: "📅", document_expiring: "📄",
    payout_processed: "💳", resource_engagement: "📚",
    program_new_enrollment: "✅", program_paused: "⏸️",
  };

  const formFromRes = (r) => ({
    title: r.title || "",
    description: r.description || "",
    resource_type: r.resource_type || "pdf",
    file_url: r.file_url || "",
    file_format: r.file_format || "",
    file_size_bytes: r.file_size_bytes ?? null,
    external_url: r.external_url || "",
    tags: r.tags || [],
    cluster_ids: (r.cluster_ids || []).map(String),
    program_ids: (r.program_ids || []).map(String),
    status: r.status || "draft",
    is_featured: r.is_featured || false,
  });

  const openResForm = (res = null) => {
    setResErr(""); setResOk(""); setTagInput("");
    if (res) {
      setEditRes(res);
      setResForm(formFromRes(res));
      setResSource(res.file_url ? "upload" : "link");
    } else {
      setEditRes(null);
      setResForm(EMPTY_RES_FORM);
      setResSource("upload");
    }
    setResScreen("form");
  };

  const uploadResFile = async (file) => {
    setResUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/doctor/resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResForm((p) => ({ ...p, file_url: data.file_url, file_format: data.file_format, file_size_bytes: data.file_size_bytes }));
      resFlash(`"${data.original_name}" uploaded successfully.`);
    } catch (e) {
      resFlash(e?.response?.data?.detail || "Upload failed.", true);
    }
    setResUploading(false);
  };

  const saveResource = async () => {
    if (!resForm.title.trim()) { resFlash("Title is required.", true); return; }
    if (resSource === "upload" && !resForm.file_url && !editRes?.file_url) { resFlash("Please upload a file first.", true); return; }
    if (resSource === "link"   && !resForm.external_url.trim()) { resFlash("External URL is required.", true); return; }
    setResSaving(true);
    try {
      const payload = {
        title: resForm.title.trim(),
        description: resForm.description.trim() || null,
        resource_type: resForm.resource_type,
        file_url: resSource === "upload" ? (resForm.file_url || editRes?.file_url || null) : null,
        file_format: resSource === "upload" ? (resForm.file_format || editRes?.file_format || null) : null,
        file_size_bytes: resSource === "upload" ? (resForm.file_size_bytes ?? editRes?.file_size_bytes ?? null) : null,
        external_url: resSource === "link" ? resForm.external_url.trim() : null,
        tags: resForm.tags,
        cluster_ids: resForm.cluster_ids,
        program_ids: resForm.program_ids,
        status: resForm.status,
        is_featured: resForm.is_featured,
      };
      if (editRes) {
        const { data } = await api.patch(`/doctor/resources/${editRes.id}`, payload);
        setResources((prev) => prev.map((r) => r.id === data.id ? data : r));
        setEditRes(data);
        resFlash("Resource updated.");
      } else {
        const { data } = await api.post("/doctor/resources", payload);
        setResources((prev) => [data, ...prev]);
        setEditRes(data);
        resFlash("Resource created.");
      }
    } catch (e) {
      resFlash(e?.response?.data?.detail || "Failed to save resource.", true);
    }
    setResSaving(false);
  };

  const deleteResource = async (resId) => {
    try {
      await api.delete(`/doctor/resources/${resId}`);
      setResources((prev) => prev.filter((r) => r.id !== resId));
      if (editRes?.id === resId) { setResScreen("list"); setEditRes(null); }
      resFlash("Resource deleted.");
    } catch (e) {
      resFlash(e?.response?.data?.detail || "Failed to delete.", true);
    }
  };

  const addResTag = () => {
    const v = tagInput.trim();
    if (v && !resForm.tags.includes(v)) setResForm((p) => ({ ...p, tags: [...p.tags, v] }));
    setTagInput("");
  };
  const removeResTag = (t) => setResForm((p) => ({ ...p, tags: p.tags.filter((x) => x !== t) }));
  const toggleResProgram = (id) => setResForm((p) => ({ ...p, program_ids: p.program_ids.includes(id) ? p.program_ids.filter((x) => x !== id) : [...p.program_ids, id] }));
  const toggleResCluster = (id) => setResForm((p) => ({ ...p, cluster_ids: p.cluster_ids.includes(id) ? p.cluster_ids.filter((x) => x !== id) : [...p.cluster_ids, id] }));

  /* ── hub helpers ── */
  const openHub = (tab = "patients") => {
    setDashView("hub");
    setHubTab(tab);
    if (tab === "patients" && !hubPatientsLoaded) loadHubPatients();
    if (tab === "intelligence" && !hubAnalyticsLoaded) loadHubAnalytics();
    if (tab === "earnings" && !hubEarningsLoaded) loadHubEarnings();
    if (tab === "programs") loadPrograms();
  };

  const loadHubPatients = async () => {
    if (hubPatientsLoaded) return;
    try {
      const { data } = await api.get("/doctor/patients");
      setHubPatients(Array.isArray(data) ? data : []);
    } catch (_) {}
    setHubPatientsLoaded(true);
  };

  const openPatientDetail = async (enrollmentId) => {
    setSelEnrollment(enrollmentId);
    setHubTab("patient_detail");
    setHubDetail(null);
    setCpFormOpen(false);
    setCpMsg(null);
    setHubDetailLoading(true);
    try {
      const { data } = await api.get(`/doctor/patients/${enrollmentId}`);
      setHubDetail(data);
    } catch (_) {}
    setHubDetailLoading(false);
  };

  const loadHubAnalytics = async () => {
    if (hubAnalyticsLoaded) return;
    try {
      const { data } = await api.get("/doctor/analytics");
      setHubAnalytics(data);
    } catch (_) {}
    setHubAnalyticsLoaded(true);
  };

  const loadHubEarnings = async () => {
    if (hubEarningsLoaded) return;
    const [earningsRes, payoutsRes] = await Promise.allSettled([
      api.get("/doctor/earnings"),
      api.get("/doctor/payouts"),
    ]);
    if (earningsRes.status === "fulfilled") setHubEarnings(earningsRes.value.data);
    if (payoutsRes.status === "fulfilled") setMyPayouts(Array.isArray(payoutsRes.value.data) ? payoutsRes.value.data : []);
    setHubEarningsLoaded(true);
  };

  /* ── cluster biomarker helpers ── */
  const EMPTY_CUSTOM_FORM = { action: "adjust_weight", proposed_weight: "", proposed_optimal_min: "", proposed_optimal_max: "", reason: "", supporting_reference: "" };

  const openCluster = async (clusterId) => {
    if (selClusterId === clusterId) {
      setSelClusterId(null); setClusterBiomarkers([]); setClusterScoring([]); setActiveBio(null);
      setClusterMasterQuestions([]); setQFormOpen(false); setQErr(""); return;
    }
    const assignment = clusters.find((c) => c.cluster_id === clusterId);
    const isApproved = assignment?.approval_status === "approved";
    setSelClusterId(clusterId); setActiveBio(null); setCustomErr(""); setBioLoading(isApproved);
    setClusterBiomarkers([]); setClusterScoring([]); setClusterMasterQuestions([]); setQFormOpen(false); setQErr("");
    if (isApproved) {
      const [bmRes, scoreRes, qRes] = await Promise.allSettled([
        api.get(`/doctor/clusters/${clusterId}/biomarkers`),
        api.get(`/doctor/clusters/${clusterId}/scoring`),
        api.get(`/doctor/clusters/${clusterId}/questions`),
      ]);
      setClusterBiomarkers(bmRes.status    === "fulfilled" ? (Array.isArray(bmRes.value.data)    ? bmRes.value.data    : []) : []);
      setClusterScoring(scoreRes.status === "fulfilled" ? (Array.isArray(scoreRes.value.data) ? scoreRes.value.data : []) : []);
      setClusterMasterQuestions(qRes.status === "fulfilled" ? (Array.isArray(qRes.value.data) ? qRes.value.data : []) : []);
      setBioLoading(false);
    }
  };

  const openCustomForm = (bm) => {
    const scoring = clusterScoring.find((s) => s.biomarker_id === bm.id);
    // No scoring config entry means this biomarker needs to be added first
    const defaultAction = scoring ? "adjust_weight" : "add_biomarker";
    setActiveBio(bm);
    setCustomForm({
      action: defaultAction,
      proposed_weight:      scoring ? String(scoring.effective_weight) : "",
      proposed_optimal_min: scoring?.effective_optimal_min != null ? String(scoring.effective_optimal_min) : "",
      proposed_optimal_max: scoring?.effective_optimal_max != null ? String(scoring.effective_optimal_max) : "",
      reason: "", supporting_reference: "",
    });
    setCustomErr("");
  };

  const submitCustomisation = async () => {
    if (!activeBio || !selClusterId) return;
    setCustomSaving(true); setCustomErr("");
    try {
      // add_biomarker carries weight + range; adjust_weight carries weight only;
      // change_range carries range only; remove_biomarker carries no numeric fields.
      const sendsWeight = customForm.action === "adjust_weight" || customForm.action === "add_biomarker";
      const sendsRange  = customForm.action === "change_range"  || customForm.action === "add_biomarker";
      const payload = {
        cluster_id:           selClusterId,
        biomarker_id:         activeBio.id,
        action:               customForm.action,
        proposed_weight:      sendsWeight && customForm.proposed_weight !== "" ? Number(customForm.proposed_weight) : null,
        proposed_optimal_min: sendsRange  && customForm.proposed_optimal_min !== "" ? Number(customForm.proposed_optimal_min) : null,
        proposed_optimal_max: sendsRange  && customForm.proposed_optimal_max !== "" ? Number(customForm.proposed_optimal_max) : null,
        reason:               customForm.reason               || null,
        supporting_reference: customForm.supporting_reference || null,
      };
      const { data } = await api.post("/doctor/clusters/biomarkers/customise", payload);
      setCustomisations((prev) => [data, ...prev]);
      setActiveBio(null);
      setCustomForm(EMPTY_CUSTOM_FORM);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setCustomErr(!d ? "Failed to submit." : typeof d === "string" ? d : Array.isArray(d) ? d.map((v) => v?.msg || String(v)).join("; ") : "Failed to submit.");
    } finally {
      setCustomSaving(false);
    }
  };

  const EMPTY_CB_FORM = { biomarker_name: "", biomarker_code: "", category: "lab", unit: "", reference_range_min: "", reference_range_min_op: "none", reference_range_max: "", reference_range_max_op: "none", optimal_range_min: "", optimal_range_min_op: "none", optimal_range_max: "", optimal_range_max_op: "none", proposed_weight: "", reason: "", supporting_reference: "" };

  const submitCustomBiomarker = async () => {
    if (!selClusterId) return;
    if (!cbForm.biomarker_name.trim() || !cbForm.biomarker_code.trim() || cbForm.proposed_weight === "") {
      setCbErr("Name, code and weight are required."); return;
    }
    setCbSaving(true); setCbErr("");
    const toNum = (v) => (v !== "" ? Number(v) : null);
    try {
      const payload = {
        cluster_id:             selClusterId,
        biomarker_name:         cbForm.biomarker_name.trim(),
        biomarker_code:         cbForm.biomarker_code.trim().toUpperCase(),
        category:               cbForm.category,
        unit:                   cbForm.unit || null,
        reference_range_min:    toNum(cbForm.reference_range_min),
        reference_range_min_op: cbForm.reference_range_min_op,
        reference_range_max:    toNum(cbForm.reference_range_max),
        reference_range_max_op: cbForm.reference_range_max_op,
        optimal_range_min:      toNum(cbForm.optimal_range_min),
        optimal_range_min_op:   cbForm.optimal_range_min_op,
        optimal_range_max:      toNum(cbForm.optimal_range_max),
        optimal_range_max_op:   cbForm.optimal_range_max_op,
        proposed_weight:        Number(cbForm.proposed_weight),
        reason:                 cbForm.reason || null,
        supporting_reference:   cbForm.supporting_reference || null,
      };
      const { data } = await api.post("/doctor/clusters/biomarkers/custom", payload);
      setMyCustomBiomarkers((prev) => [data, ...prev]);
      setCbForm(EMPTY_CB_FORM);
      setCbFormOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setCbErr(!d ? "Failed to submit." : typeof d === "string" ? d : Array.isArray(d) ? d.map((v) => v?.msg || String(v)).join("; ") : "Failed to submit.");
    } finally {
      setCbSaving(false);
    }
  };

  const submitCustomQuestion = async () => {
    if (!selClusterId || !qForm.question_text.trim()) return;
    setQSaving(true); setQErr("");
    try {
      const optionsArr = qForm.question_type === "multiple_choice"
        ? qForm.options.split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      const payload = {
        cluster_id:               selClusterId,
        question_text:            qForm.question_text,
        question_type:            qForm.question_type,
        options:                  optionsArr,
        maps_to_score_dimension:  qForm.maps_to_score_dimension || null,
        proposed_weight:          qForm.proposed_weight !== "" ? Number(qForm.proposed_weight) : null,
        display_order:            qForm.display_order !== "" ? Number(qForm.display_order) : null,
      };
      const { data } = await api.post("/doctor/clusters/questions", payload);
      setMyCustomQuestions((prev) => [data, ...prev]);
      setQForm({ question_text: "", question_type: "free_text", options: "", maps_to_score_dimension: "", proposed_weight: "", display_order: "" });
      setQFormOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setQErr(!d ? "Failed to submit question." : typeof d === "string" ? d : Array.isArray(d) ? d.map((v) => v?.msg || String(v)).join("; ") : "Failed to submit.");
    } finally {
      setQSaving(false);
    }
  };

  if (loading) return <Spinner />;

  const acctStatus = ACCOUNT_STATUS_MAP[doctor?.account_status] ?? ACCOUNT_STATUS_MAP.pending;

  /* ─── Header ─────────────────────────────────────────────────────────────── */
  const Header = (
    <header data-testid="dd-header" style={{ position: "sticky", top: 0, zIndex: 50, background: "#8AAEBB", borderBottom: "1px solid rgba(90,144,168,0.35)", padding: "0 16px 0 16px", minHeight: 58, display: "flex", alignItems: "center", gap: 8 }}>

      {/* ── Hamburger — only visible on mobile via CSS ── */}
      <button
        className="dd-hamburger"
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Open navigation menu"
        style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#FFFFFF", flexShrink: 0, alignItems: "center", justifyContent: "center" }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      <span className="font-serif-display" style={{ fontSize: 17, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.035em", flexShrink: 0, whiteSpace: "nowrap" }}>
        Supercore
      </span>

      {/* ── Scrollable nav group — hidden on mobile ── */}
      <div className="sc-no-scrollbar dd-desktop-nav" style={{ display: "flex", gap: 4, flex: 1, overflowX: "auto", alignItems: "center", justifyContent: "flex-end" }}>

      <button
        onClick={() => setDashView("clusters")}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "clusters" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "clusters" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        🧬 Clusters
      </button>

      <button
        onClick={openPractice}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "practice" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "practice" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        🏥 Practice
      </button>

      <button
        onClick={openPrograms}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "programs" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "programs" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        📋 Programs
      </button>

      <button
        onClick={openPayment}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "payment" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "payment" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        💳 Payment
      </button>

      <button
        onClick={openResources}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "resources" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "resources" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        📚 Resources
      </button>

      <button
        onClick={() => openHub("patients")}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "hub" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "hub" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        🏠 My Practice
      </button>

      <button
        onClick={openNotifications}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.3)", background: dashView === "notifications" ? "rgba(255,255,255,0.2)" : "transparent", color: "rgba(255,255,255,0.9)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
        onMouseLeave={(e) => e.currentTarget.style.background = dashView === "notifications" ? "rgba(255,255,255,0.2)" : "transparent"}
      >
        🔔 Alerts
      </button>

      </div>{/* end scrollable nav */}

      {/* ── Right actions (always visible) ── */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>

      {/* ── Notification Bell Widget ── */}
      <div style={{ position: "relative" }}>
        <button
          onClick={openBell}
          style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.35)", background: bellOpen ? "rgba(255,255,255,0.22)" : "transparent", cursor: "pointer", transition: "background 150ms ease" }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
          onMouseLeave={(e) => e.currentTarget.style.background = bellOpen ? "rgba(255,255,255,0.22)" : "transparent"}
          title="Notifications"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.92)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {unreadCount > 0 && (
            <span style={{ position: "absolute", top: -3, right: -3, minWidth: 17, height: 17, borderRadius: 999, background: "#EF4444", color: "#FFF", fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", border: "1.5px solid #8AAEBB" }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* ── Dropdown Inbox Panel ── */}
        {bellOpen && (
          <div style={{ position: "fixed", top: 62, right: 8, width: "min(360px, calc(100vw - 16px))", maxHeight: "80vh", background: "#FFF", borderRadius: 16, border: "1px solid rgba(90,144,168,0.2)", boxShadow: "0 8px 40px rgba(0,0,0,0.14)", zIndex: 200, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* header bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", borderBottom: "1px solid rgba(90,144,168,0.12)", flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                Notifications {unreadCount > 0 && <span style={{ color: "#EF4444" }}>({unreadCount})</span>}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{ fontSize: 11, color: "#5A90A8", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0, fontWeight: 600 }}>
                    Mark all read
                  </button>
                )}
                <button onClick={() => setBellOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
              </div>
            </div>

            {/* notification list */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {inboxLoading && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
                  <div style={{ width: 24, height: 24, border: "2px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                </div>
              )}
              {!inboxLoading && inbox.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>🔔</div>
                  You're all caught up!
                </div>
              )}
              {!inboxLoading && inbox.map((n) => (
                <div
                  key={n.id}
                  style={{ display: "flex", gap: 10, padding: "12px 16px", borderBottom: "1px solid rgba(90,144,168,0.08)", background: n.is_read ? "#FFF" : "rgba(90,144,168,0.05)", cursor: "pointer", transition: "background 120ms ease" }}
                  onClick={() => { if (!n.is_read) markRead(n.id); }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(90,144,168,0.08)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = n.is_read ? "#FFF" : "rgba(90,144,168,0.05)"}
                >
                  <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{NOTIF_ICONS[n.event_type] || "🔔"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6 }}>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: n.is_read ? 500 : 700, color: "#1C3545", fontFamily: "Inter, sans-serif", lineHeight: 1.4 }}>{n.title}</p>
                      {!n.is_read && <span style={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: "#5A90A8", marginTop: 4 }} />}
                    </div>
                    {n.body && <p style={{ margin: "3px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{n.body}</p>}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 5 }}>
                      <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{fmtNotifTime(n.created_at)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); dismissNotif(n.id); }}
                        style={{ fontSize: 10, color: "#9BBCCC", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: "0 0 0 8px" }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* footer */}
            <div style={{ padding: "10px 16px", borderTop: "1px solid rgba(90,144,168,0.12)", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={() => { setBellOpen(false); openNotifications(); }}
                style={{ fontSize: 12, color: "#5A90A8", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", fontWeight: 600, padding: 0 }}
              >
                Notification settings →
              </button>
              {inbox.length > 0 && (
                <button
                  onClick={loadInbox}
                  style={{ fontSize: 11, color: "#9BBCCC", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0 }}
                >
                  Refresh
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => setDashView("profile")}
        style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", cursor: "pointer", padding: "6px 10px", borderRadius: 10, transition: "background 150ms ease" }}
        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        title="View profile"
      >
        <div style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.55)", overflow: "hidden", flexShrink: 0, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {doctor?.profile_photo_url
            ? <img src={`${UPLOADS_BASE}${doctor.profile_photo_url}`} alt={doctor?.display_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
          }
        </div>
        <span className="dd-profile-name" style={{ fontSize: 13, fontWeight: 500, color: "rgba(255,255,255,0.92)", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
          {doctor?.display_name}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
      </button>

      <button
        className="dd-logout-btn"
        onClick={handleLogout}
        style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.45)", background: "transparent", color: "rgba(255,255,255,0.88)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "#FFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.88)"; }}
      >
        Log out
      </button>
      </div>{/* end right actions */}
    </header>
  );

  /* ─── Hero card ──────────────────────────────────────────────────────────── */
  const HeroCard = (
    <div style={{ ...card, padding: "32px 32px 28px" }}>
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* Avatar */}
        <div style={{ width: 100, height: 100, borderRadius: "50%", border: "3px solid rgba(90,144,168,0.4)", overflow: "hidden", flexShrink: 0, background: "rgba(90,144,168,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {doctor?.profile_photo_url
            ? <img src={`${UPLOADS_BASE}${doctor.profile_photo_url}`} alt={doctor.display_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.4)" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
          }
        </div>

        {/* Name + meta */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>
              {doctor?.display_name || "—"}
            </h1>
            <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: acctStatus.bg, color: acctStatus.color, border: `1px solid ${acctStatus.border}` }}>
              {acctStatus.label}
            </span>
          </div>

          {profile?.primary_specialty && (
            <p style={{ margin: "0 0 6px", fontSize: 15, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 500 }}>
              {profile.primary_specialty}
            </p>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 4 }}>
            {(doctor?.city || doctor?.country) && (
              <span style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                {[doctor.city, doctor.country].filter(Boolean).join(", ")}
              </span>
            )}
            {doctor?.timezone && (
              <span style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {doctor.timezone}
              </span>
            )}
            {profile?.telehealth_enabled && (
              <span style={{ fontSize: 13, color: "#16A34A", fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 10l4.553-2.07A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.9L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>
                Telehealth Available
              </span>
            )}
          </div>
        </div>

        {/* Stat chips */}
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          {profile?.years_of_experience != null && (
            <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)" }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.years_of_experience}</p>
              <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Yrs Exp</p>
            </div>
          )}
          {documents.length > 0 && (
            <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(29,78,216,0.07)", border: "1px solid rgba(29,78,216,0.18)" }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#2563EB", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{documents.length}</p>
              <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Docs</p>
            </div>
          )}
          {clusters.filter((c) => c.approval_status === "approved").length > 0 && (
            <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(22,163,74,0.07)", border: "1px solid rgba(22,163,74,0.18)" }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#16A34A", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{clusters.filter((c) => c.approval_status === "approved").length}</p>
              <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Clusters</p>
            </div>
          )}
          {(profile?.secondary_specialties?.length ?? 0) > 0 && (
            <div style={{ textAlign: "center", padding: "10px 18px", borderRadius: 14, background: "rgba(124,58,237,0.07)", border: "1px solid rgba(124,58,237,0.18)" }}>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#7C3AED", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.secondary_specialties.length + 1}</p>
              <p style={{ margin: "4px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Specialties</p>
            </div>
          )}
        </div>
      </div>

      {/* Short bio */}
      {profile?.bio_short && (
        <p style={{ margin: "24px 0 0", padding: "20px 0 0", borderTop: "1px solid rgba(90,144,168,0.12)", fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75 }}>
          {profile.bio_short}
        </p>
      )}
    </div>
  );

  /* ─── Languages tag list ─────────────────────────────────────────────────── */
  const hasBasic = doctor && (doctor.phone || doctor.gender || doctor.date_of_birth || doctor.city || doctor.country || doctor.timezone || doctor.languages_spoken?.length);
  const hasProf  = profile && (profile.medical_degree || profile.primary_specialty || profile.years_of_experience != null || profile.registration_number);
  const hasAbout = profile && (profile.bio_long || profile.philosophy_statement);
  const hasPractice = profile && (profile.practice_type || profile.clinic_name || profile.consultation_mode?.length || profile.video_intro_url);

  /* ─── Home overview ─────────────────────────────────────────────────────── */
  const HomeView = (
    <main className="dd-view-main" style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Welcome */}
      <div style={{ marginBottom: 32 }}>
        <p style={{ margin: "0 0 4px", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>Welcome back</p>
        <h2 style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>
          {doctor?.display_name || "Doctor"}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: acctStatus.bg, color: acctStatus.color, border: `1px solid ${acctStatus.border}` }}>
            {acctStatus.label}
          </span>
          {profile?.primary_specialty && (
            <span style={{ fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>· {profile.primary_specialty}</span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        {profile?.years_of_experience != null && (
          <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.years_of_experience}</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Years Experience</p>
          </div>
        )}
        <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
          <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color: "#2563EB", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{documents.length}</p>
          <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Documents Uploaded</p>
        </div>
        {clusters.filter((c) => c.approval_status === "approved").length > 0 && (
          <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color: "#16A34A", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{clusters.filter((c) => c.approval_status === "approved").length}</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Clusters Active</p>
          </div>
        )}
        {(profile?.secondary_specialties?.length ?? 0) > 0 && (
          <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 700, color: "#7C3AED", fontFamily: "Inter, sans-serif", lineHeight: 1 }}>{profile.secondary_specialties.length + 1}</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Specialties</p>
          </div>
        )}
        {(doctor?.city || doctor?.country) && (
          <div style={{ flex: "1 1 140px", padding: "20px 22px", borderRadius: 16, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", boxShadow: "0 2px 8px rgba(90,144,168,0.08)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", lineHeight: 1.3 }}>{[doctor.city, doctor.country].filter(Boolean).join(", ")}</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", textTransform: "uppercase", letterSpacing: "0.08em" }}>Location</p>
          </div>
        )}
      </div>

      {/* Quick info grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>

        {/* Profile summary card */}
        <div style={{ ...card }}>
          <p style={sectionHead}>Profile Summary</p>
          {profile?.bio_short
            ? <p style={{ margin: 0, fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75 }}>{profile.bio_short}</p>
            : <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No bio added yet. Click your name above to complete your profile.</p>
          }
          <button
            onClick={() => setDashView("profile")}
            style={{ marginTop: 18, padding: "8px 18px", borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.25)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(90,144,168,0.18)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(90,144,168,0.1)"; }}
          >
            View full profile →
          </button>
        </div>

        {/* Documents summary card */}
        <div style={{ ...card }}>
          <p style={sectionHead}>Documents & Credentials</p>
          {documents.length === 0
            ? <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No documents uploaded yet.</p>
            : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {documents.slice(0, 3).map((doc) => {
                  const vs = VERIFY_STYLE[doc.verification_status] ?? VERIFY_STYLE.pending;
                  return (
                    <div key={doc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {doc.document_label || DOC_TYPE_LABELS[doc.document_type] || doc.document_type}
                      </span>
                      <span style={{ flexShrink: 0, padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", textTransform: "capitalize", color: vs.color, background: vs.bg, border: `1px solid ${vs.border}` }}>
                        {doc.verification_status}
                      </span>
                    </div>
                  );
                })}
                {documents.length > 3 && (
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>+{documents.length - 3} more</p>
                )}
              </div>
          }
        </div>

        {/* Account status card */}
        <div style={{ ...card }}>
          <p style={sectionHead}>Account Status</p>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: acctStatus.color, flexShrink: 0 }} />
            <span style={{ fontSize: 15, fontWeight: 600, color: acctStatus.color, fontFamily: "Inter, sans-serif" }}>{acctStatus.label}</span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
            {doctor?.account_status === "pending"      ? "Your application is pending. Our team will review your profile and documents shortly."
            : doctor?.account_status === "under_review" ? "Your profile is currently under review by our team."
            : doctor?.account_status === "approved"     ? "Your profile is approved. You are live on the platform."
            : doctor?.account_status === "paused"       ? "Your account has been temporarily paused by the platform. Contact support for details."
            : doctor?.account_status === "suspended"    ? "Your account is suspended. Please contact support to resolve this."
            : doctor?.account_status === "rejected"     ? "Your application was not approved. Please contact support for more information."
            : doctor?.account_status === "offboarded"   ? "Your account has been offboarded from the platform."
            : "Please contact support for assistance with your account."}
          </p>
        </div>
      </div>

      {/* My Clusters card — full width */}
      {clusters.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid rgba(90,144,168,0.15)" }}>
              <p style={{ ...sectionHead, margin: 0, padding: 0, border: "none" }}>My Longevity Clusters</p>
              {clusters.filter((c) => c.approval_status === "approved").length > 0 && (
                <button
                  onClick={() => setDashView("clusters")}
                  style={{ padding: "5px 14px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.08)", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                >
                  Manage Biomarkers →
                </button>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {clusters.map((c) => {
                const statusStyle = CLUSTER_APPROVAL_STATUS[c.approval_status] ?? CLUSTER_APPROVAL_STATUS.pending;
                return (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderRadius: 12, background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.12)" }}>
                    {c.cluster?.icon_url ? (
                      <img
                        src={c.cluster.icon_url.startsWith("http") ? c.cluster.icon_url : `${UPLOADS_BASE}${c.cluster.icon_url}`}
                        alt=""
                        style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(90,144,168,0.2)" }}
                      />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                        🧬
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                          {c.cluster?.cluster_name || "Cluster"}
                        </p>
                        {!c.claimed_by_doctor && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "rgba(124,58,237,0.1)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)", fontFamily: "Inter, sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            Assigned
                          </span>
                        )}
                      </div>
                      <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                        {c.cluster?.cluster_group?.replace(/_/g, " ") || ""}
                        {c.expertise_level ? ` · ${c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1)}` : ""}
                        {c.years_in_cluster ? ` · ${c.years_in_cluster} yrs` : ""}
                      </p>
                    </div>
                    <span style={{ flexShrink: 0, padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: statusStyle.color, background: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}>
                      {statusStyle.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </main>
  );

  /* ─── Profile view ───────────────────────────────────────────────────────── */
  const ProfileView = (
    <main className="dd-view-main" style={{ position: "relative", zIndex: 1, maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Back + title */}
      <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 14 }}>
        <button
          onClick={() => setDashView("home")}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.45)"; e.currentTarget.style.color = "#1C3545"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.25)"; e.currentTarget.style.color = "#4D7A8E"; }}
        >
          ← Back
        </button>
        <div>
          <h2 style={{ margin: "0 0 2px", fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Doctor Profile</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Your onboarding information and credentials</p>
        </div>
      </div>

      {/* Hero */}
      {HeroCard}

        {/* Two-column grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, marginTop: 20 }}>

          {/* Basic Information */}
          {hasBasic && (
            <SectionCard title="Basic Information" icon="👤">
              <InfoRow label="Phone"       value={doctor.phone} />
              <InfoRow label="Gender"      value={doctor.gender ? GENDER_LABELS[doctor.gender] : null} />
              <InfoRow label="Date of Birth" value={doctor.date_of_birth} />
              <InfoRow label="City"        value={doctor.city} />
              <InfoRow label="Country"     value={doctor.country} />
              <InfoRow label="Timezone"    value={doctor.timezone} />
              <InfoRow label="Languages"   tags={doctor.languages_spoken?.length ? doctor.languages_spoken : null} />
            </SectionCard>
          )}

          {/* Professional Details */}
          {hasProf && (
            <SectionCard title="Professional Details" icon="🎓">
              <InfoRow label="Medical Degree"    value={profile.medical_degree} />
              <InfoRow label="Add. Qualifications" tags={profile.additional_qualifications?.length ? profile.additional_qualifications : null} />
              <InfoRow label="Primary Specialty" value={profile.primary_specialty} />
              <InfoRow label="Secondary"         tags={profile.secondary_specialties?.length ? profile.secondary_specialties : null} />
              <InfoRow label="Experience"        value={profile.years_of_experience != null ? `${profile.years_of_experience} years` : null} />
              <InfoRow label="Reg. Number"       value={profile.registration_number} />
              <InfoRow label="Reg. Body"         value={profile.registration_body} />
              <InfoRow label="Reg. Country"      value={profile.registration_country} />
            </SectionCard>
          )}
        </div>

        {/* About — full width */}
        {hasAbout && (
          <div style={{ marginTop: 20 }}>
            <SectionCard title="About" icon="📝">
              <div style={{ display: "grid", gridTemplateColumns: profile.bio_long && profile.philosophy_statement ? "1fr 1fr" : "1fr", gap: 28 }}>
                {profile.bio_long && (
                  <div>
                    <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Full Biography</p>
                    <p style={{ margin: 0, fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.8 }}>{profile.bio_long}</p>
                  </div>
                )}
                {profile.philosophy_statement && (
                  <div>
                    <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Philosophy</p>
                    <p style={{ margin: 0, fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.8, borderLeft: "2px solid rgba(90,144,168,0.4)", paddingLeft: 16, fontStyle: "italic" }}>{profile.philosophy_statement}</p>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Practice — full width */}
        {hasPractice && (
          <div style={{ marginTop: 20 }}>
            <SectionCard title="Practice Details" icon="🏥">
              <InfoRow label="Practice Type"     value={profile.practice_type ? PRACTICE_LABELS[profile.practice_type] : null} />
              <InfoRow label="Clinic / Hospital" value={profile.clinic_name} />
              <InfoRow label="Address"           value={profile.clinic_address} />
              <InfoRow label="Telehealth"        value={profile.telehealth_enabled != null ? (profile.telehealth_enabled ? "Available" : "Not available") : null} />
              <InfoRow label="Consultation"      tags={profile.consultation_mode?.length ? profile.consultation_mode : null} />
              {profile.video_intro_url && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                  <span style={rowLabel}>Video Intro</span>
                  <a href={profile.video_intro_url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
                  >
                    Watch →
                  </a>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* Clusters — full width */}
        {clusters.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <SectionCard title="Longevity Clusters" icon="🧬">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {clusters.map((c) => {
                  const statusStyle = CLUSTER_APPROVAL_STATUS[c.approval_status] ?? CLUSTER_APPROVAL_STATUS.pending;
                  return (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderRadius: 12, background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.12)" }}>
                      {c.cluster?.icon_url ? (
                        <img
                          src={c.cluster.icon_url.startsWith("http") ? c.cluster.icon_url : `${UPLOADS_BASE}${c.cluster.icon_url}`}
                          alt=""
                          style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(90,144,168,0.2)" }}
                        />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                          🧬
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                            {c.cluster?.cluster_name || "Cluster"}
                          </p>
                          {!c.claimed_by_doctor && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "rgba(124,58,237,0.1)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)", fontFamily: "Inter, sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                              Assigned
                            </span>
                          )}
                        </div>
                        <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                          {c.cluster?.cluster_group?.replace(/_/g, " ") || ""}
                          {c.expertise_level ? ` · ${c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1)}` : ""}
                          {c.years_in_cluster ? ` · ${c.years_in_cluster} yrs` : ""}
                        </p>
                      </div>
                      <span style={{ flexShrink: 0, padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: statusStyle.color, background: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}>
                        {statusStyle.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          </div>
        )}

        {/* Documents — full width */}
        <div style={{ marginTop: 20 }}>
          <SectionCard title="Documents & Credentials" icon="📄">
            {documents.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", textAlign: "center", padding: "16px 0" }}>
                No documents uploaded yet.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {documents.map((doc) => {
                  const typeLabel = DOC_TYPE_LABELS[doc.document_type] || doc.document_type;
                  const vs = VERIFY_STYLE[doc.verification_status] ?? VERIFY_STYLE.pending;
                  return (
                    <div key={doc.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(90,144,168,0.12)", background: "#F5FAFC", transition: "background 150ms ease" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#EDF4F7"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "#F5FAFC"}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                          {doc.document_label || typeLabel}
                        </p>
                        <div style={{ display: "flex", gap: 14, marginTop: 4, flexWrap: "wrap" }}>
                          {doc.document_label && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{typeLabel}</span>}
                          {doc.issued_by       && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Issued by {doc.issued_by}</span>}
                          {doc.issue_date      && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Issued {doc.issue_date}</span>}
                          {doc.expiry_date     && <span style={{ fontSize: 11, color: doc.is_expiring_soon ? "#B45309" : "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Expires {doc.expiry_date}{doc.is_expiring_soon ? " ⚠" : ""}</span>}
                        </div>
                      </div>
                      <span style={{ flexShrink: 0, padding: "4px 14px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", textTransform: "capitalize", color: vs.color, background: vs.bg, border: `1px solid ${vs.border}` }}>
                        {doc.verification_status}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>

      </main>
  );

  /* ─── Clusters & Biomarkers view ────────────────────────────────────────── */
  const CUST_STATUS = {
    pending:            { label: "Pending",            color: "#B45309", bg: "rgba(180,83,9,0.08)",    border: "rgba(180,83,9,0.2)"    },
    approved:           { label: "Approved",           color: "#16A34A", bg: "rgba(22,163,74,0.08)",   border: "rgba(22,163,74,0.2)"   },
    rejected:           { label: "Rejected",           color: "#DC2626", bg: "rgba(220,38,38,0.08)",   border: "rgba(220,38,38,0.2)"   },
    partially_approved: { label: "Partial",            color: "#7C3AED", bg: "rgba(124,58,237,0.08)",  border: "rgba(124,58,237,0.2)"  },
  };
  const SOURCE_STYLE = {
    master_default:    { label: "Default",    color: "#5A90A8", bg: "rgba(90,144,168,0.1)",   border: "rgba(90,144,168,0.25)"  },
    doctor_customised: { label: "Customised", color: "#16A34A", bg: "rgba(22,163,74,0.08)",   border: "rgba(22,163,74,0.25)"   },
  };
  const ACTION_LABELS = {
    adjust_weight:   "Adjust Weight",
    change_range:    "Change Range",
    add_biomarker:   "Add Biomarker",
    remove_biomarker:"Remove",
  };

  const inputSt = {
    width: "100%", padding: "8px 12px", borderRadius: 8,
    border: "1px solid rgba(90,144,168,0.3)", background: "#FFFFFF",
    fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif",
    outline: "none", boxSizing: "border-box",
  };
  const labelSt = { fontSize: 11, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: 5 };

  const ClustersView = (
    <main className="dd-view-main" style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 32 }}>
        <button
          onClick={() => { setDashView("home"); setSelClusterId(null); setActiveBio(null); }}
          style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", flexShrink: 0 }}
        >
          ← Back
        </button>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Clusters &amp; Biomarkers</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>All your cluster assignments — expand any cluster to view full details and manage biomarker customisations</p>
        </div>
      </div>

      {/* Empty state */}
      {clusters.length === 0 && (
        <div style={{ ...card, padding: "48px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>🧬</div>
          <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>No clusters assigned yet</p>
          <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>The Longevity OS team will assign clusters to your profile during review.</p>
        </div>
      )}

      {/* All cluster assignments */}
      {clusters.map((c) => {
        const isOpen = selClusterId === c.cluster_id;
        const isApproved = c.approval_status === "approved";
        const clusterCustomisations = customisations.filter((x) => x.cluster_id === c.cluster_id);
        const statusStyle = CLUSTER_APPROVAL_STATUS[c.approval_status] ?? CLUSTER_APPROVAL_STATUS.pending;

        return (
          <div key={c.id} style={{ marginBottom: 16 }}>
            {/* Cluster header card */}
            <div
              style={{ ...card, padding: "18px 22px", cursor: "pointer", borderColor: isOpen ? "rgba(90,144,168,0.45)" : "rgba(90,144,168,0.18)", transition: "border-color 150ms ease" }}
              onClick={() => openCluster(c.cluster_id)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {c.cluster?.icon_url ? (
                  <img src={c.cluster.icon_url.startsWith("http") ? c.cluster.icon_url : `${UPLOADS_BASE}${c.cluster.icon_url}`} alt="" style={{ width: 42, height: 42, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(90,144,168,0.2)" }} />
                ) : (
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🧬</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                      {c.cluster?.cluster_name || "Cluster"}
                    </p>
                    {c.cluster?.cluster_code && (
                      <span style={{ fontSize: 11, fontFamily: "monospace", color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 7px", borderRadius: 5, border: "1px solid rgba(90,144,168,0.2)" }}>
                        {c.cluster.cluster_code}
                      </span>
                    )}
                    {!c.claimed_by_doctor && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "rgba(124,58,237,0.1)", color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)", fontFamily: "Inter, sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Assigned
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                    {c.cluster?.cluster_group?.replace(/_/g, " ") || ""}
                    {c.expertise_level ? ` · ${c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1)}` : ""}
                    {c.years_in_cluster ? ` · ${c.years_in_cluster} yrs` : ""}
                    {clusterCustomisations.length > 0 ? ` · ${clusterCustomisations.length} customisation${clusterCustomisations.length > 1 ? "s" : ""}` : ""}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: statusStyle.color, background: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}>
                    {statusStyle.label}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7AAABB" strokeWidth="2.2" strokeLinecap="round" style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
            </div>

            {/* Expanded panel */}
            {isOpen && (
              <div style={{ marginTop: 4, borderRadius: 16, border: "1px solid rgba(90,144,168,0.2)", background: "#FFFFFF", overflow: "hidden" }}>

                {/* ── Cluster Details ── */}
                <div style={{ borderBottom: "1px solid rgba(90,144,168,0.12)" }}>

                  {/* ─ Sub-header: user-facing name + subtitle ─ */}
                  {(c.cluster?.user_facing_name || c.cluster?.user_subtitle || c.cluster?.score_full_name) && (
                    <div style={{ padding: "14px 22px 0", display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                      {c.cluster.user_facing_name && (
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{c.cluster.user_facing_name}</span>
                      )}
                      {c.cluster.score_full_name && (
                        <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>({c.cluster.score_full_name})</span>
                      )}
                      {c.cluster.user_subtitle && (
                        <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>— {c.cluster.user_subtitle}</span>
                      )}
                    </div>
                  )}

                  <div style={{ padding: "16px 22px 20px" }}>

                    {/* ─ Top grid: Cluster Info card + Assignment card ─ */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 12, marginBottom: 14 }}>

                      {/* Cluster Info — L1 short fields */}
                      {(() => {
                        const settings = Object.keys(clusterFieldSettings).length > 0 ? clusterFieldSettings : DOCTOR_FIELD_DEFAULTS;
                        const l1Short = DOCTOR_CLUSTER_FIELD_MAP
                          .filter(({ key, multiline }) => !multiline && settings[key] === "L1")
                          .map(({ label, get }) => ({ label, val: get(c.cluster || {}) }))
                          .filter(({ val }) => val != null && val !== "");
                        if (l1Short.length === 0) return null;
                        return (
                          <div style={{ background: "#FAFCFE", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 16px" }}>
                            <p style={{ margin: "0 0 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Cluster Info</p>
                            {l1Short.map(({ label, val }) => (
                              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "6px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0, minWidth: 110 }}>{label}</span>
                                <span style={{ fontSize: 11, color: "#1C3545", fontFamily: "Inter, sans-serif", textAlign: "right", fontWeight: 500, wordBreak: "break-word" }}>{String(val)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {/* Assignment Info card */}
                      <div style={{ background: "#FAFCFE", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 16px" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Assignment</p>
                        {[
                          { label: "Type",           val: c.claimed_by_doctor ? "Self-selected" : "Assigned by Admin" },
                          { label: "Status",         val: statusStyle.label, color: statusStyle.color },
                          { label: "Expertise",      val: c.expertise_level ? c.expertise_level.charAt(0).toUpperCase() + c.expertise_level.slice(1) : null },
                          { label: "Years",          val: c.years_in_cluster != null ? `${c.years_in_cluster} yrs` : null },
                          { label: "Admin Approved", val: c.approved_by_longevityos ? "Yes" : null },
                          { label: "Approved On",    val: c.approved_at ? new Date(c.approved_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null },
                        ].filter(({ val }) => val != null).map(({ label, val, color }) => (
                          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                            <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{label}</span>
                            <span style={{ fontSize: 11, color: color || "#1C3545", fontFamily: "Inter, sans-serif", fontWeight: color ? 600 : 500 }}>{val}</span>
                          </div>
                        ))}
                        {c.cluster_notes_by_doctor && (
                          <div style={{ marginTop: 10 }}>
                            <p style={{ margin: "0 0 4px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Your Notes</p>
                            <p style={{ margin: 0, fontSize: 11, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6, fontStyle: "italic" }}>{c.cluster_notes_by_doctor}</p>
                          </div>
                        )}
                        {c.rejection_reason && (
                          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 7, background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.2)" }}>
                            <p style={{ margin: "0 0 3px", fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#DC2626", fontFamily: "Inter, sans-serif" }}>Rejection</p>
                            <p style={{ margin: 0, fontSize: 11, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{c.rejection_reason}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ─ Description ─ */}
                    {c.cluster?.description && (
                      <div style={{ background: "#FAFCFE", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
                        <p style={{ margin: "0 0 6px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Description</p>
                        <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75 }}>{c.cluster.description}</p>
                      </div>
                    )}

                    {/* ─ L1 multiline/list fields ─ */}
                    {(() => {
                      const settings = Object.keys(clusterFieldSettings).length > 0 ? clusterFieldSettings : DOCTOR_FIELD_DEFAULTS;
                      const l1Multi = DOCTOR_CLUSTER_FIELD_MAP
                        .filter(({ key, multiline }) => multiline && settings[key] === "L1")
                        .map(({ label, get }) => ({ label, val: get(c.cluster || {}) }))
                        .filter(({ val }) => val != null && val !== "");
                      if (l1Multi.length === 0) return null;
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 12, marginBottom: 14 }}>
                          {l1Multi.map(({ label, val }) => {
                            const strVal = String(val);
                            const items = strVal.includes("\n") ? strVal.split("\n").filter(Boolean) : null;
                            return (
                              <div key={label} style={{ background: "#FAFCFE", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 12, padding: "14px 16px" }}>
                                <p style={{ margin: "0 0 8px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{label}</p>
                                {items ? (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {items.map((item) => (
                                      <span key={item} style={{ display: "inline-block", padding: "4px 11px", borderRadius: 999, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.22)", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", fontWeight: 500 }}>{item}</span>
                                    ))}
                                  </div>
                                ) : (
                                  <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.75, whiteSpace: "pre-wrap" }}>{strVal}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* ─ L2 collapsible ─ */}
                    <ClusterL2Fields cluster={c.cluster} fieldSettings={clusterFieldSettings} />
                  </div>
                </div>

                {/* ── Non-approved notice ── */}
                {!isApproved && (
                  <div style={{ padding: "24px", textAlign: "center" }}>
                    <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                      {c.approval_status === "pending"
                        ? "Biomarker data will be available once the Longevity OS team approves this cluster assignment."
                        : c.approval_status === "rejected"
                        ? "This cluster assignment was rejected. Contact support if you believe this is an error."
                        : "Biomarker details are available for approved cluster assignments only."}
                    </p>
                  </div>
                )}

                {/* ── Biomarkers & Scoring (approved only) ── */}
                {isApproved && (
                  <>
                    {bioLoading ? (
                      <div style={{ padding: "40px 32px", textAlign: "center" }}>
                        <div style={{ width: 32, height: 32, border: "3px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
                        <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Loading biomarkers…</p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                      </div>
                    ) : (
                      <>
                        {/* ── Biomarkers table ── */}
                        <div style={{ padding: "20px 22px 0" }}>
                          <p style={{ margin: "0 0 14px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                            Biomarkers &amp; Effective Scoring
                          </p>

                          {clusterBiomarkers.length === 0 ? (
                            <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic", marginBottom: 20 }}>No biomarkers configured for this cluster yet.</p>
                          ) : (
                            <div style={{ overflowX: "auto", marginBottom: 20 }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                                <thead>
                                  <tr style={{ background: "rgba(90,144,168,0.05)", borderBottom: "1px solid rgba(90,144,168,0.12)" }}>
                                    {(()=>{
                                      const bfSettings = Object.keys(biomarkerFieldSettings).length > 0 ? biomarkerFieldSettings : DOCTOR_BIO_FIELD_DEFAULTS;
                                      const heads = ["Biomarker", "Code", "Category", "Reference Range", "Default Wt", "Effective Wt", "Optimal Range"];
                                      if (bfSettings.data_type === "L1") heads.push("Data Type");
                                      if (bfSettings.clinical_rationale === "L1") heads.push("Clinical Rationale");
                                      heads.push("Source");
                                      if (doctorFeatureFlags.biomarker_customization) heads.push("");
                                      return heads.map((h) => (
                                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{h}</th>
                                      ));
                                    })()}
                                  </tr>
                                </thead>
                                <tbody>
                                  {clusterBiomarkers.map((bm) => {
                                    const scoring = clusterScoring.find((s) => s.biomarker_id === bm.id);
                                    const srcStyle = SOURCE_STYLE[scoring?.source || "master_default"];
                                    const isActive = activeBio?.id === bm.id;
                                    const bfSettings = Object.keys(biomarkerFieldSettings).length > 0 ? biomarkerFieldSettings : DOCTOR_BIO_FIELD_DEFAULTS;

                                    const effectiveWeight = scoring ? scoring.effective_weight : bm.default_weight;
                                    const effectiveOptMin = scoring ? scoring.effective_optimal_min : bm.optimal_range_min;
                                    const effectiveOptMax = scoring ? scoring.effective_optimal_max : bm.optimal_range_max;
                                    const optRange = fmtBioRange(effectiveOptMin, effectiveOptMax, bm.unit, bm.optimal_range_min_op, bm.optimal_range_max_op);
                                    const refRange = fmtBioRange(bm.reference_range_min, bm.reference_range_max, bm.unit, bm.reference_range_min_op, bm.reference_range_max_op);
                                    const weightCustomised = scoring?.source === "doctor_customised";

                                    const l2Fields = [];
                                    if (bfSettings.score_direction === "L2" && bm.score_direction?.length) l2Fields.push({ label: "Score Direction", val: bm.score_direction, isArray: true });
                                    if (bfSettings.scoring_type === "L2" && bm.scoring_type?.length) l2Fields.push({ label: "Scoring Type", val: bm.scoring_type, isArray: true });
                                    const hasL2 = l2Fields.length > 0;

                                    const colCount = 7 +
                                      (bfSettings.data_type === "L1" ? 1 : 0) +
                                      (bfSettings.clinical_rationale === "L1" ? 1 : 0) + 1 +
                                      (doctorFeatureFlags.biomarker_customization ? 1 : 0);

                                    return (
                                      <React.Fragment key={bm.id}>
                                        <tr style={{ borderBottom: "1px solid rgba(90,144,168,0.08)", background: isActive ? "rgba(90,144,168,0.04)" : "transparent" }}>
                                          <td style={{ padding: "11px 12px", maxWidth: 200 }}>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{bm.biomarker_name}</p>
                                            {bm.is_mandatory && <span style={{ fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>mandatory</span>}
                                            {bm.description && (
                                              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{bm.description}</p>
                                            )}
                                          </td>
                                          <td style={{ padding: "11px 12px" }}>
                                            <span style={{ fontSize: 12, fontFamily: "monospace", color: "#5A90A8", background: "rgba(90,144,168,0.08)", padding: "2px 7px", borderRadius: 5 }}>{bm.biomarker_code}</span>
                                          </td>
                                          <td style={{ padding: "11px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", textTransform: "capitalize", whiteSpace: "nowrap" }}>{bm.category}</td>
                                          <td style={{ padding: "11px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{refRange}</td>
                                          <td style={{ padding: "11px 12px" }}>
                                            <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                                              {typeof bm.default_weight === "number" ? bm.default_weight.toFixed(3) : "—"}
                                            </span>
                                          </td>
                                          <td style={{ padding: "11px 12px" }}>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: weightCustomised ? "#16A34A" : "#1C3545", fontFamily: "Inter, sans-serif" }}>
                                              {typeof effectiveWeight === "number" ? effectiveWeight.toFixed(3) : "—"}
                                            </p>
                                            {weightCustomised && typeof bm.default_weight === "number" && (
                                              <p style={{ margin: "2px 0 0", fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                                                default: {bm.default_weight.toFixed(3)}
                                              </p>
                                            )}
                                          </td>
                                          <td style={{ padding: "11px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{optRange}</td>
                                          {/* L1 new fields */}
                                          {bfSettings.data_type === "L1" && (
                                            <td style={{ padding: "11px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                                              {bm.data_type?.length ? bm.data_type.map((t) => (
                                                <span key={t} style={{ display: "inline-block", marginRight: 4, marginBottom: 2, padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: "rgba(90,144,168,0.1)", color: "#4D7A8E", border: "1px solid rgba(90,144,168,0.2)" }}>{t}</span>
                                              )) : "—"}
                                            </td>
                                          )}
                                          {bfSettings.clinical_rationale === "L1" && (
                                            <td style={{ padding: "11px 12px", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif", maxWidth: 220, lineHeight: 1.5 }}>
                                              {bm.clinical_rationale || "—"}
                                            </td>
                                          )}
                                          <td style={{ padding: "11px 12px" }}>
                                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                              <span style={{ padding: "2px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, fontFamily: "Inter, sans-serif", color: srcStyle.color, background: srcStyle.bg, border: `1px solid ${srcStyle.border}` }}>
                                                {srcStyle.label}
                                              </span>
                                              {scoring?.activated_at && (
                                                <p style={{ margin: 0, fontSize: 10, color: "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                                                  {new Date(scoring.activated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                                                </p>
                                              )}
                                              {hasL2 && (
                                                <button onClick={(e) => { e.stopPropagation(); setExpandedBiomarkerId((p) => p === bm.id ? null : bm.id); }}
                                                  style={{ fontSize: 10, color: "#5A90A8", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0, textAlign: "left", textDecoration: "underline" }}>
                                                  {expandedBiomarkerId === bm.id ? "Less" : "More"}
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                          {doctorFeatureFlags.biomarker_customization && (
                                            <td style={{ padding: "11px 12px" }}>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); isActive ? setActiveBio(null) : openCustomForm(bm); }}
                                                style={{ padding: "5px 13px", borderRadius: 7, border: `1px solid ${!scoring ? "rgba(22,163,74,0.35)" : "rgba(90,144,168,0.3)"}`, background: isActive ? "rgba(90,144,168,0.15)" : !scoring ? "rgba(22,163,74,0.08)" : "rgba(90,144,168,0.08)", color: !scoring ? "#16A34A" : "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}
                                              >
                                                {isActive ? "Cancel" : !scoring ? "+ Add" : "Customise"}
                                              </button>
                                            </td>
                                          )}
                                        </tr>

                                        {/* L2 expanded row */}
                                        {hasL2 && expandedBiomarkerId === bm.id && (
                                          <tr>
                                            <td colSpan={colCount} style={{ padding: 0 }}>
                                              <div style={{ background: "rgba(90,144,168,0.03)", borderBottom: "1px solid rgba(90,144,168,0.1)", padding: "14px 18px" }}>
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                                                  {bm.description && (
                                                    <div style={{ flex: "1 1 240px" }}>
                                                      <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Description</p>
                                                      <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{bm.description}</p>
                                                    </div>
                                                  )}
                                                  {l2Fields.map(({ label, val, isArray }) => (
                                                    <div key={label} style={{ flex: "0 0 auto" }}>
                                                      <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{label}</p>
                                                      {isArray ? (
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                                          {val.map((v) => <span key={v} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", color: "#B45309", fontFamily: "Inter, sans-serif" }}>{v}</span>)}
                                                        </div>
                                                      ) : (
                                                        <p style={{ margin: 0, fontSize: 12, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{val}</p>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}

                                        {/* Inline customisation form */}
                                        {doctorFeatureFlags.biomarker_customization && isActive && (
                                          <tr>
                                            <td colSpan={colCount} style={{ padding: 0 }}>
                                              <div style={{ margin: "0 12px 12px", padding: "20px 22px", borderRadius: 12, background: customForm.action === "remove_biomarker" ? "rgba(220,38,38,0.03)" : "rgba(90,144,168,0.04)", border: `1px solid ${customForm.action === "remove_biomarker" ? "rgba(220,38,38,0.2)" : "rgba(90,144,168,0.18)"}` }}>
                                                <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                                                  {!scoring ? "Request to Add: " : "Customise: "}
                                                  {bm.biomarker_name} <span style={{ fontWeight: 400, color: "#7AAABB" }}>({bm.biomarker_code})</span>
                                                </p>

                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 14 }}>
                                                  <div>
                                                    <label style={labelSt}>Action</label>
                                                    <select
                                                      value={customForm.action}
                                                      onChange={(e) => setCustomForm((p) => ({ ...p, action: e.target.value }))}
                                                      style={{ ...inputSt, cursor: "pointer" }}
                                                    >
                                                      {!scoring ? (
                                                        <option value="add_biomarker">Add Biomarker to Scoring</option>
                                                      ) : (
                                                        <>
                                                          <option value="adjust_weight">Adjust Weight</option>
                                                          <option value="change_range">Change Optimal Range</option>
                                                          {!bm.is_mandatory && (
                                                            <option value="remove_biomarker">Remove Biomarker</option>
                                                          )}
                                                        </>
                                                      )}
                                                    </select>
                                                  </div>

                                                  {(customForm.action === "adjust_weight" || customForm.action === "add_biomarker") && (
                                                    <div>
                                                      <label style={labelSt}>
                                                        Proposed Weight
                                                        {customForm.action === "adjust_weight" && (
                                                          <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}> (current: {typeof effectiveWeight === "number" ? effectiveWeight.toFixed(3) : "—"})</span>
                                                        )}
                                                        {customForm.action === "add_biomarker" && (
                                                          <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}> (0 – 1, optional)</span>
                                                        )}
                                                      </label>
                                                      <input
                                                        type="number" step="0.001" min="0" max="1"
                                                        value={customForm.proposed_weight}
                                                        onChange={(e) => setCustomForm((p) => ({ ...p, proposed_weight: e.target.value }))}
                                                        style={inputSt}
                                                        placeholder="e.g. 0.350"
                                                      />
                                                    </div>
                                                  )}
                                                </div>

                                                {(customForm.action === "change_range" || customForm.action === "add_biomarker") && (
                                                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                                                    <div>
                                                      <label style={labelSt}>
                                                        Optimal Min {bm.unit ? `(${bm.unit})` : ""}
                                                        {customForm.action === "change_range" && effectiveOptMin != null && (
                                                          <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}> (current: {effectiveOptMin})</span>
                                                        )}
                                                      </label>
                                                      <input
                                                        type="number" step="any"
                                                        value={customForm.proposed_optimal_min}
                                                        onChange={(e) => setCustomForm((p) => ({ ...p, proposed_optimal_min: e.target.value }))}
                                                        style={inputSt}
                                                        placeholder={effectiveOptMin != null ? String(effectiveOptMin) : "e.g. 70"}
                                                      />
                                                    </div>
                                                    <div>
                                                      <label style={labelSt}>
                                                        Optimal Max {bm.unit ? `(${bm.unit})` : ""}
                                                        {customForm.action === "change_range" && effectiveOptMax != null && (
                                                          <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}> (current: {effectiveOptMax})</span>
                                                        )}
                                                      </label>
                                                      <input
                                                        type="number" step="any"
                                                        value={customForm.proposed_optimal_max}
                                                        onChange={(e) => setCustomForm((p) => ({ ...p, proposed_optimal_max: e.target.value }))}
                                                        style={inputSt}
                                                        placeholder={effectiveOptMax != null ? String(effectiveOptMax) : "e.g. 100"}
                                                      />
                                                    </div>
                                                  </div>
                                                )}

                                                {customForm.action === "remove_biomarker" && (
                                                  <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
                                                    <p style={{ margin: 0, fontSize: 12, color: "#DC2626", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
                                                      This will request removal of <strong>{bm.biomarker_name}</strong> from your scoring engine for this cluster. The biomarker will be excluded from your patient scores once the admin approves. Provide a clinical justification below.
                                                    </p>
                                                  </div>
                                                )}

                                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                                                  <div>
                                                    <label style={labelSt}>Clinical Reason {customForm.action === "remove_biomarker" && <span style={{ color: "#DC2626" }}>*</span>}</label>
                                                    <textarea
                                                      rows={2}
                                                      value={customForm.reason}
                                                      onChange={(e) => setCustomForm((p) => ({ ...p, reason: e.target.value }))}
                                                      style={{ ...inputSt, resize: "vertical" }}
                                                      placeholder={customForm.action === "remove_biomarker" ? "Required — explain why this biomarker should be removed…" : "Explain your clinical rationale…"}
                                                    />
                                                  </div>
                                                  <div>
                                                    <label style={labelSt}>Supporting Reference <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}>(DOI / PubMed ID)</span></label>
                                                    <input
                                                      value={customForm.supporting_reference}
                                                      onChange={(e) => setCustomForm((p) => ({ ...p, supporting_reference: e.target.value }))}
                                                      style={inputSt}
                                                      placeholder="e.g. 10.1001/jama.2023.1234"
                                                    />
                                                  </div>
                                                </div>

                                                {customErr && (
                                                  <p style={{ margin: "0 0 12px", fontSize: 12, color: "#DC2626", fontFamily: "Inter, sans-serif" }}>{customErr}</p>
                                                )}

                                                <div style={{ display: "flex", gap: 10 }}>
                                                  <button
                                                    onClick={submitCustomisation}
                                                    disabled={customSaving}
                                                    style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: customSaving ? "rgba(90,144,168,0.4)" : customForm.action === "remove_biomarker" ? "#DC2626" : "#5A90A8", color: "#FFFFFF", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: customSaving ? "not-allowed" : "pointer" }}
                                                  >
                                                    {customSaving ? "Submitting…" : "Submit Request"}
                                                  </button>
                                                  <button
                                                    onClick={() => { setActiveBio(null); setCustomErr(""); }}
                                                    style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#7AAABB", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>

                        {/* ── Customisation requests for this cluster ── */}
                        {doctorFeatureFlags.biomarker_customization && clusterCustomisations.length > 0 && (
                          <div style={{ padding: "0 22px 20px", borderTop: "1px solid rgba(90,144,168,0.1)", marginTop: 4 }}>
                            <p style={{ margin: "16px 0 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>My Customisation Requests</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                              {clusterCustomisations.map((req) => {
                                const bio    = clusterBiomarkers.find((b) => b.id === req.biomarker_id);
                                const bioName = bio?.biomarker_name || req.biomarker_id.slice(0, 8) + "…";
                                const bioCode = bio?.biomarker_code;
                                const st = CUST_STATUS[req.status] ?? CUST_STATUS.pending;
                                const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                                return (
                                  <div key={req.id} style={{ borderRadius: 12, border: "1px solid rgba(90,144,168,0.15)", overflow: "hidden", background: "#FAFCFD" }}>

                                    {/* ── Card header ── */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid rgba(90,144,168,0.1)", flexWrap: "wrap", background: "rgba(90,144,168,0.03)" }}>
                                      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{bioName}</p>
                                        {bioCode && (
                                          <span style={{ fontSize: 11, fontFamily: "monospace", color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 6px", borderRadius: 4, border: "1px solid rgba(90,144,168,0.2)" }}>{bioCode}</span>
                                        )}
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                                        <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.25)" }}>
                                          {ACTION_LABELS[req.action] || req.action}
                                        </span>
                                        <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>
                                          {st.label}
                                        </span>
                                      </div>
                                    </div>

                                    {/* ── Details grid ── */}
                                    <div style={{ padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>

                                      {/* Proposed numeric changes */}
                                      <div>
                                        <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Proposed Changes</p>
                                        {req.proposed_weight != null && (
                                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Weight</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{Number(req.proposed_weight).toFixed(3)}</span>
                                          </div>
                                        )}
                                        {(req.proposed_optimal_min != null || req.proposed_optimal_max != null) && (
                                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Optimal Min</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{req.proposed_optimal_min ?? "—"}</span>
                                          </div>
                                        )}
                                        {(req.proposed_optimal_min != null || req.proposed_optimal_max != null) && (
                                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Optimal Max</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{req.proposed_optimal_max ?? "—"}</span>
                                          </div>
                                        )}
                                        {req.proposed_weight == null && req.proposed_optimal_min == null && req.proposed_optimal_max == null && (
                                          <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No numeric changes</p>
                                        )}
                                      </div>

                                      {/* Timeline */}
                                      <div>
                                        <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Timeline</p>
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                          <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Submitted</span>
                                          <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{fmtDate(req.submitted_at)}</span>
                                        </div>
                                        {req.reviewed_at && (
                                          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Reviewed</span>
                                            <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{fmtDate(req.reviewed_at)}</span>
                                          </div>
                                        )}
                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0" }}>
                                          <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Status</span>
                                          <span style={{ fontSize: 12, fontWeight: 600, color: st.color, fontFamily: "Inter, sans-serif" }}>{st.label}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* ── Clinical reason + reference ── */}
                                    {(req.reason || req.supporting_reference) && (
                                      <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "grid", gridTemplateColumns: req.reason && req.supporting_reference ? "1fr 1fr" : "1fr", gap: 16, background: "rgba(90,144,168,0.02)" }}>
                                        {req.reason && (
                                          <div>
                                            <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Clinical Reason</p>
                                            <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{req.reason}</p>
                                          </div>
                                        )}
                                        {req.supporting_reference && (
                                          <div>
                                            <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Supporting Reference</p>
                                            <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "monospace", wordBreak: "break-all" }}>{req.supporting_reference}</p>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* ── Admin notes ── */}
                                    {req.admin_notes && (
                                      <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(29,78,216,0.03)" }}>
                                        <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1D4ED8", fontFamily: "Inter, sans-serif" }}>Admin Notes</p>
                                        <p style={{ margin: 0, fontSize: 12, color: "#1D4ED8", fontFamily: "Inter, sans-serif", lineHeight: 1.65, fontStyle: "italic" }}>{req.admin_notes}</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* ── Custom Biomarker Proposals ── */}
                        {doctorFeatureFlags.custom_biomarker_proposals && (() => {
                          const clusterCBs = myCustomBiomarkers.filter((b) => b.cluster_id === c.cluster_id);
                          const CB_STATUS = {
                            pending:  { label: "Pending Review", color: "#B45309", bg: "rgba(180,83,9,0.1)",  border: "rgba(180,83,9,0.25)"  },
                            approved: { label: "Approved",       color: "#16A34A", bg: "rgba(22,163,74,0.1)", border: "rgba(22,163,74,0.25)" },
                            rejected: { label: "Rejected",       color: "#DC2626", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.25)" },
                          };
                          const OP_OPTS = [["none","None"],["lt","< Less than"],["gt","> Greater than"]];
                          const RangeField = ({ label, valKey, opKey }) => {
                            const op = cbForm[opKey];
                            const active = op !== "none";
                            return (
                              <div>
                                <label style={labelSt}>{label}</label>
                                <div style={{ display: "flex", gap: 0 }}>
                                  <select value={op} onChange={(e) => { const v = e.target.value; setCbForm((p) => ({ ...p, [opKey]: v })); }}
                                    style={{ ...inputSt, width: 120, borderRight: "none", borderRadius: "8px 0 0 8px", cursor: "pointer", color: active ? "#1D4ED8" : "#9BBCCC", paddingRight: 4 }}>
                                    {OP_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                  </select>
                                  <input type="number" step="any" value={cbForm[valKey]}
                                    onChange={(e) => setCbForm((p) => ({ ...p, [valKey]: e.target.value }))}
                                    placeholder="Value (optional)"
                                    style={{ ...inputSt, flex: 1, borderRadius: "0 8px 8px 0", minWidth: 0 }} />
                                </div>
                              </div>
                            );
                          };
                          return (
                            <div style={{ padding: "0 22px 20px", borderTop: "1px solid rgba(90,144,168,0.1)", marginTop: 4 }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, margin: "16px 0 12px" }}>
                                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                                  Custom Biomarker Proposals {clusterCBs.length > 0 && `(${clusterCBs.length})`}
                                </p>
                                {!cbFormOpen && (
                                  <button onClick={() => { setCbFormOpen(true); setCbForm(EMPTY_CB_FORM); setCbErr(""); }}
                                    style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(22,163,74,0.35)", background: "rgba(22,163,74,0.08)", color: "#16A34A", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                                    + Propose New Biomarker
                                  </button>
                                )}
                              </div>

                              {/* ── Proposal Form ── */}
                              {cbFormOpen && (
                                <div style={{ borderRadius: 12, border: "1px solid rgba(90,144,168,0.2)", background: "rgba(90,144,168,0.03)", padding: "18px 20px", marginBottom: 16 }}>
                                  <p style={{ margin: "0 0 14px", fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>New Biomarker Proposal</p>

                                  {/* Row 1: Name · Code · Category · Unit · Weight */}
                                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 12 }}>
                                    <div>
                                      <label style={labelSt}>Name <span style={{ color: "#5A90A8" }}>*</span></label>
                                      <input value={cbForm.biomarker_name} onChange={(e) => setCbForm((p) => ({ ...p, biomarker_name: e.target.value }))} placeholder="e.g. hs-CRP" style={inputSt} />
                                    </div>
                                    <div>
                                      <label style={labelSt}>Code <span style={{ color: "#5A90A8" }}>*</span></label>
                                      <input value={cbForm.biomarker_code} onChange={(e) => setCbForm((p) => ({ ...p, biomarker_code: e.target.value.toUpperCase().replace(/\s/g,"") }))} placeholder="HSCRP" style={{ ...inputSt, fontFamily: "monospace" }} />
                                    </div>
                                    <div>
                                      <label style={labelSt}>Category <span style={{ color: "#5A90A8" }}>*</span></label>
                                      <select value={cbForm.category} onChange={(e) => setCbForm((p) => ({ ...p, category: e.target.value }))} style={{ ...inputSt, cursor: "pointer" }}>
                                        {["lab","vitals","wearable","subjective","radiology"].map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <label style={labelSt}>Unit</label>
                                      <input value={cbForm.unit} onChange={(e) => setCbForm((p) => ({ ...p, unit: e.target.value }))} placeholder="mg/L, mmol/L…" style={inputSt} />
                                    </div>
                                    <div>
                                      <label style={labelSt}>Weight (0–1) <span style={{ color: "#5A90A8" }}>*</span></label>
                                      <input type="number" min="0" max="1" step="0.001" value={cbForm.proposed_weight} onChange={(e) => setCbForm((p) => ({ ...p, proposed_weight: e.target.value }))} placeholder="0.10" style={inputSt} />
                                    </div>
                                  </div>

                                  {/* Row 2: Reference Range */}
                                  <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.12)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                                    <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Reference Range <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#B0CDD8" }}>— optional</span></p>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                      {RangeField({ label: "Min", valKey: "reference_range_min", opKey: "reference_range_min_op" })}
                                      {RangeField({ label: "Max", valKey: "reference_range_max", opKey: "reference_range_max_op" })}
                                    </div>
                                  </div>

                                  {/* Row 3: Optimal Range */}
                                  <div style={{ background: "rgba(90,144,168,0.03)", border: "1px solid rgba(90,144,168,0.12)", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                                    <p style={{ margin: "0 0 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Optimal Range <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#B0CDD8" }}>— optional</span></p>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                      {RangeField({ label: "Min", valKey: "optimal_range_min", opKey: "optimal_range_min_op" })}
                                      {RangeField({ label: "Max", valKey: "optimal_range_max", opKey: "optimal_range_max_op" })}
                                    </div>
                                  </div>

                                  {/* Row 4: Clinical Reason · Reference */}
                                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 12 }}>
                                    <div>
                                      <label style={labelSt}>Clinical Reason</label>
                                      <textarea rows={2} value={cbForm.reason} onChange={(e) => setCbForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Explain why this biomarker is clinically relevant…" style={{ ...inputSt, resize: "vertical" }} />
                                    </div>
                                    <div>
                                      <label style={labelSt}>Supporting Reference <span style={{ color: "#9BBCCC", textTransform: "none", letterSpacing: 0 }}>(DOI / PubMed ID)</span></label>
                                      <input value={cbForm.supporting_reference} onChange={(e) => setCbForm((p) => ({ ...p, supporting_reference: e.target.value }))} placeholder="e.g. 10.1001/jama.2023.1234" style={inputSt} />
                                    </div>
                                  </div>

                                  {cbErr && <p style={{ margin: "0 0 10px", fontSize: 12, color: "#DC2626", fontFamily: "Inter, sans-serif" }}>{cbErr}</p>}

                                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <button onClick={submitCustomBiomarker} disabled={cbSaving}
                                      style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: cbSaving ? "rgba(90,144,168,0.4)" : "#5A90A8", color: "#FFFFFF", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: cbSaving ? "not-allowed" : "pointer" }}>
                                      {cbSaving ? "Submitting…" : "Submit Proposal"}
                                    </button>
                                    <button onClick={() => { setCbFormOpen(false); setCbErr(""); }}
                                      style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#7AAABB", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* ── Existing proposals ── */}
                              {clusterCBs.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                  {clusterCBs.map((cb) => {
                                    const st = CB_STATUS[cb.approval_status] ?? CB_STATUS.pending;
                                    const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
                                    const refRange = fmtBioRange(cb.reference_range_min, cb.reference_range_max, cb.unit, cb.reference_range_min_op, cb.reference_range_max_op);
                                    const optRange = fmtBioRange(cb.optimal_range_min, cb.optimal_range_max, cb.unit, cb.optimal_range_min_op, cb.optimal_range_max_op);
                                    const bioInfoRows = [
                                      { label: "Category",  val: cb.category,                            capitalize: true },
                                      { label: "Unit",      val: cb.unit || "—" },
                                      { label: "Weight",    val: Number(cb.proposed_weight).toFixed(3) },
                                      { label: "Ref Range", val: refRange !== "—" ? refRange : null },
                                      { label: "Opt Range", val: optRange !== "—" ? optRange : null },
                                    ].filter((r) => r.val !== null);
                                    return (
                                      <div key={cb.id} style={{ borderRadius: 12, border: "1px solid rgba(90,144,168,0.15)", overflow: "hidden", background: "#FAFCFD" }}>

                                        {/* ── Card header ── */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid rgba(90,144,168,0.1)", flexWrap: "wrap", background: "rgba(90,144,168,0.03)" }}>
                                          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{cb.biomarker_name}</p>
                                            <span style={{ fontSize: 11, fontFamily: "monospace", color: "#5A90A8", background: "rgba(90,144,168,0.1)", padding: "1px 6px", borderRadius: 4, border: "1px solid rgba(90,144,168,0.2)" }}>{cb.biomarker_code}</span>
                                          </div>
                                          <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                                            <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.25)" }}>
                                              New Biomarker
                                            </span>
                                            <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, fontFamily: "Inter, sans-serif", color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>
                                              {st.label}
                                            </span>
                                          </div>
                                        </div>

                                        {/* ── Details grid ── */}
                                        <div style={{ padding: "14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20 }}>

                                          {/* Biomarker Info */}
                                          <div>
                                            <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Biomarker Info</p>
                                            {bioInfoRows.map((r, i) => (
                                              <div key={r.label} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: i < bioInfoRows.length - 1 ? "1px solid rgba(90,144,168,0.07)" : "none" }}>
                                                <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{r.label}</span>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", textTransform: r.capitalize ? "capitalize" : "none" }}>{r.val}</span>
                                              </div>
                                            ))}
                                          </div>

                                          {/* Timeline */}
                                          <div>
                                            <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Timeline</p>
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                              <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Submitted</span>
                                              <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{fmtDate(cb.created_at)}</span>
                                            </div>
                                            {cb.reviewed_at && (
                                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0", borderBottom: "1px solid rgba(90,144,168,0.07)" }}>
                                                <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Reviewed</span>
                                                <span style={{ fontSize: 12, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{fmtDate(cb.reviewed_at)}</span>
                                              </div>
                                            )}
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 0" }}>
                                              <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Status</span>
                                              <span style={{ fontSize: 12, fontWeight: 600, color: st.color, fontFamily: "Inter, sans-serif" }}>{st.label}</span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* ── Clinical reason + reference ── */}
                                        {(cb.reason || cb.supporting_reference) && (
                                          <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(90,144,168,0.1)", display: "grid", gridTemplateColumns: cb.reason && cb.supporting_reference ? "1fr 1fr" : "1fr", gap: 16, background: "rgba(90,144,168,0.02)" }}>
                                            {cb.reason && (
                                              <div>
                                                <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Clinical Reason</p>
                                                <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>{cb.reason}</p>
                                              </div>
                                            )}
                                            {cb.supporting_reference && (
                                              <div>
                                                <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Supporting Reference</p>
                                                <p style={{ margin: 0, fontSize: 12, color: "#4D7A8E", fontFamily: "monospace", wordBreak: "break-all" }}>{cb.supporting_reference}</p>
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        {/* ── Admin notes ── */}
                                        {cb.admin_notes && (
                                          <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(90,144,168,0.1)", background: "rgba(29,78,216,0.03)" }}>
                                            <p style={{ margin: "0 0 5px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1D4ED8", fontFamily: "Inter, sans-serif" }}>Admin Notes</p>
                                            <p style={{ margin: 0, fontSize: 12, color: "#1D4ED8", fontFamily: "Inter, sans-serif", lineHeight: 1.65, fontStyle: "italic" }}>{cb.admin_notes}</p>
                                          </div>
                                        )}

                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {clusterCBs.length === 0 && !cbFormOpen && (
                                <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No custom biomarker proposals yet for this cluster.</p>
                              )}
                            </div>
                          );
                        })()}

                      {/* ── Assessment Questions ── */}
                      {(() => {
                        const clusterMyQs = myCustomQuestions.filter((q) => q.cluster_id === c.cluster_id);
                        const qStatusStyle = {
                          pending:  { color: "#B45309", bg: "rgba(180,83,9,0.1)",  border: "rgba(180,83,9,0.25)"  },
                          approved: { color: "#16A34A", bg: "rgba(22,163,74,0.1)", border: "rgba(22,163,74,0.25)" },
                          rejected: { color: "#DC2626", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.25)" },
                        };
                        return (
                          <div style={{ padding: "0 22px 24px", marginTop: 4 }}>
                            {/* Master questions */}
                            {clusterMasterQuestions.length > 0 && (
                              <div style={{ marginBottom: 20 }}>
                                <p style={{ margin: "16px 0 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                                  Platform Assessment Questions ({clusterMasterQuestions.length})
                                </p>
                                <div style={{ overflowX: "auto" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600, background: "#FAFCFD", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(90,144,168,0.12)" }}>
                                    <thead>
                                      <tr style={{ background: "rgba(90,144,168,0.05)" }}>
                                        {["Question", "Type", "Dimension", "Weight", "Mandatory"].map((h) => (
                                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {clusterMasterQuestions.map((q) => (
                                        <tr key={q.id} style={{ borderTop: "1px solid rgba(90,144,168,0.08)" }}>
                                          <td style={{ padding: "10px 12px", fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif", maxWidth: 320, lineHeight: 1.5 }}>
                                            {q.question_text}
                                            {q.options?.length > 0 && (
                                              <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                                                {q.options.map((opt, i) => (
                                                  <span key={i} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.15)", fontFamily: "Inter, sans-serif" }}>{opt}</span>
                                                ))}
                                              </div>
                                            )}
                                          </td>
                                          <td style={{ padding: "10px 12px", fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif", textTransform: "capitalize", whiteSpace: "nowrap" }}>{q.question_type?.replace(/_/g, " ")}</td>
                                          <td style={{ padding: "10px 12px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{q.maps_to_score_dimension || "—"}</td>
                                          <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{Number(q.default_weight).toFixed(2)}</td>
                                          <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 600, color: q.is_mandatory ? "#5A90A8" : "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{q.is_mandatory ? "Yes" : "No"}</span></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Propose + My custom questions */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 12px" }}>
                              <p style={{ margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>
                                My Custom Questions {clusterMyQs.length > 0 ? `(${clusterMyQs.length})` : ""}
                              </p>
                              {!qFormOpen && (
                                <button onClick={() => setQFormOpen(true)} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.08)", color: "#4D7A8E", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                                  + Propose a Question
                                </button>
                              )}
                            </div>

                            {/* Propose form */}
                            {qFormOpen && (
                              <div style={{ borderRadius: 12, border: "1px solid rgba(90,144,168,0.2)", background: "rgba(90,144,168,0.03)", padding: "18px 20px", marginBottom: 16 }}>
                                <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "Inter, sans-serif" }}>Propose a Custom Question</p>
                                {qErr && <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)", color: "#DC2626", fontSize: 12, fontFamily: "Inter, sans-serif" }}>{qErr}</div>}
                                <div style={{ marginBottom: 12 }}>
                                  <label style={{ fontSize: 11, fontWeight: 700, color: "#7AAABB", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 }}>Question Text *</label>
                                  <textarea value={qForm.question_text} onChange={(e) => setQForm((p) => ({ ...p, question_text: e.target.value }))} rows={2} placeholder="e.g. How many hours of sleep do you typically get per night?" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 12 }}>
                                  <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#7AAABB", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 }}>Type *</label>
                                    <select value={qForm.question_type} onChange={(e) => setQForm((p) => ({ ...p, question_type: e.target.value }))} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", cursor: "pointer" }}>
                                      {["scale_1_10", "yes_no", "multiple_choice", "free_text", "frequency"].map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#7AAABB", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 }}>Score Dimension</label>
                                    <input value={qForm.maps_to_score_dimension} onChange={(e) => setQForm((p) => ({ ...p, maps_to_score_dimension: e.target.value }))} placeholder="e.g. sleep" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" }} />
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#7AAABB", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 }}>Proposed Weight</label>
                                    <input type="number" min="0" max="1" step="0.01" value={qForm.proposed_weight} onChange={(e) => setQForm((p) => ({ ...p, proposed_weight: e.target.value }))} placeholder="0.0 – 1.0" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" }} />
                                  </div>
                                </div>
                                {qForm.question_type === "multiple_choice" && (
                                  <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#7AAABB", textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 }}>Options <span style={{ color: "#9BBCCC", fontWeight: 400, textTransform: "none" }}>(comma-separated) *</span></label>
                                    <input value={qForm.options} onChange={(e) => setQForm((p) => ({ ...p, options: e.target.value }))} placeholder="Option A, Option B, Option C" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" }} />
                                  </div>
                                )}
                                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                                  <button onClick={submitCustomQuestion} disabled={qSaving || !qForm.question_text.trim() || (qForm.question_type === "multiple_choice" && !qForm.options.trim())}
                                    style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.4)", background: "#5A90A8", color: "#FFFFFF", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: qSaving ? "not-allowed" : "pointer", opacity: qSaving ? 0.6 : 1 }}>
                                    {qSaving ? "Submitting…" : "Submit for Review"}
                                  </button>
                                  <button onClick={() => { setQFormOpen(false); setQErr(""); setQForm({ question_text: "", question_type: "free_text", options: "", maps_to_score_dimension: "", proposed_weight: "", display_order: "" }); }}
                                    style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#7AAABB", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* My submitted custom questions for this cluster */}
                            {clusterMyQs.length === 0 && !qFormOpen ? (
                              <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No custom questions proposed yet. Click "Propose a Question" to suggest one for admin review.</p>
                            ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {clusterMyQs.map((q) => {
                                  const st = qStatusStyle[q.approval_status] ?? qStatusStyle.pending;
                                  return (
                                    <div key={q.id} style={{ borderRadius: 12, border: "1px solid rgba(90,144,168,0.15)", overflow: "hidden", background: "#FAFCFD" }}>
                                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", flexWrap: "wrap" }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{q.question_text}</p>
                                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Type: <strong>{q.question_type?.replace(/_/g, " ")}</strong></span>
                                            {q.maps_to_score_dimension && <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Dimension: <strong>{q.maps_to_score_dimension}</strong></span>}
                                            {q.proposed_weight != null && <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Weight: <strong>{q.proposed_weight}</strong></span>}
                                            <span style={{ fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Submitted: <strong>{new Date(q.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</strong></span>
                                          </div>
                                          {q.options?.length > 0 && (
                                            <div style={{ marginTop: 6, display: "flex", gap: 5, flexWrap: "wrap" }}>
                                              {q.options.map((opt, i) => (
                                                <span key={i} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 999, background: "rgba(90,144,168,0.08)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.15)", fontFamily: "Inter, sans-serif" }}>{opt}</span>
                                              ))}
                                            </div>
                                          )}
                                          {q.admin_notes && (
                                            <p style={{ margin: "6px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>Admin notes: {q.admin_notes}</p>
                                          )}
                                        </div>
                                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 999, whiteSpace: "nowrap", color: st.color, background: st.bg, border: `1px solid ${st.border}`, fontFamily: "Inter, sans-serif", flexShrink: 0 }}>
                                          {(q.approval_status || "pending").replace(/_/g, " ")}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </main>
  );

  /* ─── Shared practice style helpers ──────────────────────────────────────── */
  const pInput = { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" };
  const pLabel = { fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 };
  const pBtn = (col = "#5A90A8", bg = "rgba(90,144,168,0.1)", bdr = "rgba(90,144,168,0.25)") => ({ padding: "8px 18px", borderRadius: 8, border: `1px solid ${bdr}`, background: bg, color: col, fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" });
  const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
  const ROLE_LABELS = { nurse: "Nurse", coordinator: "Care Coordinator", admin: "Admin", receptionist: "Receptionist", support: "Support" };
  const CONSULT_MODES = ["Video", "In-person", "Phone", "Chat"];
  const PRACTICE_TYPE_OPTS = [{ value: "solo", label: "Solo Practice" }, { value: "clinic", label: "Clinic" }, { value: "hospital", label: "Hospital" }, { value: "telehealth_only", label: "Telehealth Only" }];

  /* ─── Practice View ────────────────────────────────────────────────────────── */
  const PracticeView = (
    <main className="dd-view-main" style={{ maxWidth: 980, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: "0 0 4px", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Practice Configuration</p>
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>How your practice runs</h2>
        <p style={{ margin: "6px 0 0", fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Set your schedule, clinic details, and team so patients and the system know when and how to reach you.</p>
      </div>

      {/* Flash messages */}
      {practiceOk && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16A34A", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{practiceOk}</div>}
      {practiceErr && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{practiceErr}</div>}

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28, background: "rgba(90,144,168,0.07)", borderRadius: 10, padding: 4 }}>
        {[{ k: "schedule", label: "Schedule Builder" }, { k: "clinic", label: "Clinic Details" }, { k: "team", label: "Team & Support" }].map(({ k, label }) => (
          <button key={k} onClick={() => setPracticeTab(k)} style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "none", background: practiceTab === k ? "#FFFFFF" : "transparent", color: practiceTab === k ? "#1C3545" : "#7AAABB", fontSize: 13, fontWeight: practiceTab === k ? 700 : 500, fontFamily: "Inter, sans-serif", cursor: "pointer", boxShadow: practiceTab === k ? "0 1px 4px rgba(90,144,168,0.18)" : "none", transition: "all 150ms ease" }}>
            {label}
          </button>
        ))}
      </div>

      {/* ══ SCHEDULE BUILDER ══════════════════════════════════════════════════ */}
      {practiceTab === "schedule" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Add slot form */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <p style={sectionHead}>Add Availability Window</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
              <div>
                <label style={pLabel}>Day of Week</label>
                <select value={avForm.day_of_week} onChange={(e) => setAvForm((p) => ({ ...p, day_of_week: e.target.value }))} style={pInput}>
                  {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((d) => <option key={d} value={d}>{DAY_LABELS[d] || d}</option>)}
                </select>
              </div>
              <div>
                <label style={pLabel}>Start Time</label>
                <input type="time" value={avForm.start_time} onChange={(e) => setAvForm((p) => ({ ...p, start_time: e.target.value }))} style={pInput} />
              </div>
              <div>
                <label style={pLabel}>End Time</label>
                <input type="time" value={avForm.end_time} onChange={(e) => setAvForm((p) => ({ ...p, end_time: e.target.value }))} style={pInput} />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button onClick={addSlot} disabled={practiceSaving} style={{ ...pBtn("#16A34A", "rgba(22,163,74,0.1)", "rgba(22,163,74,0.25)"), width: "100%" }}>
                  {practiceSaving ? "Saving…" : "+ Add Slot"}
                </button>
              </div>
            </div>
            {doctor?.timezone && <p style={{ margin: "10px 0 0", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Timezone: {doctor.timezone}</p>}
          </div>

          {/* Weekly grid */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <p style={sectionHead}>Weekly Schedule</p>
            {availability.length === 0 ? (
              <p style={{ color: "#A8C8D8", fontSize: 13, fontFamily: "Inter, sans-serif", fontStyle: "italic", textAlign: "center", padding: "24px 0" }}>No availability slots yet. Add your first slot above.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((day) => {
                  const daySlots = availability.filter((s) => s.day_of_week === day);
                  if (daySlots.length === 0) return null;
                  return (
                    <div key={day} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                      <span style={{ width: 38, fontSize: 12, fontWeight: 700, color: "#5A90A8", fontFamily: "Inter, sans-serif", textTransform: "uppercase" }}>{DAY_LABELS[day]}</span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, flex: 1 }}>
                        {daySlots.map((s) => (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", borderRadius: 20, background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)" }}>
                            <span style={{ fontSize: 13, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>{s.start_time} – {s.end_time}</span>
                            <button onClick={() => deleteSlot(s.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#DC2626", fontSize: 16, lineHeight: 1 }} title="Remove">×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Capacity settings */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <p style={sectionHead}>Capacity & Booking Rules</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              {[
                { key: "max_active_patients_total",    label: "Max Active Patients" },
                { key: "max_new_patients_per_month",   label: "New Patients / Month" },
                { key: "lead_time_days",               label: "Lead Time (days)" },
                { key: "consultation_duration_mins",   label: "Consultation Duration (min)" },
                { key: "buffer_between_slots_mins",    label: "Buffer Between Slots (min)" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label style={pLabel}>{label}</label>
                  <input type="number" min={0} value={capForm[key]} onChange={(e) => setCapForm((p) => ({ ...p, [key]: Number(e.target.value) }))} style={pInput} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 28, marginTop: 16 }}>
              {[{ key: "accepting_new_patients", label: "Accepting New Patients" }, { key: "auto_pause_at_capacity", label: "Auto-Pause at Capacity" }].map(({ key, label }) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                  <input type="checkbox" checked={capForm[key]} onChange={(e) => setCapForm((p) => ({ ...p, [key]: e.target.checked }))} style={{ width: 16, height: 16, accentColor: "#5A90A8" }} />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ marginTop: 18 }}>
              <button onClick={saveCapacity} disabled={practiceSaving} style={pBtn("#5A90A8", "rgba(90,144,168,0.1)", "rgba(90,144,168,0.25)")}>
                {practiceSaving ? "Saving…" : "Save Capacity Settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ CLINIC DETAILS ════════════════════════════════════════════════════ */}
      {practiceTab === "clinic" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ ...card, padding: "22px 24px" }}>
            <p style={sectionHead}>Clinic Information</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
              <div>
                <label style={pLabel}>Clinic / Practice Name</label>
                <input value={clinicForm.clinic_name} onChange={(e) => { setClinicForm((p) => ({ ...p, clinic_name: e.target.value })); setClinicDirty(true); }} placeholder="e.g. Longevity Clinic" style={pInput} />
              </div>
              <div>
                <label style={pLabel}>Practice Type</label>
                <select value={clinicForm.practice_type} onChange={(e) => { setClinicForm((p) => ({ ...p, practice_type: e.target.value })); setClinicDirty(true); }} style={pInput}>
                  {PRACTICE_TYPE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={pLabel}>Clinic Address</label>
              <textarea value={clinicForm.clinic_address} onChange={(e) => { setClinicForm((p) => ({ ...p, clinic_address: e.target.value })); setClinicDirty(true); }} placeholder="Full address" rows={3} style={{ ...pInput, resize: "vertical" }} />
            </div>
          </div>

          <div style={{ ...card, padding: "22px 24px" }}>
            <p style={sectionHead}>Consultation Modes</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              {CONSULT_MODES.map((mode) => {
                const active = clinicForm.consultation_mode?.includes(mode);
                return (
                  <button key={mode} onClick={() => toggleMode(mode)} style={{ padding: "8px 18px", borderRadius: 20, border: `1px solid ${active ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.2)"}`, background: active ? "rgba(90,144,168,0.15)" : "transparent", color: active ? "#2D5A72" : "#9BBCCC", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}>
                    {mode}
                  </button>
                );
              })}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
              <input type="checkbox" checked={clinicForm.telehealth_enabled} onChange={(e) => { setClinicForm((p) => ({ ...p, telehealth_enabled: e.target.checked })); setClinicDirty(true); }} style={{ width: 16, height: 16, accentColor: "#5A90A8" }} />
              Telehealth Available
            </label>
          </div>

          <div>
            <button onClick={saveClinic} disabled={practiceSaving || !clinicDirty} style={{ ...pBtn(clinicDirty ? "#16A34A" : "#9BBCCC", clinicDirty ? "rgba(22,163,74,0.1)" : "rgba(90,144,168,0.05)", clinicDirty ? "rgba(22,163,74,0.25)" : "rgba(90,144,168,0.15)") }}>
              {practiceSaving ? "Saving…" : "Save Clinic Details"}
            </button>
          </div>
        </div>
      )}

      {/* ══ TEAM & SUPPORT ════════════════════════════════════════════════════ */}
      {practiceTab === "team" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Team list */}
          <div style={{ ...card, padding: "22px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <p style={{ ...sectionHead, marginBottom: 0, paddingBottom: 0, border: "none" }}>Team Members</p>
              <button onClick={() => { resetTmForm(); setShowTmForm(true); }} style={pBtn("#5A90A8", "rgba(90,144,168,0.1)", "rgba(90,144,168,0.25)")}>+ Add Member</button>
            </div>

            {teamMembers.length === 0 && !showTmForm && (
              <p style={{ color: "#A8C8D8", fontSize: 13, fontFamily: "Inter, sans-serif", fontStyle: "italic", textAlign: "center", padding: "24px 0" }}>No team members yet. Add your first team member above.</p>
            )}

            {teamMembers.map((m) => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(90,144,168,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5A90A8" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{m.full_name}</span>
                    {m.is_primary_contact && <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: "rgba(22,163,74,0.1)", color: "#16A34A", border: "1px solid rgba(22,163,74,0.25)", fontFamily: "Inter, sans-serif" }}>Primary Contact</span>}
                    {!m.is_active && <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: "rgba(107,114,128,0.1)", color: "#6B7280", border: "1px solid rgba(107,114,128,0.2)", fontFamily: "Inter, sans-serif" }}>Inactive</span>}
                  </div>
                  <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{ROLE_LABELS[m.role] || m.role}</span>
                  {(m.email || m.phone) && (
                    <div style={{ display: "flex", gap: 14, marginTop: 2 }}>
                      {m.email && <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{m.email}</span>}
                      {m.phone && <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{m.phone}</span>}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button onClick={() => editMember(m)} style={{ ...pBtn("#5A90A8", "rgba(90,144,168,0.08)", "rgba(90,144,168,0.2)"), padding: "5px 12px", fontSize: 12 }}>Edit</button>
                  <button onClick={() => removeMember(m.id)} style={{ ...pBtn("#DC2626", "rgba(220,38,38,0.07)", "rgba(220,38,38,0.2)"), padding: "5px 12px", fontSize: 12 }}>Remove</button>
                </div>
              </div>
            ))}
          </div>

          {/* Add / Edit form */}
          {showTmForm && (
            <div style={{ ...card, padding: "22px 24px", border: "1px solid rgba(90,144,168,0.3)" }}>
              <p style={sectionHead}>{editMemberId ? "Edit Team Member" : "Add Team Member"}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                <div>
                  <label style={pLabel}>Full Name *</label>
                  <input value={tmForm.full_name} onChange={(e) => setTmForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="e.g. Priya Sharma" style={pInput} />
                </div>
                <div>
                  <label style={pLabel}>Role *</label>
                  <select value={tmForm.role} onChange={(e) => setTmForm((p) => ({ ...p, role: e.target.value }))} style={pInput}>
                    {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={pLabel}>Email</label>
                  <input type="email" value={tmForm.email} onChange={(e) => setTmForm((p) => ({ ...p, email: e.target.value }))} placeholder="email@clinic.com" style={pInput} />
                </div>
                <div>
                  <label style={pLabel}>Phone</label>
                  <input type="tel" value={tmForm.phone} onChange={(e) => setTmForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" style={pInput} />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <label style={pLabel}>Notes (optional)</label>
                <textarea value={tmForm.notes} onChange={(e) => setTmForm((p) => ({ ...p, notes: e.target.value }))} placeholder="e.g. Handles scheduling queries" rows={2} style={{ ...pInput, resize: "vertical" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 12, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                <input type="checkbox" checked={tmForm.is_primary_contact} onChange={(e) => setTmForm((p) => ({ ...p, is_primary_contact: e.target.checked }))} style={{ width: 16, height: 16, accentColor: "#5A90A8" }} />
                Mark as Primary Contact
              </label>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={saveMember} disabled={practiceSaving || !tmForm.full_name} style={pBtn("#16A34A", "rgba(22,163,74,0.1)", "rgba(22,163,74,0.25)")}>
                  {practiceSaving ? "Saving…" : editMemberId ? "Update Member" : "Add Member"}
                </button>
                <button onClick={resetTmForm} style={pBtn("#9BBCCC", "rgba(90,144,168,0.05)", "rgba(90,144,168,0.15)")}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );

  /* ─── Programs View ─────────────────────────────────────────────────────────── */
  const PROG_STATUS = {
    draft:          { label: "Draft",           color: "#6B7280", bg: "rgba(107,114,128,0.1)",  border: "rgba(107,114,128,0.2)"  },
    pending_review: { label: "Pending Review",  color: "#B45309", bg: "rgba(180,83,9,0.1)",    border: "rgba(180,83,9,0.25)"    },
    approved:       { label: "Approved",        color: "#2563EB", bg: "rgba(37,99,235,0.1)",   border: "rgba(37,99,235,0.25)"   },
    rejected:       { label: "Rejected",        color: "#DC2626", bg: "rgba(220,38,38,0.1)",   border: "rgba(220,38,38,0.25)"   },
    live:           { label: "Live",            color: "#16A34A", bg: "rgba(22,163,74,0.1)",   border: "rgba(22,163,74,0.25)"   },
    paused:         { label: "Paused",          color: "#7C3AED", bg: "rgba(124,58,237,0.1)",  border: "rgba(124,58,237,0.25)"  },
    archived:       { label: "Archived",        color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.15)" },
  };
  const FREQ_LBL    = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly", as_needed: "As Needed" };
  const PRICING_LBL = { flat_fee: "Flat Fee", per_session: "Per Session", subscription: "Subscription" };
  const INCL_TYPE_LBL = { lab_panel: "Lab Panel", supplement: "Supplement", therapy: "Therapy", consultation: "Consultation", wearable_review: "Wearable Review", care_plan: "Care Plan", follow_up: "Follow-up", report: "Report", other: "Other" };
  const MODE_OPTS   = ["in_person", "video", "async"];
  const MODE_LBL    = { in_person: "In-Person", video: "Video", async: "Async" };
  const approvedClusters = clusters.filter((c) => c.approval_status === "approved");

  const pi = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.28)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none", boxSizing: "border-box" };
  const pl = { fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#7AAABB", fontFamily: "Inter, sans-serif", display: "block", marginBottom: 5 };
  const pb = (col="#5A90A8", bg="rgba(90,144,168,0.1)", bdr="rgba(90,144,168,0.25)") => ({ padding: "9px 20px", borderRadius: 8, border: `1px solid ${bdr}`, background: bg, color: col, fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" });
  const canEdit = !editProg || editProg.status === "draft" || editProg.status === "pending_review";

  const ProgramsView = (
    <main className="dd-view-main" style={{ maxWidth: 1000, margin: "0 auto", padding: "36px 24px 80px" }}>

      {/* ── Flash messages ── */}
      {progOk  && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16A34A", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{progOk}</div>}
      {progErr && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{progErr}</div>}

      {/* ══════════════════════ PROGRAMS LIST ══════════════════════════════════ */}
      {progScreen === "list" && (
        <>
          {/* Page header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
            <div>
              <p style={{ margin: "0 0 4px", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Programs I Offer</p>
              <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Care Programs</h2>
              <p style={{ margin: "6px 0 0", fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Create and manage programs so patients know what they're enrolling in.</p>
            </div>
            <button onClick={() => openBuilder(null)} style={{ ...pb("#FFFFFF", "#5A90A8", "#5A90A8"), whiteSpace: "nowrap" }}>+ New Program</button>
          </div>

          {/* Status filter pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
            {["all", "draft", "pending_review", "approved", "rejected", "live", "paused", "archived"].map((s) => {
              const st = PROG_STATUS[s];
              const active = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", border: active ? `1px solid ${st?.border || "#5A90A8"}` : "1px solid rgba(90,144,168,0.18)", background: active ? (st?.bg || "rgba(90,144,168,0.1)") : "transparent", color: active ? (st?.color || "#5A90A8") : "#9BBCCC" }}>
                  {s === "all" ? "All" : PROG_STATUS[s]?.label}
                  {s !== "all" && <span style={{ marginLeft: 5, fontSize: 10 }}>({programs.filter((p) => p.status === s).length})</span>}
                </button>
              );
            })}
          </div>

          {/* Programs grid */}
          {programs.filter((p) => statusFilter === "all" || p.status === statusFilter).length === 0 ? (
            <div style={{ ...card, textAlign: "center", padding: "52px 28px" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.35)" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 14 }}><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8 12h8M8 8h5M8 16h3"/></svg>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>No programs yet</p>
              <p style={{ margin: "0 0 20px", fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>Create your first program to let patients enrol.</p>
              <button onClick={() => openBuilder(null)} style={pb("#5A90A8", "rgba(90,144,168,0.1)", "rgba(90,144,168,0.25)")}>Create your first program</button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {programs
                .filter((p) => statusFilter === "all" || p.status === statusFilter)
                .sort((a, b) => { const order = { live: 0, approved: 1, pending_review: 2, draft: 3, rejected: 4, paused: 5, archived: 6 }; return (order[a.status] ?? 9) - (order[b.status] ?? 9); })
                .map((prog) => {
                  const st = PROG_STATUS[prog.status] ?? PROG_STATUS.draft;
                  return (
                    <div key={prog.id} style={{ ...card, padding: 0, overflow: "hidden" }}>
                      {/* Status stripe */}
                      <div style={{ height: 4, background: st.color, opacity: 0.7 }} />
                      <div style={{ padding: "18px 22px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                              <span style={{ fontSize: 16, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{prog.program_name}</span>
                              <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>{st.label}</span>
                            </div>
                            {prog.program_tagline && <p style={{ margin: "0 0 8px", fontSize: 13, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>{prog.program_tagline}</p>}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                              {prog.duration_weeks   && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>⏱ {prog.duration_weeks}w</span>}
                              {prog.total_sessions   && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>📅 {prog.total_sessions} sessions</span>}
                              {prog.session_frequency && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>🔁 {FREQ_LBL[prog.session_frequency] || prog.session_frequency}</span>}
                              {prog.max_patients     && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>👥 {prog.current_patients}/{prog.max_patients} patients</span>}
                              {prog.pricing_model    && <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>💰 {prog.price_inr ? `₹${Number(prog.price_inr).toLocaleString("en-IN")}` : ""}{prog.price_usd ? ` / $${prog.price_usd}` : ""} ({PRICING_LBL[prog.pricing_model]})</span>}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                            <button onClick={() => openBuilder(prog)} style={{ ...pb("#5A90A8", "rgba(90,144,168,0.08)", "rgba(90,144,168,0.2)"), padding: "6px 14px", fontSize: 12 }}>
                              {prog.status === "draft" ? "Edit" : "View"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}

      {/* ══════════════════════ PROGRAM BUILDER ════════════════════════════════ */}
      {progScreen === "builder" && (
        <>
          {/* Builder header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
            <button onClick={() => { setProgScreen("list"); setEditProg(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 5, padding: 0 }}>
              ← Back to list
            </button>
            <span style={{ color: "#C8DDE6" }}>|</span>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>
                {editProg ? editProg.program_name || "Untitled Program" : "New Program"}
              </h2>
              {editProg && (
                <span style={{ padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", color: (PROG_STATUS[editProg.status] || PROG_STATUS.draft).color, background: (PROG_STATUS[editProg.status] || PROG_STATUS.draft).bg, border: `1px solid ${(PROG_STATUS[editProg.status] || PROG_STATUS.draft).border}` }}>
                  {(PROG_STATUS[editProg.status] || PROG_STATUS.draft).label}
                </span>
              )}
            </div>
          </div>

          {/* Status notice for non-draft programs */}
          {editProg && editProg.status !== "draft" && (
            <div style={{
              marginBottom: 20, padding: "12px 16px", borderRadius: 8, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              background: editProg.status === "rejected" ? "rgba(220,38,38,0.06)"  : "rgba(37,99,235,0.06)",
              border:     editProg.status === "rejected" ? "1px solid rgba(220,38,38,0.25)" : "1px solid rgba(37,99,235,0.2)",
            }}>
              <span style={{ flex: 1, fontSize: 13, fontFamily: "Inter, sans-serif", color: editProg.status === "rejected" ? "#DC2626" : "#2563EB" }}>
                {editProg.status === "pending_review"
                  ? "This program is under admin review. You can still update it while pending."
                  : editProg.status === "rejected"
                  ? "This program was rejected by admin. Duplicate it, make the required changes, and resubmit for review."
                  : "This program is read-only. Duplicate it to create an editable copy."}
              </span>
              {editProg.status !== "pending_review" && (
                <button
                  onClick={duplicateProgram}
                  disabled={progSaving}
                  style={{
                    padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: progSaving ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                    border:      editProg.status === "rejected" ? "1px solid rgba(220,38,38,0.35)"  : "1px solid rgba(37,99,235,0.35)",
                    background:  editProg.status === "rejected" ? "rgba(220,38,38,0.1)"   : "rgba(37,99,235,0.1)",
                    color:       editProg.status === "rejected" ? "#DC2626"  : "#2563EB",
                  }}
                >
                  {progSaving ? "Duplicating…" : "Duplicate Program"}
                </button>
              )}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Section 1: Basic Info ── */}
            <div style={{ ...card, padding: "22px 26px" }}>
              <p style={sectionHead}>Basic Information</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={pl}>Program Name *</label>
                  <input disabled={!canEdit} value={progForm.program_name} onChange={(e) => setProgForm((p) => ({ ...p, program_name: e.target.value }))} placeholder="e.g. Metabolic Reset Program" style={pi} />
                </div>
                <div>
                  <label style={pl}>Tagline <span style={{ color: "#A8C8D8", fontWeight: 400 }}>(max 100 chars)</span></label>
                  <input disabled={!canEdit} value={progForm.program_tagline} onChange={(e) => setProgForm((p) => ({ ...p, program_tagline: e.target.value }))} maxLength={100} placeholder="One-line pitch for patients" style={pi} />
                </div>
                <div>
                  <label style={pl}>Full Description</label>
                  <textarea disabled={!canEdit} value={progForm.program_description} onChange={(e) => setProgForm((p) => ({ ...p, program_description: e.target.value }))} placeholder="What does this program include? Who is it for? What will patients achieve?" rows={5} style={{ ...pi, resize: "vertical" }} />
                </div>
              </div>
            </div>

            {/* ── Section 2: Structure & Delivery ── */}
            <div style={{ ...card, padding: "22px 26px" }}>
              <p style={sectionHead}>Structure & Delivery</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16 }}>
                <div>
                  <label style={pl}>Duration (weeks)</label>
                  <input disabled={!canEdit} type="number" min={1} value={progForm.duration_weeks} onChange={(e) => setProgForm((p) => ({ ...p, duration_weeks: e.target.value }))} placeholder="12" style={pi} />
                </div>
                <div>
                  <label style={pl}>Total Sessions</label>
                  <input disabled={!canEdit} type="number" min={1} value={progForm.total_sessions} onChange={(e) => setProgForm((p) => ({ ...p, total_sessions: e.target.value }))} placeholder="24" style={pi} />
                </div>
                <div>
                  <label style={pl}>Session Frequency</label>
                  <select disabled={!canEdit} value={progForm.session_frequency} onChange={(e) => setProgForm((p) => ({ ...p, session_frequency: e.target.value }))} style={pi}>
                    <option value="">— Select —</option>
                    {Object.entries(FREQ_LBL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={pl}>Max Patients</label>
                  <input disabled={!canEdit} type="number" min={1} value={progForm.max_patients} onChange={(e) => setProgForm((p) => ({ ...p, max_patients: e.target.value }))} placeholder="20" style={pi} />
                </div>
              </div>

              {/* Delivery Mode */}
              <div style={{ marginTop: 18 }}>
                <label style={pl}>Delivery Mode</label>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {MODE_OPTS.map((m) => {
                    const active = progForm.mode.includes(m);
                    return (
                      <button key={m} disabled={!canEdit} onClick={() => toggleProgMode(m)} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${active ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.2)"}`, background: active ? "rgba(90,144,168,0.15)" : "transparent", color: active ? "#2D5A72" : "#9BBCCC", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: canEdit ? "pointer" : "default" }}>
                        {MODE_LBL[m]}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Section 3: Clusters Covered ── */}
            <div style={{ ...card, padding: "22px 26px" }}>
              <p style={sectionHead}>Longevity Clusters Covered</p>
              {approvedClusters.length === 0 ? (
                <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No approved clusters yet. Get cluster approvals first.</p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {approvedClusters.map((c) => {
                    const selected = progForm.clusters_covered.includes(c.cluster_id);
                    return (
                      <button key={c.cluster_id} disabled={!canEdit} onClick={() => toggleCluster(c.cluster_id)} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${selected ? "rgba(22,163,74,0.45)" : "rgba(90,144,168,0.2)"}`, background: selected ? "rgba(22,163,74,0.1)" : "transparent", color: selected ? "#16A34A" : "#9BBCCC", fontSize: 13, fontWeight: selected ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: canEdit ? "pointer" : "default" }}>
                        {c.cluster?.cluster_name || c.cluster_id}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Section 4: Target & Outcomes ── */}
            <div style={{ ...card, padding: "22px 26px" }}>
              <p style={sectionHead}>Target Profile & Expected Outcomes</p>
              <div style={{ marginBottom: 16 }}>
                <label style={pl}>Target Patient Profile</label>
                <textarea disabled={!canEdit} value={progForm.target_profile} onChange={(e) => setProgForm((p) => ({ ...p, target_profile: e.target.value }))} placeholder="Who is this program best suited for? e.g. adults 40+ with metabolic dysfunction..." rows={3} style={{ ...pi, resize: "vertical" }} />
              </div>
              <div>
                <label style={pl}>Expected Outcomes</label>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input disabled={!canEdit} value={outcomeInput} onChange={(e) => setOutcomeInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOutcome(); } }} placeholder="e.g. 20% reduction in fasting insulin" style={{ ...pi, flex: 1 }} />
                  <button disabled={!canEdit} onClick={addOutcome} style={{ ...pb("#5A90A8", "rgba(90,144,168,0.1)", "rgba(90,144,168,0.25)"), whiteSpace: "nowrap" }}>Add</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {progForm.expected_outcomes.map((o, i) => (
                    <span key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 12px", borderRadius: 20, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", fontSize: 12, fontFamily: "Inter, sans-serif", color: "#2D5A72" }}>
                      {o}
                      {canEdit && <button onClick={() => removeOutcome(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Section 5: Pricing ── */}
            <div style={{ ...card, padding: "22px 26px" }}>
              <p style={sectionHead}>Pricing</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
                <div>
                  <label style={pl}>Pricing Model</label>
                  <select disabled={!canEdit} value={progForm.pricing_model} onChange={(e) => setProgForm((p) => ({ ...p, pricing_model: e.target.value }))} style={pi}>
                    <option value="">— Select —</option>
                    {Object.entries(PRICING_LBL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label style={pl}>Price (INR ₹)</label>
                  <input disabled={!canEdit} type="number" min={0} value={progForm.price_inr} onChange={(e) => setProgForm((p) => ({ ...p, price_inr: e.target.value }))} placeholder="0" style={pi} />
                </div>
                <div>
                  <label style={pl}>Price (USD $)</label>
                  <input disabled={!canEdit} type="number" min={0} value={progForm.price_usd} onChange={(e) => setProgForm((p) => ({ ...p, price_usd: e.target.value }))} placeholder="Optional" style={pi} />
                </div>
              </div>
            </div>

            {/* ── Section 6: Inclusions ── */}
            {editProg && (
              <div style={{ ...card, padding: "22px 26px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <p style={{ ...sectionHead, marginBottom: 0, paddingBottom: 0, border: "none" }}>What's Included</p>
                  {canEdit && <button onClick={() => { setInclForm(EMPTY_INCL); setShowInclForm((v) => !v); }} style={{ ...pb("#5A90A8", "rgba(90,144,168,0.08)", "rgba(90,144,168,0.2)"), padding: "6px 14px", fontSize: 12 }}>+ Add Item</button>}
                </div>

                {inclLoading && <p style={{ fontSize: 13, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Loading inclusions…</p>}

                {!inclLoading && progInclusions.length === 0 && !showInclForm && (
                  <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No inclusions added yet. Tell patients what they'll receive.</p>
                )}

                {progInclusions.map((inc) => (
                  <div key={inc.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: "rgba(90,144,168,0.1)", color: "#5A90A8", border: "1px solid rgba(90,144,168,0.22)", fontFamily: "Inter, sans-serif", flexShrink: 0 }}>{INCL_TYPE_LBL[inc.inclusion_type] || inc.inclusion_type}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{inc.inclusion_label}</span>
                        {inc.quantity > 1 && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>×{inc.quantity}</span>}
                        <span style={{ fontSize: 11, fontFamily: "Inter, sans-serif", color: inc.is_included_in_price ? "#16A34A" : "#B45309" }}>
                          {inc.is_included_in_price ? "Included" : `+₹${inc.additional_cost || 0}`}
                        </span>
                      </div>
                      {inc.description && <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{inc.description}</p>}
                    </div>
                    {canEdit && <button onClick={() => removeInclusion(inc.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 18, lineHeight: 1, flexShrink: 0 }} title="Remove">×</button>}
                  </div>
                ))}

                {showInclForm && (
                  <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, background: "rgba(90,144,168,0.04)", border: "1px solid rgba(90,144,168,0.18)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
                      <div>
                        <label style={pl}>Type *</label>
                        <select value={inclForm.inclusion_type} onChange={(e) => setInclForm((p) => ({ ...p, inclusion_type: e.target.value }))} style={pi}>
                          {Object.entries(INCL_TYPE_LBL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={pl}>Label *</label>
                        <input value={inclForm.inclusion_label} onChange={(e) => setInclForm((p) => ({ ...p, inclusion_label: e.target.value }))} placeholder="e.g. Initial blood panel" style={pi} />
                      </div>
                      <div>
                        <label style={pl}>Quantity</label>
                        <input type="number" min={1} value={inclForm.quantity} onChange={(e) => setInclForm((p) => ({ ...p, quantity: e.target.value }))} style={pi} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={pl}>Description</label>
                      <input value={inclForm.description} onChange={(e) => setInclForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional detail" style={pi} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>
                        <input type="checkbox" checked={inclForm.is_included_in_price} onChange={(e) => setInclForm((p) => ({ ...p, is_included_in_price: e.target.checked }))} style={{ width: 15, height: 15, accentColor: "#5A90A8" }} />
                        Included in program price
                      </label>
                      {!inclForm.is_included_in_price && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <label style={{ ...pl, marginBottom: 0 }}>Additional Cost (₹)</label>
                          <input type="number" min={0} value={inclForm.additional_cost} onChange={(e) => setInclForm((p) => ({ ...p, additional_cost: e.target.value }))} style={{ ...pi, width: 120 }} />
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={addInclusion} disabled={progSaving} style={pb("#16A34A", "rgba(22,163,74,0.1)", "rgba(22,163,74,0.25)")}>
                        {progSaving ? "Adding…" : "Add Inclusion"}
                      </button>
                      <button onClick={() => setShowInclForm(false)} style={pb("#9BBCCC", "transparent", "rgba(90,144,168,0.15)")}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Footer actions ── */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", padding: "20px 0" }}>
              <button onClick={saveDraft} disabled={progSaving || !progForm.program_name.trim()} style={{ ...pb("#5A90A8", "rgba(90,144,168,0.1)", "rgba(90,144,168,0.25)"), opacity: progForm.program_name.trim() ? 1 : 0.5 }}>
                {progSaving ? "Saving…" : editProg ? "Save Changes" : "Save as Draft"}
              </button>

              {editProg && editProg.status === "draft" && (
                <button onClick={submitForReview} disabled={progSaving} style={pb("#2563EB", "rgba(37,99,235,0.1)", "rgba(37,99,235,0.3)")}>
                  {progSaving ? "Submitting…" : "Submit for Review →"}
                </button>
              )}

              {editProg && editProg.status === "pending_review" && (
                <span style={{ fontSize: 12, color: "#B45309", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>Awaiting admin review…</span>
              )}

              {editProg && (editProg.status === "approved" || editProg.status === "live") && (
                <span style={{ fontSize: 12, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>✓ {editProg.status === "live" ? "Program is live" : "Approved — ready to go live"}</span>
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );

  /* ─── Payment Setup View ───────────────────────────────────────────────────── */
  const FEE_MODEL_LBL = { flat_monthly: "Flat Monthly Fee", per_patient_percentage: "Per-Patient Revenue Share", hybrid: "Hybrid (Flat + %" };
  const FEE_TYPE_LBL  = { flat: "Flat (₹)", percentage: "Percentage (%)" };

  const PaymentView = (
    <main className="dd-view-main" style={{ maxWidth: 820, margin: "0 auto", padding: "32px 20px 60px" }}>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: "0 0 4px", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Earnings & Payouts</p>
        <h2 style={{ margin: "0 0 6px", fontSize: 28, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Payment Setup</h2>
        <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Set up your bank account and tax details to receive earnings without delays.</p>
      </div>

      {/* Verification status banner */}
      {paymentDetails && (
        <div style={{ marginBottom: 24, padding: "12px 18px", borderRadius: 10, display: "flex", alignItems: "center", gap: 12, background: paymentDetails.is_verified ? "rgba(22,163,74,0.08)" : "rgba(180,83,9,0.07)", border: `1px solid ${paymentDetails.is_verified ? "rgba(22,163,74,0.3)" : "rgba(180,83,9,0.25)"}` }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: paymentDetails.is_verified ? "#16A34A" : "#B45309", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", color: paymentDetails.is_verified ? "#16A34A" : "#B45309" }}>
              {paymentDetails.is_verified ? "Payment details verified" : "Pending verification"}
            </span>
            <span style={{ fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginLeft: 10 }}>
              {paymentDetails.is_verified ? "Payouts are enabled for your account." : "Our finance team will verify your details. Payouts are held until verified."}
            </span>
          </div>
        </div>
      )}

      {/* Flash messages */}
      {paymentErr && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{paymentErr}</div>}
      {paymentOk  && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(22,163,74,0.07)",  border: "1px solid rgba(22,163,74,0.25)",  color: "#16A34A", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{paymentOk}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Section 1: Bank Account ── */}
        <div style={{ ...card, padding: "24px 28px" }}>
          <p style={sectionHead}>Bank Account Details</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={pl}>Account Holder Name *</label>
              <input value={payForm.account_holder_name} onChange={(e) => setPayForm((p) => ({ ...p, account_holder_name: e.target.value }))} placeholder="As printed on passbook" style={pi} />
            </div>
            <div>
              <label style={pl}>Bank Name *</label>
              <input value={payForm.bank_name} onChange={(e) => setPayForm((p) => ({ ...p, bank_name: e.target.value }))} placeholder="e.g. HDFC Bank" style={pi} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={pl}>Account Number {paymentDetails ? "" : "*"}</label>
              <input
                type="password"
                value={payForm.account_number}
                onChange={(e) => setPayForm((p) => ({ ...p, account_number: e.target.value }))}
                placeholder={paymentDetails?.account_number_masked ? `Currently: ${paymentDetails.account_number_masked} — re-enter to update` : "Enter account number"}
                style={pi}
                autoComplete="off"
              />
            </div>
            <div>
              <label style={pl}>IFSC Code *</label>
              <input
                value={payForm.ifsc_code}
                onChange={(e) => setPayForm((p) => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))}
                placeholder="e.g. HDFC0001234"
                maxLength={11}
                style={{ ...pi, fontFamily: "monospace" }}
              />
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>11-character code from your cheque book</p>
            </div>
          </div>

          <div>
            <label style={pl}>UPI ID <span style={{ color: "#A8C8D8", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
            <input value={payForm.upi_id} onChange={(e) => setPayForm((p) => ({ ...p, upi_id: e.target.value }))} placeholder="e.g. yourname@upi" style={{ ...pi, maxWidth: 340 }} />
          </div>
        </div>

        {/* ── Section 2: Tax Information ── */}
        <div style={{ ...card, padding: "24px 28px" }}>
          <p style={sectionHead}>Tax & Compliance</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
            <div>
              <label style={pl}>PAN Number {paymentDetails ? "" : "*"}</label>
              <input
                value={payForm.pan_number}
                onChange={(e) => setPayForm((p) => ({ ...p, pan_number: e.target.value.toUpperCase() }))}
                placeholder={paymentDetails?.pan_number_masked ? `Currently: ${paymentDetails.pan_number_masked} — re-enter to update` : "e.g. ABCDE1234F"}
                maxLength={10}
                style={{ ...pi, fontFamily: "monospace", textTransform: "uppercase" }}
                autoComplete="off"
              />
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Required for TDS deduction compliance</p>
            </div>
            <div>
              <label style={pl}>GST Number <span style={{ color: "#A8C8D8", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              <input value={payForm.gst_number} onChange={(e) => setPayForm((p) => ({ ...p, gst_number: e.target.value.toUpperCase() }))} placeholder="e.g. 27ABCDE1234F1Z5" maxLength={20} style={{ ...pi, fontFamily: "monospace" }} />
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Required only if you are GST registered</p>
            </div>
          </div>

          {/* Privacy note */}
          <div style={{ marginTop: 18, padding: "10px 14px", borderRadius: 8, background: "rgba(90,144,168,0.05)", border: "1px solid rgba(90,144,168,0.15)", display: "flex", gap: 10 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#7AAABB" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1 }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
              Account number and PAN are AES-256 encrypted before storage and never logged. Only masked values are shown after saving.
            </p>
          </div>
        </div>

        {/* ── Save button ── */}
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            onClick={savePayment}
            disabled={paymentSaving}
            style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: paymentSaving ? "#A8C8D8" : "#5A90A8", color: "#FFFFFF", fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: paymentSaving ? "not-allowed" : "pointer", transition: "background 150ms ease" }}
          >
            {paymentSaving ? "Saving…" : paymentDetails ? "Update Payment Details" : "Save Payment Details"}
          </button>
          {paymentDetails && (
            <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Leave account number or PAN blank to keep existing values</span>
          )}
        </div>

        {/* ── Section 3: Fee Agreements ── */}
        {feeAgreements.length > 0 && (
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Platform Fee Agreement</p>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Review and sign your platform fee agreement to activate payouts.</p>

            {feeAgreements.map((ag) => (
              <div key={ag.id} style={{ borderRadius: 12, border: `1px solid ${ag.is_active ? "rgba(90,144,168,0.3)" : "rgba(90,144,168,0.12)"}`, padding: "18px 20px", marginBottom: 12, background: ag.is_active ? "#FFFFFF" : "rgba(90,144,168,0.02)", opacity: ag.is_active ? 1 : 0.6 }}>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Agreement v{ag.agreement_version}</span>
                    {!ag.is_active && <span style={{ marginLeft: 10, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>(Superseded)</span>}
                  </div>
                  {ag.signed_by_doctor
                    ? <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#16A34A", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.25)", fontFamily: "Inter, sans-serif" }}>✓ Signed</span>
                    : ag.is_active
                      ? <span style={{ padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#B45309", background: "rgba(180,83,9,0.1)", border: "1px solid rgba(180,83,9,0.25)", fontFamily: "Inter, sans-serif" }}>Awaiting Signature</span>
                      : null
                  }
                </div>

                {/* Fee terms grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
                  {[
                    ["Fee Model", FEE_MODEL_LBL[ag.fee_model] || ag.fee_model],
                    ag.monthly_platform_fee_inr != null && ["Monthly Fee", `₹${ag.monthly_platform_fee_inr.toLocaleString()}`],
                    ag.per_patient_share_pct    != null && ["Revenue Share", `${ag.per_patient_share_pct}%`],
                    ag.minimum_monthly_fee      != null && ["Minimum Monthly", `₹${ag.minimum_monthly_fee.toLocaleString()}`],
                    ag.payment_terms                   && ["Payment Terms", ag.payment_terms],
                    ag.effective_from                  && ["Effective From", new Date(ag.effective_from).toLocaleDateString()],
                    ag.expires_at                      && ["Expires", new Date(ag.expires_at).toLocaleDateString()],
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label} style={{ background: "rgba(90,144,168,0.05)", borderRadius: 8, padding: "9px 12px" }}>
                      <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{label}</p>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{val}</p>
                    </div>
                  ))}
                </div>

                {ag.signed_at && (
                  <p style={{ margin: "0 0 10px", fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Signed on {new Date(ag.signed_at).toLocaleDateString()}</p>
                )}

                {/* Sign button — only for active unsigned agreements */}
                {ag.is_active && !ag.signed_by_doctor && (
                  <div>
                    <p style={{ margin: "0 0 10px", fontSize: 12, color: "#2D5A72", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
                      By clicking "Sign Agreement" you confirm you have read and accept the platform fee terms above.
                    </p>
                    <button
                      onClick={() => signAgreement(ag.id)}
                      disabled={paymentSaving}
                      style={{ padding: "8px 22px", borderRadius: 8, border: "1px solid rgba(37,99,235,0.4)", background: "rgba(37,99,235,0.08)", color: "#2563EB", fontSize: 13, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: paymentSaving ? "not-allowed" : "pointer" }}
                    >
                      {paymentSaving ? "Processing…" : "Sign Agreement"}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {feeAgreements.length === 0 && (
              <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No fee agreement issued yet. The admin team will set this up once your account is approved.</p>
            )}
          </div>
        )}

        {/* No fee agreement yet */}
        {paymentLoaded && feeAgreements.length === 0 && (
          <div style={{ ...card, padding: "20px 24px", background: "rgba(90,144,168,0.03)" }}>
            <p style={sectionHead}>Platform Fee Agreement</p>
            <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No fee agreement issued yet. The admin team will set this up once your profile is fully approved.</p>
          </div>
        )}
      </div>
    </main>
  );

  /* ─── Resource Library View ────────────────────────────────────────────────── */
  const RES_TYPE_OPTS = [
    { value: "pdf",            label: "PDF" },
    { value: "article",        label: "Article" },
    { value: "protocol",       label: "Protocol" },
    { value: "guideline",      label: "Guideline" },
    { value: "research_paper", label: "Research Paper" },
    { value: "patient_handout",label: "Patient Handout" },
    { value: "video",          label: "Video" },
    { value: "infographic",    label: "Infographic" },
    { value: "other",          label: "Other" },
  ];
  const RES_TYPE_COLOR = {
    pdf:             { color: "#DC2626", bg: "rgba(220,38,38,0.08)",   border: "rgba(220,38,38,0.2)"   },
    article:         { color: "#2563EB", bg: "rgba(37,99,235,0.08)",   border: "rgba(37,99,235,0.2)"   },
    protocol:        { color: "#7C3AED", bg: "rgba(124,58,237,0.08)",  border: "rgba(124,58,237,0.2)"  },
    guideline:       { color: "#0891B2", bg: "rgba(8,145,178,0.08)",   border: "rgba(8,145,178,0.2)"   },
    research_paper:  { color: "#1D4ED8", bg: "rgba(29,78,216,0.08)",   border: "rgba(29,78,216,0.2)"   },
    patient_handout: { color: "#16A34A", bg: "rgba(22,163,74,0.08)",   border: "rgba(22,163,74,0.2)"   },
    video:           { color: "#EA580C", bg: "rgba(234,88,12,0.08)",   border: "rgba(234,88,12,0.2)"   },
    infographic:     { color: "#B45309", bg: "rgba(180,83,9,0.08)",    border: "rgba(180,83,9,0.2)"    },
    other:           { color: "#5A90A8", bg: "rgba(90,144,168,0.08)",  border: "rgba(90,144,168,0.2)"  },
  };
  const RES_STATUS_COLOR = {
    draft:     { color: "#9BBCCC", bg: "rgba(90,144,168,0.06)",  border: "rgba(90,144,168,0.15)"  },
    published: { color: "#16A34A", bg: "rgba(22,163,74,0.1)",    border: "rgba(22,163,74,0.25)"   },
    archived:  { color: "#6B7280", bg: "rgba(107,114,128,0.08)", border: "rgba(107,114,128,0.2)"  },
  };
  const approvedPrograms = programs.filter((p) => ["approved", "live"].includes(p.status));
  const approvedClustersForRes = clusters.filter((c) => c.approval_status === "approved");

  const filteredResources = resources.filter((r) => {
    if (resTypeFilter !== "all" && r.resource_type !== resTypeFilter) return false;
    if (resStatusFilter !== "all" && r.status !== resStatusFilter) return false;
    if (resSearch.trim()) {
      const q = resSearch.toLowerCase();
      if (!r.title.toLowerCase().includes(q) && !(r.description || "").toLowerCase().includes(q) && !(r.tags || []).some((t) => t.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const ResourcesView = (
    <main className="dd-view-main" style={{ maxWidth: 1060, margin: "0 auto", padding: "32px 20px 60px" }}>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Clinical Content</p>
          <h2 style={{ margin: "0 0 6px", fontSize: 28, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>Resource Library</h2>
          <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Upload and tag clinical materials to programs so patients receive the right content as part of their care.</p>
        </div>
        {resScreen === "list"
          ? <button onClick={() => openResForm(null)} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "#5A90A8", color: "#FFF", fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}>+ New Resource</button>
          : <button onClick={() => { setResScreen("list"); setEditRes(null); setResErr(""); setResOk(""); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>← Back to Library</button>
        }
      </div>

      {/* Flash messages */}
      {resErr && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{resErr}</div>}
      {resOk  && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 8, background: "rgba(22,163,74,0.07)",  border: "1px solid rgba(22,163,74,0.25)",  color: "#16A34A", fontSize: 13, fontFamily: "Inter, sans-serif" }}>{resOk}</div>}

      {/* ══ LIST SCREEN ══ */}
      {resScreen === "list" && (
        <>
          {/* Search + filters */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }}>
            <input
              value={resSearch}
              onChange={(e) => setResSearch(e.target.value)}
              placeholder="Search by title, description or tag…"
              style={{ flex: "1 1 220px", padding: "8px 13px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.28)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none" }}
            />
            <select value={resTypeFilter} onChange={(e) => setResTypeFilter(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.28)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#4D7A8E", cursor: "pointer" }}>
              <option value="all">All Types</option>
              {RES_TYPE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select value={resStatusFilter} onChange={(e) => setResStatusFilter(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.28)", background: "#FAFCFD", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#4D7A8E", cursor: "pointer" }}>
              <option value="all">All Statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Summary counts */}
          <div style={{ display: "flex", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
            {[
              { label: "Total",     count: resources.length,                                          color: "#5A90A8" },
              { label: "Published", count: resources.filter((r) => r.status === "published").length,  color: "#16A34A" },
              { label: "Draft",     count: resources.filter((r) => r.status === "draft").length,      color: "#9BBCCC" },
            ].map(({ label, count, color }) => (
              <div key={label} style={{ padding: "8px 18px", borderRadius: 10, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 18, fontWeight: 700, color, fontFamily: "Inter, sans-serif" }}>{count}</span>
                <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{label}</span>
              </div>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <p style={{ fontSize: 32, marginBottom: 12 }}>📚</p>
              <p style={{ fontSize: 15, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif", marginBottom: 6 }}>No resources yet</p>
              <p style={{ fontSize: 13, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{resSearch || resTypeFilter !== "all" || resStatusFilter !== "all" ? "No resources match your filters." : "Upload guides, protocols, handouts or videos and tag them to your programs."}</p>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {filteredResources.map((r) => {
              const tc = RES_TYPE_COLOR[r.resource_type] || RES_TYPE_COLOR.other;
              const sc = RES_STATUS_COLOR[r.status] || RES_STATUS_COLOR.draft;
              const typeLabel = RES_TYPE_OPTS.find((o) => o.value === r.resource_type)?.label || r.resource_type;
              const linkedProgs = programs.filter((p) => (r.program_ids || []).includes(p.id));
              return (
                <div key={r.id} style={{ ...card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                  {/* Color stripe */}
                  <div style={{ height: 4, background: tc.color, flexShrink: 0 }} />
                  <div style={{ padding: "16px 18px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
                    {/* Type + status badges */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif", color: tc.color, background: tc.bg, border: `1px solid ${tc.border}`, textTransform: "uppercase", letterSpacing: "0.07em" }}>{typeLabel}</span>
                      <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif", color: sc.color, background: sc.bg, border: `1px solid ${sc.border}`, textTransform: "capitalize" }}>{r.status}</span>
                      {r.is_featured && <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, fontFamily: "Inter, sans-serif", color: "#B45309", background: "rgba(180,83,9,0.08)", border: "1px solid rgba(180,83,9,0.2)" }}>Featured</span>}
                    </div>
                    {/* Title */}
                    <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", lineHeight: 1.35 }}>{r.title}</p>
                    {/* Description */}
                    {r.description && <p style={{ margin: "0 0 10px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{r.description}</p>}
                    {/* Tags */}
                    {r.tags?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                        {r.tags.slice(0, 4).map((t) => <span key={t} style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontFamily: "Inter, sans-serif", color: "#5A90A8", background: "rgba(90,144,168,0.08)", border: "1px solid rgba(90,144,168,0.2)" }}>#{t}</span>)}
                        {r.tags.length > 4 && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif", padding: "2px 0" }}>+{r.tags.length - 4} more</span>}
                      </div>
                    )}
                    {/* Linked programs */}
                    {linkedProgs.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                        {linkedProgs.map((p) => <span key={p.id} style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontFamily: "Inter, sans-serif", color: "#2D5A72", background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.2)" }}>📋 {p.program_name}</span>)}
                      </div>
                    )}
                    {/* File/link indicator */}
                    <div style={{ marginTop: "auto", paddingTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
                      {r.file_url
                        ? <><span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>📄 {r.file_format?.toUpperCase() || "FILE"}</span>{r.file_size_bytes && <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>· {(r.file_size_bytes / 1024 / 1024).toFixed(1)} MB</span>}</>
                        : r.external_url ? <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>🔗 External Link</span> : null
                      }
                    </div>
                  </div>
                  {/* Card footer actions */}
                  <div style={{ padding: "10px 18px 14px", display: "flex", gap: 8, borderTop: "1px solid rgba(90,144,168,0.1)" }}>
                    <button onClick={() => openResForm(r)} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "rgba(90,144,168,0.06)", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>Edit</button>
                    {r.file_url && (
                      <a href={`${UPLOADS_BASE}${r.file_url}`} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>View</a>
                    )}
                    {r.external_url && (
                      <a href={r.external_url} target="_blank" rel="noreferrer" style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#5A90A8", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>Open</a>
                    )}
                    <button onClick={() => deleteResource(r.id)} style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid rgba(220,38,38,0.2)", background: "transparent", color: "#DC2626", fontSize: 12, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══ FORM SCREEN ══ */}
      {resScreen === "form" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* ── Section 1: Content source ── */}
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Content Source</p>

            {/* Source toggle */}
            <div style={{ display: "flex", gap: 0, marginBottom: 20, borderRadius: 8, overflow: "hidden", border: "1px solid rgba(90,144,168,0.25)", width: "fit-content" }}>
              {[["upload", "📁 Upload File"], ["link", "🔗 External Link"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setResSource(val)} style={{ padding: "8px 20px", border: "none", background: resSource === val ? "#5A90A8" : "transparent", color: resSource === val ? "#FFF" : "#7AAABB", fontSize: 13, fontWeight: resSource === val ? 700 : 500, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>{lbl}</button>
              ))}
            </div>

            {resSource === "upload" && (
              <div>
                {/* Drop zone */}
                <label style={{ display: "block", border: "2px dashed rgba(90,144,168,0.35)", borderRadius: 12, padding: "30px 20px", textAlign: "center", cursor: "pointer", background: "rgba(90,144,168,0.03)", transition: "border-color 150ms" }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) uploadResFile(f); }}
                >
                  <input type="file" style={{ display: "none" }} onChange={(e) => { const f = e.target.files[0]; if (f) uploadResFile(f); }} accept=".pdf,.doc,.docx,.pptx,.jpg,.jpeg,.png,.mp4,.mov,.txt,.csv" />
                  {resUploading
                    ? <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Uploading…</p>
                    : resForm.file_url || editRes?.file_url
                      ? <div>
                          <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: "#16A34A", fontFamily: "Inter, sans-serif" }}>✓ File uploaded</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{(resForm.file_format || editRes?.file_format || "").toUpperCase()} · {resForm.file_size_bytes || editRes?.file_size_bytes ? `${((resForm.file_size_bytes || editRes?.file_size_bytes) / 1024 / 1024).toFixed(1)} MB` : ""} — click or drop to replace</p>
                        </div>
                      : <div>
                          <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>Click or drag & drop a file here</p>
                          <p style={{ margin: 0, fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>PDF, Word, PowerPoint, images, videos up to 50 MB</p>
                        </div>
                  }
                </label>
              </div>
            )}

            {resSource === "link" && (
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.08em" }}>External URL *</label>
                <input value={resForm.external_url} onChange={(e) => setResForm((p) => ({ ...p, external_url: e.target.value }))} placeholder="https://pubmed.ncbi.nlm.nih.gov/..." style={{ ...pi, maxWidth: 520 }} />
                <p style={{ margin: "5px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>PubMed articles, YouTube videos, Notion docs, Google Drive links…</p>
              </div>
            )}
          </div>

          {/* ── Section 2: Metadata ── */}
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Resource Details</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "start" }}>
                <div>
                  <label style={pl}>Title *</label>
                  <input value={resForm.title} onChange={(e) => setResForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Metabolic Reset Protocol — Patient Guide" style={pi} />
                </div>
                <div>
                  <label style={pl}>Type *</label>
                  <select value={resForm.resource_type} onChange={(e) => setResForm((p) => ({ ...p, resource_type: e.target.value }))} style={{ ...pi, width: 180 }}>
                    {RES_TYPE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={pl}>Description</label>
                <textarea value={resForm.description} onChange={(e) => setResForm((p) => ({ ...p, description: e.target.value }))} placeholder="What is this resource about? When should patients read/watch it?" rows={3} style={{ ...pi, resize: "vertical" }} />
              </div>
            </div>
          </div>

          {/* ── Section 3: Tags ── */}
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Tags & Keywords</p>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addResTag(); } }} placeholder="e.g. insulin resistance, metabolic, nutrition…" style={{ ...pi, flex: 1 }} />
              <button onClick={addResTag} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "rgba(90,144,168,0.08)", color: "#5A90A8", fontSize: 13, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", whiteSpace: "nowrap" }}>Add Tag</button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {resForm.tags.map((t) => (
                <span key={t} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 12px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.22)", fontSize: 12, fontFamily: "Inter, sans-serif", color: "#2D5A72" }}>
                  #{t}
                  <button onClick={() => removeResTag(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                </span>
              ))}
              {resForm.tags.length === 0 && <p style={{ margin: 0, fontSize: 12, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No tags yet — add keywords to help with search and filtering.</p>}
            </div>
          </div>

          {/* ── Section 4: Program tagging ── */}
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Tag to Programs</p>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Patients enrolled in the selected programs will have access to this resource.</p>
            {approvedPrograms.length === 0
              ? <p style={{ fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>No approved programs yet. Create and get a program approved to tag resources.</p>
              : <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {approvedPrograms.map((p) => {
                    const sel = resForm.program_ids.includes(p.id);
                    return (
                      <button key={p.id} onClick={() => toggleResProgram(p.id)} style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${sel ? "rgba(22,163,74,0.45)" : "rgba(90,144,168,0.2)"}`, background: sel ? "rgba(22,163,74,0.1)" : "transparent", color: sel ? "#16A34A" : "#9BBCCC", fontSize: 13, fontWeight: sel ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                        {sel ? "✓ " : ""}{p.program_name}
                      </button>
                    );
                  })}
                </div>
            }

            {/* Cluster tags (optional) */}
            {approvedClustersForRes.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>Relevant Clusters <span style={{ color: "#A8C8D8", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {approvedClustersForRes.map((c) => {
                    const sel = resForm.cluster_ids.includes(c.cluster_id);
                    return (
                      <button key={c.cluster_id} onClick={() => toggleResCluster(c.cluster_id)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${sel ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.2)"}`, background: sel ? "rgba(90,144,168,0.15)" : "transparent", color: sel ? "#2D5A72" : "#9BBCCC", fontSize: 12, fontWeight: sel ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                        {c.cluster?.cluster_name || c.cluster_id}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Section 5: Visibility ── */}
          <div style={{ ...card, padding: "24px 28px" }}>
            <p style={sectionHead}>Visibility & Settings</p>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Status</label>
                <div style={{ display: "flex", gap: 0, borderRadius: 8, overflow: "hidden", border: "1px solid rgba(90,144,168,0.25)", width: "fit-content" }}>
                  {[["draft", "Draft"], ["published", "Published"], ["archived", "Archived"]].map(([val, lbl]) => (
                    <button key={val} onClick={() => setResForm((p) => ({ ...p, status: val }))} style={{ padding: "8px 18px", border: "none", background: resForm.status === val ? "#5A90A8" : "transparent", color: resForm.status === val ? "#FFF" : "#7AAABB", fontSize: 13, fontWeight: resForm.status === val ? 700 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>{lbl}</button>
                  ))}
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Only Published resources are visible to enrolled patients.</p>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", userSelect: "none" }}>
                <input type="checkbox" checked={resForm.is_featured} onChange={(e) => setResForm((p) => ({ ...p, is_featured: e.target.checked }))} style={{ width: 16, height: 16, accentColor: "#5A90A8" }} />
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#2D5A72", fontFamily: "Inter, sans-serif" }}>Feature this resource</span>
                  <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Pinned at the top of the patient's resource list.</p>
                </div>
              </label>
            </div>
          </div>

          {/* ── Save footer ── */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", paddingBottom: 20 }}>
            <button onClick={saveResource} disabled={resSaving || resUploading} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: resSaving ? "#A8C8D8" : "#5A90A8", color: "#FFF", fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: resSaving || resUploading ? "not-allowed" : "pointer" }}>
              {resSaving ? "Saving…" : editRes ? "Update Resource" : "Save Resource"}
            </button>
            {editRes && (
              <button onClick={() => { if (window.confirm("Delete this resource?")) deleteResource(editRes.id); }} style={{ padding: "10px 20px", borderRadius: 10, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </main>
  );

  /* ─── Notification Preferences View ──────────────────────────────────────── */
  const np = notifPrefs;

  const ToggleRow = ({ label, desc, field }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
      <div style={{ flex: 1, paddingRight: 24 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{label}</p>
        {desc && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{desc}</p>}
      </div>
      <button
        onClick={() => toggleNotif(field)}
        style={{ flexShrink: 0, width: 44, height: 24, borderRadius: 999, border: "none", background: np[field] ? "#5A90A8" : "rgba(90,144,168,0.2)", cursor: "pointer", position: "relative", transition: "background 200ms ease" }}
      >
        <span style={{ position: "absolute", top: 2, left: np[field] ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#FFF", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 200ms ease", display: "block" }} />
      </button>
    </div>
  );

  const NotificationsView = (
    <main className="dd-view-main" style={{ maxWidth: 760, margin: "0 auto", padding: "32px 24px 60px" }}>
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Notifications &amp; Alerts</h2>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Your full notification inbox and preference controls — never miss what matters.</p>
      </div>

      {notifErr && <div style={{ padding: "12px 18px", borderRadius: 10, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", marginBottom: 20 }}>{notifErr}</div>}
      {notifOk  && <div style={{ padding: "12px 18px", borderRadius: 10, background: "rgba(22,163,74,0.08)",  border: "1px solid rgba(22,163,74,0.25)",  color: "#16A34A", fontSize: 13, fontFamily: "Inter, sans-serif", marginBottom: 20 }}>{notifOk}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

        {/* ── Inbox ── */}
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <p style={{ ...sectionHead, marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
              🔔 Inbox {unreadCount > 0 && <span style={{ color: "#EF4444", fontWeight: 800 }}>({unreadCount} unread)</span>}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              {unreadCount > 0 && (
                <button onClick={markAllRead} style={{ fontSize: 12, color: "#5A90A8", background: "none", border: "1px solid rgba(90,144,168,0.3)", borderRadius: 6, cursor: "pointer", fontFamily: "Inter, sans-serif", padding: "4px 10px", fontWeight: 600 }}>
                  Mark all read
                </button>
              )}
              <button onClick={loadInbox} style={{ fontSize: 12, color: "#9BBCCC", background: "none", border: "1px solid rgba(90,144,168,0.15)", borderRadius: 6, cursor: "pointer", fontFamily: "Inter, sans-serif", padding: "4px 10px" }}>
                Refresh
              </button>
            </div>
          </div>
          <div style={{ borderBottom: "1px solid rgba(90,144,168,0.15)", marginBottom: 14 }} />
          {inboxLoading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <div style={{ width: 24, height: 24, border: "2px solid rgba(90,144,168,0.2)", borderTopColor: "#8AAEBB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            </div>
          )}
          {!inboxLoading && inbox.length === 0 && (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#9BBCCC", fontFamily: "Inter, sans-serif", fontSize: 13 }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>🎉</div>
              You're all caught up — no notifications yet.
            </div>
          )}
          {!inboxLoading && inbox.slice(0, 15).map((n) => (
            <div
              key={n.id}
              style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid rgba(90,144,168,0.08)", background: "transparent", cursor: n.is_read ? "default" : "pointer" }}
              onClick={() => { if (!n.is_read) markRead(n.id); }}
            >
              <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{NOTIF_ICONS[n.event_type] || "🔔"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{n.title}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {!n.is_read && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#5A90A8", display: "inline-block" }} />}
                    <span style={{ fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{fmtNotifTime(n.created_at)}</span>
                    <button onClick={(e) => { e.stopPropagation(); dismissNotif(n.id); }} style={{ fontSize: 11, color: "#C0D4DC", background: "none", border: "none", cursor: "pointer", fontFamily: "Inter, sans-serif", padding: 0 }}>✕</button>
                  </div>
                </div>
                {n.body && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>{n.body}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* ── Delivery Channels ── */}
        <div style={card}>
          <p style={sectionHead}>📡 Delivery Channels</p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Choose how you receive alerts. You can enable multiple channels.</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {[
              { key: "channel_in_app", icon: "🖥️", label: "In-App",  desc: "Bell icon in dashboard" },
              { key: "channel_email",  icon: "✉️", label: "Email",   desc: "Sent to your account email" },
              { key: "channel_sms",   icon: "📱", label: "SMS",     desc: "Text to registered number" },
            ].map(({ key, icon, label, desc }) => (
              <button
                key={key}
                onClick={() => toggleNotif(key)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 12, border: `2px solid ${np[key] ? "#5A90A8" : "rgba(90,144,168,0.2)"}`, background: np[key] ? "rgba(90,144,168,0.1)" : "#FFF", cursor: "pointer", transition: "all 150ms ease", minWidth: 140 }}
              >
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div style={{ textAlign: "left" }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: np[key] ? "#2D5A72" : "#7AAABB", fontFamily: "Inter, sans-serif" }}>{label}</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{desc}</p>
                </div>
                {np[key] && <span style={{ marginLeft: "auto", fontSize: 14, color: "#5A90A8" }}>✓</span>}
              </button>
            ))}
          </div>
        </div>

        {/* ── Patient Activity ── */}
        <div style={card}>
          <p style={sectionHead}>👤 Patient Activity</p>
          <ToggleRow field="notify_new_patient_enrolled"       label="New patient enrolled"         desc="When a patient joins one of your programs." />
          <ToggleRow field="notify_patient_assessment_done"    label="Assessment submitted"          desc="A patient completes a health or progress assessment." />
          <ToggleRow field="notify_patient_missed_session"     label="Missed session"                desc="A scheduled session was not attended." />
          <ToggleRow field="notify_patient_message"            label="Patient message received"      desc="A patient sends you a message or query." />
          <ToggleRow field="notify_patient_care_plan_deviation" label="Care plan deviation"          desc="A patient's behaviour deviates from their care plan." />
          <ToggleRow field="notify_patient_goal_achieved"      label="Goal achieved"                 desc="A patient hits a milestone or health goal." />
        </div>

        {/* ── Program Activity ── */}
        <div style={card}>
          <p style={sectionHead}>📋 Program Activity</p>
          <ToggleRow field="notify_program_status_changed"  label="Program status changed"     desc="Admin approves, rejects, or suspends one of your programs." />
          <ToggleRow field="notify_program_new_enrollment"  label="New program enrollment"     desc="A patient enrolls in a program you offer." />
          <ToggleRow field="notify_program_paused"          label="Program paused"             desc="One of your live programs is automatically paused at capacity." />
        </div>

        {/* ── Clinical & Admin ── */}
        <div style={card}>
          <p style={sectionHead}>🔬 Clinical &amp; Admin</p>
          <ToggleRow field="notify_lab_results_ready"     label="Lab results ready"          desc="A patient's lab results are available for review." />
          <ToggleRow field="notify_resource_engagement"   label="Resource engagement"        desc="A patient views or downloads a resource you published." />
          <ToggleRow field="notify_follow_up_due"         label="Follow-up due"              desc="A scheduled follow-up or review date is approaching." />
          <ToggleRow field="notify_document_expiring"     label="Document expiring"          desc="One of your credentials or documents is nearing expiry." />
          <ToggleRow field="notify_payout_processed"      label="Payout processed"           desc="A payment has been disbursed to your bank account." />
        </div>

        {/* ── Quiet Hours ── */}
        <div style={card}>
          <p style={sectionHead}>🌙 Quiet Hours</p>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Suppress non-urgent alerts during these hours. Critical alerts always go through.</p>
          <ToggleRow field="quiet_hours_enabled" label="Enable quiet hours" desc="No non-urgent alerts during the window below." />
          {np.quiet_hours_enabled && (
            <div style={{ display: "flex", gap: 20, marginTop: 16, flexWrap: "wrap" }}>
              {[["quiet_start", "Start time"], ["quiet_end", "End time"]].map(([field, lbl]) => (
                <div key={field}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#7AAABB", fontFamily: "Inter, sans-serif", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>{lbl}</label>
                  <input
                    type="time"
                    value={np[field] || ""}
                    onChange={(e) => setNotifPrefs((p) => ({ ...p, [field]: e.target.value }))}
                    style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", fontSize: 14, fontFamily: "Inter, sans-serif", color: "#1C3545", background: "#FFF", outline: "none" }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Digest Summary ── */}
        <div style={card}>
          <p style={sectionHead}>📊 Summary Digest</p>
          <p style={{ margin: "0 0 4px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Receive a bundled summary instead of individual alerts.</p>
          <ToggleRow field="daily_digest_enabled"  label="Daily digest"  desc="One summary email each morning with the day's activity." />
          <ToggleRow field="weekly_digest_enabled" label="Weekly digest" desc="A weekly summary every Monday morning." />
        </div>

        {/* ── Save ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            onClick={saveNotifPrefs}
            disabled={notifSaving}
            style={{ padding: "11px 32px", borderRadius: 10, border: "none", background: notifSaving ? "#A8C8D8" : "#5A90A8", color: "#FFF", fontSize: 14, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: notifSaving ? "not-allowed" : "pointer", transition: "background 150ms ease" }}
          >
            {notifSaving ? "Saving…" : "Save Preferences"}
          </button>
          <p style={{ margin: 0, fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>Changes take effect immediately across all your active sessions.</p>
        </div>

      </div>
    </main>
  );

  /* ─── Hub View ────────────────────────────────────────────────────────────── */
  const HUB_TABS = [
    { key: "patients",       label: "Patient List",         icon: "👥" },
    { key: "patient_detail", label: "Patient Detail",       icon: "🧑‍⚕️", hidden: true },
    { key: "programs",       label: "Programs & Packages",  icon: "📋" },
    { key: "intelligence",   label: "Clinical Intelligence",icon: "📊" },
    { key: "credentials",    label: "Profile & Credentials",icon: "🪪" },
    { key: "earnings",       label: "Earnings & Payouts",   icon: "💰" },
  ];

  const tabBtnStyle = (k) => ({
    display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
    borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13,
    fontFamily: "Inter, sans-serif", fontWeight: 600, transition: "all 150ms ease",
    background: hubTab === k ? "#1C3545" : "transparent",
    color: hubTab === k ? "#FFF" : "#5A90A8",
  });

  // ── Hub: Patient List tab ──
  const filteredPatients = hubPatients.filter((p) =>
    !hubPatientSearch.trim() ||
    p.display_name.toLowerCase().includes(hubPatientSearch.toLowerCase()) ||
    (p.program_name || "").toLowerCase().includes(hubPatientSearch.toLowerCase())
  );

  const ENROLL_STATUS_COLORS = {
    enrolled:   { color: "#16A34A", bg: "rgba(22,163,74,0.1)" },
    active:     { color: "#16A34A", bg: "rgba(22,163,74,0.1)" },
    completed:  { color: "#5A90A8", bg: "rgba(90,144,168,0.1)" },
    paused:     { color: "#B45309", bg: "rgba(180,83,9,0.1)" },
    withdrawn:  { color: "#DC2626", bg: "rgba(220,38,38,0.1)" },
    connected:  { color: "#2563EB", bg: "rgba(37,99,235,0.1)" },
  };

  const PatientListTab = (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
        <input
          type="text" placeholder="Search patients or programs…"
          value={hubPatientSearch}
          onChange={(e) => setHubPatientSearch(e.target.value)}
          style={{ flex: 1, padding: "9px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", fontSize: 13, fontFamily: "Inter, sans-serif", color: "#1C3545", outline: "none" }}
        />
        <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
          {filteredPatients.length} of {hubPatients.length}
        </span>
      </div>
      {!hubPatientsLoaded
        ? <Spinner label="Loading patients…" />
        : filteredPatients.length === 0
          ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
              <p style={{ margin: 0, fontSize: 14 }}>No patients yet. Once patients enroll in your programs or connect with you on the marketplace, they'll appear here.</p>
            </div>
          : <div style={{ display: "grid", gap: 12 }}>
              {filteredPatients.map((p) => {
                const es = ENROLL_STATUS_COLORS[p.enrollment_status] || { color: "#6B7280", bg: "rgba(107,114,128,0.1)" };
                const hasEnrollment = !!p.enrollment_id;
                return (
                  <div
                    key={p.enrollment_id || p.relationship_id}
                    onClick={() => hasEnrollment && openPatientDetail(p.enrollment_id)}
                    style={{ ...card, padding: "16px 20px", cursor: hasEnrollment ? "pointer" : "default", transition: "box-shadow 150ms ease" }}
                    onMouseEnter={(e) => { if (hasEnrollment) e.currentTarget.style.boxShadow = "0 4px 24px rgba(90,144,168,0.2)"; }}
                    onMouseLeave={(e) => { if (hasEnrollment) e.currentTarget.style.boxShadow = "0 2px 16px rgba(90,144,168,0.1)"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: "1 1 240px" }}>
                        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(90,144,168,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.7)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{p.display_name}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", overflowWrap: "anywhere" }}>
                            {p.program_name || "Direct connection — no program yet"} · {p.city || p.country || "—"}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ padding: "3px 10px", borderRadius: 999, background: es.bg, color: es.color, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>
                          {p.enrollment_status}
                        </span>
                        <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                          {p.last_active_at ? `Last active: ${new Date(p.last_active_at).toLocaleDateString()}` : "Never active"}
                        </span>
                        {hasEnrollment && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C0D5DE" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
      }
    </div>
  );

  // ── Hub: Patient Detail tab ──
  const PatientDetailTab = hubDetailLoading
    ? <Spinner label="Loading patient detail…" />
    : !hubDetail
      ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
          <p>No data.</p>
          <button onClick={() => setHubTab("patients")} style={{ marginTop: 12, padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
            Back to Patient List
          </button>
        </div>
      : (() => {
          const d = hubDetail;
          const p = d.progress;
          const cp = d.care_plan;
          const es = ENROLL_STATUS_COLORS[d.enrollment_status] || { color: "#6B7280", bg: "rgba(107,114,128,0.1)" };
          return (
            <div style={{ display: "grid", gap: 20 }}>
              {/* Back */}
              <button onClick={() => setHubTab("patients")} style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.25)", background: "transparent", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer", width: "fit-content" }}>
                ← Back to Patient List
              </button>
              {/* Identity */}
              <SectionCard title="Patient Identity" icon="🧑‍⚕️">
                <InfoRow label="Name"         value={d.display_name} />
                <InfoRow label="Gender"       value={d.gender || "—"} />
                <InfoRow label="Date of Birth" value={d.date_of_birth ? new Date(d.date_of_birth).toLocaleDateString() : "—"} />
                <InfoRow label="Phone"        value={d.phone || "—"} />
                <InfoRow label="Location"     value={[d.city, d.country].filter(Boolean).join(", ") || "—"} />
                <InfoRow label="Lifecycle"    value={d.lifecycle_status} />
                <InfoRow label="Last Active"  value={d.last_active_at ? new Date(d.last_active_at).toLocaleString() : "Never"} />
              </SectionCard>
              {/* Enrollment */}
              <SectionCard title="Enrollment" icon="📋">
                <InfoRow label="Program"      value={d.program_name} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                  <span style={rowLabel}>Status</span>
                  <span style={{ padding: "3px 10px", borderRadius: 999, background: es.bg, color: es.color, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>{d.enrollment_status}</span>
                </div>
                <InfoRow label="Enrolled"     value={new Date(d.enrolled_at).toLocaleDateString()} />
                <InfoRow label="Start Date"   value={d.program_start_date ? new Date(d.program_start_date).toLocaleDateString() : "—"} />
                <InfoRow label="Expected End" value={d.expected_end_date ? new Date(d.expected_end_date).toLocaleDateString() : "—"} />
              </SectionCard>
              {/* Progress */}
              {p && (
                <SectionCard title="Program Progress" icon="📈">
                  <InfoRow label="Sessions"     value={`${p.sessions_completed} / ${p.sessions_total ?? "?"}`} />
                  <InfoRow label="Adherence"    value={p.adherence_rate_pct != null ? `${p.adherence_rate_pct}%` : "—"} />
                  <InfoRow label="Goals Met"    value={`${p.goals_met_count} / ${p.goals_total_count ?? "?"}`} />
                  <InfoRow label="Goal Achievement" value={p.goal_achievement_pct != null ? `${p.goal_achievement_pct}%` : "—"} />
                  <InfoRow label="Cluster Score" value={p.cluster_score_current != null ? `${p.cluster_score_current} (start: ${p.cluster_score_start ?? "?"})` : "—"} />
                  {p.specialist_notes && <InfoRow label="Notes" value={p.specialist_notes} />}
                </SectionCard>
              )}
              {/* Care Plan */}
              <SectionCard title={cp ? `Care Plan v${cp.version}` : "Care Plan"} icon="📝">
                {cp ? (
                  <>
                    {cp.plan_overview && <p style={{ margin: "0 0 14px", fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>{cp.plan_overview}</p>}
                    <InfoRow label="Acknowledged" value={cp.user_acknowledged ? `Yes — ${new Date(cp.created_at).toLocaleDateString()}` : "Not yet"} />
                    {cp.goals.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <p style={{ ...sectionHead, marginBottom: 10 }}>Goals</p>
                        {cp.goals.map((g) => (
                          <div key={g.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid rgba(90,144,168,0.1)", flexWrap: "wrap" }}>
                            <div style={{ minWidth: 0, flex: "1 1 200px" }}>
                              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{g.goal_title}</p>
                              {g.goal_description && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{g.goal_description}</p>}
                              {(g.target_value || g.due_date) && (
                                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
                                  {[g.target_value ? `Target: ${g.target_value}` : null, g.due_date ? `Due: ${new Date(g.due_date).toLocaleDateString()}` : null].filter(Boolean).join(" · ")}
                                </p>
                              )}
                            </div>
                            <span style={{ padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", background: g.status === "achieved" ? "rgba(22,163,74,0.1)" : g.status === "in_progress" ? "rgba(29,78,216,0.1)" : "rgba(107,114,128,0.1)", color: g.status === "achieved" ? "#16A34A" : g.status === "in_progress" ? "#1D4ED8" : "#6B7280", textTransform: "capitalize", flexShrink: 0 }}>
                              {g.status.replace("_", " ")}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  !cpFormOpen && (
                    <p style={{ margin: 0, fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>
                      No care plan yet for this enrollment.
                    </p>
                  )
                )}

                {cpMsg && (
                  <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, fontSize: 13, fontFamily: "Inter, sans-serif", background: cpMsg.type === "success" ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.08)", border: `1px solid ${cpMsg.type === "success" ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.25)"}`, color: cpMsg.type === "success" ? "#16A34A" : "#DC2626" }}>
                    {cpMsg.text}
                  </div>
                )}

                {!cpFormOpen ? (
                  <button onClick={openCpForm} style={{ ...pb("#FFFFFF", "#5A90A8", "#5A90A8"), marginTop: 14 }}>
                    {cp ? "+ New Care Plan Version" : "+ Create Care Plan"}
                  </button>
                ) : (
                  <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid rgba(90,144,168,0.15)" }}>
                    <p style={{ ...sectionHead, marginBottom: 14 }}>
                      {cp ? `New Care Plan (v${cp.version + 1})` : "New Care Plan"}
                    </p>

                    <label style={pl}>Plan Overview</label>
                    <textarea
                      value={cpForm.plan_overview}
                      onChange={(e) => setCpForm((f) => ({ ...f, plan_overview: e.target.value }))}
                      placeholder="Summarise the plan: focus areas, approach, and what the patient should expect…"
                      rows={4}
                      style={{ ...pi, resize: "vertical", marginBottom: 14 }}
                    />

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
                      {[
                        { key: "tailored_for_personality", label: "Tailored for Personality" },
                        { key: "tailored_for_beliefs",     label: "Tailored for Beliefs" },
                        { key: "tailored_for_readiness",   label: "Tailored for Readiness" },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label style={pl}>{label} <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                          <textarea
                            value={cpForm[key]}
                            onChange={(e) => setCpForm((f) => ({ ...f, [key]: e.target.value }))}
                            rows={2}
                            style={{ ...pi, resize: "vertical" }}
                          />
                        </div>
                      ))}
                    </div>

                    <p style={{ ...sectionHead, marginBottom: 12 }}>Goals</p>
                    {cpForm.goals.length === 0 && (
                      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#A8C8D8", fontFamily: "Inter, sans-serif", fontStyle: "italic" }}>
                        No goals added yet.
                      </p>
                    )}
                    {cpForm.goals.map((g, idx) => (
                      <div key={idx} style={{ padding: "14px 14px 12px", borderRadius: 12, border: "1px solid rgba(90,144,168,0.18)", background: "#F8FBFC", marginBottom: 12 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 10 }}>
                          <div>
                            <label style={pl}>Goal Title *</label>
                            <input value={g.goal_title} onChange={(e) => setCpGoal(idx, "goal_title", e.target.value)} placeholder="e.g. Reduce hs-CRP below 1 mg/L" style={pi} />
                          </div>
                          <div>
                            <label style={pl}>Type</label>
                            <select value={g.goal_type} onChange={(e) => setCpGoal(idx, "goal_type", e.target.value)} style={pi}>
                              <option value="short_term">Short term</option>
                              <option value="long_term">Long term</option>
                            </select>
                          </div>
                          <div>
                            <label style={pl}>Target Value</label>
                            <input value={g.target_value} onChange={(e) => setCpGoal(idx, "target_value", e.target.value)} placeholder='e.g. "< 1 mg/L", "Daily"' style={pi} />
                          </div>
                          <div>
                            <label style={pl}>Due Date</label>
                            <input type="date" value={g.due_date} onChange={(e) => setCpGoal(idx, "due_date", e.target.value)} style={pi} />
                          </div>
                        </div>
                        <label style={pl}>Description</label>
                        <textarea value={g.goal_description} onChange={(e) => setCpGoal(idx, "goal_description", e.target.value)} rows={2} placeholder="How will the patient work toward this goal?" style={{ ...pi, resize: "vertical" }} />
                        <button onClick={() => removeCpGoal(idx)} style={{ marginTop: 8, padding: "5px 12px", borderRadius: 7, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
                          Remove goal
                        </button>
                      </div>
                    ))}
                    <button onClick={addCpGoal} style={{ ...pb(), marginBottom: 18 }}>+ Add Goal</button>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <button onClick={submitCarePlan} disabled={cpSaving} style={{ ...pb("#FFFFFF", cpSaving ? "rgba(90,144,168,0.5)" : "#5A90A8", "#5A90A8"), cursor: cpSaving ? "default" : "pointer" }}>
                        {cpSaving ? "Saving…" : "Save Care Plan"}
                      </button>
                      <button onClick={() => { setCpFormOpen(false); setCpMsg(null); }} disabled={cpSaving} style={pb()}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </SectionCard>
            </div>
          );
        })();

  // ── Hub: Programs tab (reuses programs state) ──
  const HubProgramsTab = (
    <div>
      {!programsLoaded
        ? <Spinner label="Loading programs…" />
        : programs.length === 0
          ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <p style={{ margin: 0, fontSize: 14 }}>No programs yet.</p>
            </div>
          : <div style={{ display: "grid", gap: 12 }}>
              {programs.map((p) => {
                const st = PROG_STATUS[p.status] ?? PROG_STATUS.draft;
                return (
                  <div key={p.id} style={{ ...card, padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{p.program_name}</p>
                        <p style={{ margin: "3px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{p.program_tagline || "No tagline"}</p>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ padding: "3px 10px", borderRadius: 999, background: st.bg, color: st.color, border: `1px solid ${st.border}`, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif" }}>
                          {st.label}
                        </span>
                        <button
                          onClick={() => { openPrograms(); setTimeout(() => openBuilder(p), 100); }}
                          style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
      }
    </div>
  );

  // ── Hub: Clinical Intelligence tab ──
  const an = hubAnalytics;
  const IntelligenceTab = (
    <div>
      {!hubAnalyticsLoaded
        ? <Spinner label="Loading analytics…" />
        : !an
          ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
              <p>No analytics data available yet.</p>
            </div>
          : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
              {[
                { label: "Total Patients",     value: an.total_patients,               icon: "👥", sub: `${an.active_patients} active` },
                { label: "Total Enrollments",  value: an.total_enrollments,            icon: "📋", sub: `${an.active_enrollments} active` },
                { label: "Avg Adherence",      value: an.avg_adherence_pct != null ? `${an.avg_adherence_pct}%` : "—", icon: "✅", sub: "across all patients" },
                { label: "Goal Achievement",   value: an.avg_goal_achievement_pct != null ? `${an.avg_goal_achievement_pct}%` : "—", icon: "🏆", sub: "average" },
                { label: "Total Programs",     value: an.total_programs,               icon: "📦", sub: `${an.live_programs} live` },
                { label: "Pending Review",     value: an.pending_review_programs,      icon: "⏳", sub: "programs awaiting admin" },
              ].map((m) => (
                <div key={m.label} style={{ ...card, padding: "20px 22px" }}>
                  <p style={{ margin: "0 0 6px", fontSize: 24 }}>{m.icon}</p>
                  <p style={{ margin: "0 0 2px", fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{m.value}</p>
                  <p style={{ margin: "0 0 2px", fontSize: 12, fontWeight: 600, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{m.label}</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{m.sub}</p>
                </div>
              ))}
            </div>
      }
    </div>
  );

  // ── Hub: Profile & Credentials tab (links to existing profile view) ──
  const CredentialsTab = (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionCard title="Account Identity" icon="🪪">
        <InfoRow label="Display Name"  value={doctor?.display_name} />
        <InfoRow label="City"          value={doctor?.city || "—"} />
        <InfoRow label="Country"       value={doctor?.country || "—"} />
        <InfoRow label="Status"        value={acctStatus.label} />
        <InfoRow label="Onboarding Step" value={doctor?.onboarding_step} />
      </SectionCard>
      {profile && (
        <SectionCard title="Professional Profile" icon="🩺">
          <InfoRow label="Specialty"   value={profile.specialty || "—"} />
          <InfoRow label="Sub-specialty" value={profile.sub_specialty || "—"} />
          <InfoRow label="Experience"  value={profile.years_of_experience != null ? `${profile.years_of_experience} years` : "—"} />
          <InfoRow label="Medical Council No." value={profile.medical_council_reg_no || "—"} />
          <InfoRow label="License State" value={profile.license_state || "—"} />
          <InfoRow label="Languages"   tags={doctor?.languages_spoken?.length ? doctor.languages_spoken : null} value={!doctor?.languages_spoken?.length ? "—" : undefined} />
        </SectionCard>
      )}
      {documents.length > 0 && (
        <SectionCard title="Submitted Documents" icon="📄">
          {documents.map((doc) => {
            const vs = VERIFY_STYLE[doc.verification_status] ?? VERIFY_STYLE.pending;
            return (
              <div key={doc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid rgba(90,144,168,0.1)" }}>
                <span style={rowLabel}>{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</span>
                <span style={{ padding: "3px 10px", borderRadius: 999, background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`, fontSize: 11, fontWeight: 700, fontFamily: "Inter, sans-serif", textTransform: "capitalize" }}>
                  {doc.verification_status}
                </span>
              </div>
            );
          })}
        </SectionCard>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => setDashView("profile")} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", fontWeight: 600 }}>
          Edit Full Profile
        </button>
        <button onClick={() => setDashView("payment")} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", fontWeight: 600 }}>
          Payment Setup
        </button>
      </div>
    </div>
  );

  // ── Hub: Earnings & Payouts tab ──
  const EarningsTab = (
    <div>
      {!hubEarningsLoaded
        ? <Spinner label="Loading earnings…" />
        : !hubEarnings
          ? <div style={{ textAlign: "center", padding: "48px 20px", color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>
              <p>No earnings data available.</p>
            </div>
          : (() => {
              const e = hubEarnings;
              const feeLabels = { flat_monthly: "Flat Monthly", per_patient_percentage: "Per-Patient %", hybrid: "Hybrid" };
              return (
                <div style={{ display: "grid", gap: 20 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
                    {[
                      { label: "Fee Model",        value: e.fee_model ? (feeLabels[e.fee_model] || e.fee_model) : "Not set",                  icon: "📄" },
                      { label: "Fee Value",         value: e.fee_value != null ? `₹ ${e.fee_value.toLocaleString()}` : "—",                    icon: "💵" },
                      { label: "Active Patients",  value: e.active_enrollments,                                                                  icon: "👥" },
                      { label: "Est. Monthly",      value: e.estimated_monthly != null ? `₹ ${e.estimated_monthly.toLocaleString()}` : "—",    icon: "📈" },
                    ].map((m) => (
                      <div key={m.label} style={{ ...card, padding: "18px 20px" }}>
                        <p style={{ margin: "0 0 6px", fontSize: 22 }}>{m.icon}</p>
                        <p style={{ margin: "0 0 2px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{m.value}</p>
                        <p style={{ margin: 0, fontSize: 12, color: "#5A90A8", fontFamily: "Inter, sans-serif" }}>{m.label}</p>
                      </div>
                    ))}
                  </div>
                  {e.fee_effective_from && (
                    <SectionCard title="Fee Agreement" icon="📜">
                      <InfoRow label="Effective From" value={new Date(e.fee_effective_from).toLocaleDateString()} />
                      <InfoRow label="Model"          value={feeLabels[e.fee_model] || e.fee_model} />
                      {e.fee_value != null && <InfoRow label="Value" value={`₹ ${e.fee_value.toLocaleString()}`} />}
                    </SectionCard>
                  )}
                  <div style={{ ...card, padding: "16px 20px", background: "rgba(90,144,168,0.05)" }}>
                    <p style={{ margin: 0, fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>ℹ️ {e.platform_note}</p>
                  </div>
                  <button onClick={() => setDashView("payment")} style={{ alignSelf: "flex-start", padding: "9px 18px", borderRadius: 8, border: "1px solid rgba(90,144,168,0.3)", background: "transparent", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", fontWeight: 600 }}>
                    Manage Payment Details
                  </button>
                </div>
              );
            })()
      }

      {/* Payout history — always shown once loaded */}
      {hubEarningsLoaded && (
        <div style={{ marginTop: 28 }}>
          <p style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>Payout History</p>
          {myPayouts.length === 0
            ? <div style={{ ...card, padding: "28px 20px", textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 13, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>No payouts processed yet. Your finance team will record payouts here once disbursed.</p>
              </div>
            : <div style={{ ...card, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(90,144,168,0.06)" }}>
                      {["Period", "Gross (₹)", "TDS", "GST", "Net (₹)", "Status", "Reference", "Date"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#5A90A8", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {myPayouts.map((p, i) => {
                      const statusColor = { pending: "#B45309", processed: "#16A34A", failed: "#DC2626" }[p.status] || "#9BBCCC";
                      const statusBg   = { pending: "rgba(180,83,9,0.08)", processed: "rgba(22,163,74,0.08)", failed: "rgba(220,38,38,0.08)" }[p.status] || "transparent";
                      return (
                        <tr key={p.id} style={{ borderTop: i > 0 ? "1px solid rgba(90,144,168,0.1)" : "none" }}>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>
                            {new Date(p.period_start).toLocaleDateString()} – {new Date(p.period_end).toLocaleDateString()}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>₹{Number(p.gross_amount).toLocaleString()}</td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                            {p.tds_amount != null ? `₹${Number(p.tds_amount).toLocaleString()}` : p.tds_rate != null ? `${p.tds_rate}%` : "—"}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                            {p.gst_amount != null ? `₹${Number(p.gst_amount).toLocaleString()}` : "—"}
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 13, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>₹{Number(p.net_amount).toLocaleString()}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: statusColor, background: statusBg, padding: "3px 8px", borderRadius: 20, textTransform: "capitalize", fontFamily: "Inter, sans-serif" }}>{p.status}</span>
                          </td>
                          <td style={{ padding: "11px 14px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{p.reference_no || "—"}</td>
                          <td style={{ padding: "11px 14px", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{new Date(p.created_at).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </div>
      )}
    </div>
  );

  const HubTabContent = hubTab === "patients"       ? PatientListTab
    : hubTab === "patient_detail" ? PatientDetailTab
    : hubTab === "programs"       ? HubProgramsTab
    : hubTab === "intelligence"   ? IntelligenceTab
    : hubTab === "credentials"    ? CredentialsTab
    : EarningsTab;

  const HubView = (
    <main className="dd-view-main" style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px", fontFamily: "Inter, sans-serif" }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>My Practice</h2>
        <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>Single view of your practice, patients, performance and profile</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 24, background: "rgba(90,144,168,0.08)", padding: 6, borderRadius: 10, flexWrap: "wrap" }}>
        {HUB_TABS.filter((t) => !t.hidden || hubTab === t.key).map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setHubTab(t.key);
              if (t.key === "patients" && !hubPatientsLoaded) loadHubPatients();
              if (t.key === "intelligence" && !hubAnalyticsLoaded) loadHubAnalytics();
              if (t.key === "earnings" && !hubEarningsLoaded) loadHubEarnings();
              if (t.key === "programs") loadPrograms();
            }}
            style={tabBtnStyle(t.key)}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Active tab content */}
      {HubTabContent}
    </main>
  );

  /* ── Helper: nav item for mobile drawer ── */
  const navGo = (view, action) => {
    if (action) action();
    else setDashView(view);
    setMobileMenuOpen(false);
  };

  const DRAWER_NAV = [
    { icon: "🏠", label: "Home",        view: "home",          action: null },
    { icon: "🧬", label: "Clusters",    view: "clusters",      action: null },
    { icon: "🏥", label: "Practice",    view: "practice",      action: openPractice },
    { icon: "📋", label: "Programs",    view: "programs",      action: openPrograms },
    { icon: "💳", label: "Payment",     view: "payment",       action: openPayment },
    { icon: "📚", label: "Resources",   view: "resources",     action: openResources },
    { icon: "🏠", label: "My Practice", view: "hub",           action: () => openHub("patients") },
    { icon: "🔔", label: "Alerts",      view: "notifications", action: openNotifications },
    { icon: "👤", label: "Profile",     view: "profile",       action: null },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#EDF4F7", position: "relative" }}>
      <style>{`
        /* Hamburger: hidden on desktop */
        .dd-hamburger { display: none; }

        /* Drawer: always in DOM via inline styles; hide shell on desktop */
        @media (min-width: 769px) {
          .dd-mobile-drawer { display: none; }
        }

        /* Mobile overrides */
        @media (max-width: 768px) {
          .dd-hamburger  { display: flex; }
          .dd-desktop-nav { display: none; }
          .dd-profile-name { display: none; }
          .dd-logout-btn { display: none; }
          [data-testid="dd-header"] { padding: 0 12px; }
          .dd-view-main { padding: 20px 14px 60px !important; }
        }
        @media (max-width: 480px) {
          .dd-view-main { padding: 16px 12px 60px !important; }
        }

        /* Tablet compact nav */
        @media (min-width: 769px) and (max-width: 1024px) {
          .dd-desktop-nav button { padding: 5px 10px; font-size: 12px; }
          .dd-profile-name { display: none; }
        }
      `}</style>

      {/* ── Backdrop: only covers content to the RIGHT of the 280px drawer ── */}
      {mobileMenuOpen && (
        <div
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: "fixed",
            top: 0, left: 280, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 398,
            cursor: "pointer",
          }}
        />
      )}

      {/* ── Mobile slide-out drawer ── */}
      <nav
        className="dd-mobile-drawer"
        style={{
          position: "fixed",
          top: 0, left: 0, bottom: 0,
          width: 280,
          background: "#DAEAF2",
          zIndex: 400,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          boxShadow: "4px 0 32px rgba(0,0,0,0.2)",
          transform: mobileMenuOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 280ms cubic-bezier(.4,0,.2,1)",
          pointerEvents: mobileMenuOpen ? "auto" : "none",
        }}
      >
        {/* Drawer header */}
        <div style={{ background: "#8AAEBB", padding: "0 16px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#FFFFFF", fontFamily: "Inter, sans-serif", letterSpacing: "-0.03em" }}>Doctor Portal</span>
          <button onClick={() => setMobileMenuOpen(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.85)", fontSize: 22, cursor: "pointer", padding: "4px 6px", lineHeight: 1 }}>✕</button>
        </div>

        {/* Doctor identity */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid rgba(90,144,168,0.2)", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", border: "2px solid rgba(90,144,168,0.4)", overflow: "hidden", flexShrink: 0, background: "rgba(90,144,168,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {doctor?.profile_photo_url
              ? <img src={`${UPLOADS_BASE}${doctor.profile_photo_url}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.6)" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
            }
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doctor?.display_name || "Doctor"}</p>
            {profile?.primary_specialty && <p style={{ margin: "2px 0 0", fontSize: 11, color: "#5A90A8", fontFamily: "Inter, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.primary_specialty}</p>}
          </div>
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, padding: "8px 0" }}>
          {DRAWER_NAV.map((item) => {
            const active = dashView === item.view;
            return (
              <button
                key={item.label}
                onClick={() => navGo(item.view, item.action)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: "13px 20px", background: active ? "rgba(90,144,168,0.18)" : "transparent", border: "none", textAlign: "left", cursor: "pointer", fontSize: 15, color: active ? "#1C3545" : "#4D7A8E", fontWeight: active ? 700 : 400, fontFamily: "Inter, sans-serif", borderLeft: active ? "3px solid #5A90A8" : "3px solid transparent", transition: "all 150ms" }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(90,144,168,0.1)"; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Logout at bottom */}
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(90,144,168,0.2)", flexShrink: 0 }}>
          <button
            onClick={handleLogout}
            style={{ width: "100%", padding: "11px 16px", borderRadius: 10, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Log Out
          </button>
        </div>
      </nav>

      {Header}
      {dashView === "home" ? HomeView : dashView === "clusters" ? ClustersView : dashView === "practice" ? PracticeView : dashView === "programs" ? ProgramsView : dashView === "payment" ? PaymentView : dashView === "resources" ? ResourcesView : dashView === "notifications" ? NotificationsView : dashView === "hub" ? HubView : ProfileView}
    </div>
  );
}
